// Learning content based on Microsoft Learn and PCEP syllabus
export const learningContent = {
  1: {
    sections: [
      {
        title: "Introduction to Python Programming",
        duration: 3,
        content: [
          {
            type: "paragraph",
            text: "Welcome to your Python journey! Python is one of the most popular programming languages in the world, used by millions of developers for web development, data science, automation, and more."
          },
          {
            type: "heading",
            text: "What is Python?"
          },
          {
            type: "paragraph",
            text: "Python is an interpreted, high-level programming language created by Guido van Rossum in 1991. It emphasizes code readability and simplicity, making it perfect for beginners."
          },
          {
            type: "list",
            items: [
              "Easy to learn and read - Python's syntax is clean and English-like",
              "Versatile - Used in web development, data science, AI, automation, and more",
              "Large community - Millions of developers and extensive libraries",
              "Cross-platform - Works on Windows, Mac, and Linux"
            ]
          },
          {
            type: "tip",
            text: "Python's name comes from 'Monty Python's Flying Circus,' not the snake!"
          }
        ]
      },
      {
        title: "Your First Program: Hello World",
        duration: 5,
        content: [
          {
            type: "paragraph",
            text: "Every programmer's journey begins with 'Hello, World!' - a simple program that displays text. In Python, this is incredibly easy thanks to the built-in print() function."
          },
          {
            type: "heading",
            text: "The print() Function"
          },
          {
            type: "paragraph",
            text: "The print() function is one of Python's most fundamental tools. It displays output to the console (screen). The syntax is simple: print(value)"
          },
          {
            type: "code",
            title: "Example: Basic Print Statement",
            code: "print('Hello, World!')",
            output: "Hello, World!",
            explanation: "This displays the text 'Hello, World!' on the screen"
          },
          {
            type: "heading",
            text: "Understanding String Literals"
          },
          {
            type: "paragraph",
            text: "In programming, text is called a 'string'. In Python, strings are created by wrapping text in either single quotes ('text') or double quotes (\"text\"). Both work identically!"
          },
          {
            type: "code",
            title: "Example: Single vs Double Quotes",
            code: "print('Hello with single quotes')\nprint(\"Hello with double quotes\")",
            output: "Hello with single quotes\nHello with double quotes",
            explanation: "Both produce the same result - choose whichever you prefer!"
          },
          {
            type: "note",
            text: "Python is case-sensitive! print() works, but Print() or PRINT() will cause an error."
          },
          {
            type: "keyPoints",
            points: [
              "print() displays output to the console",
              "Strings are text wrapped in quotes (' or \")",
              "Python is case-sensitive - always use lowercase print()",
              "Every programming journey starts with Hello World!"
            ]
          }
        ]
      }
    ]
  },

  2: {
    sections: [
      {
        title: "Understanding Variables",
        duration: 4,
        content: [
          {
            type: "paragraph",
            text: "Variables are containers that store data values. Think of them as labeled boxes where you can store information for later use. They are fundamental to every programming language."
          },
          {
            type: "heading",
            text: "Creating Variables in Python"
          },
          {
            type: "paragraph",
            text: "In Python, creating a variable is simple - just give it a name and assign a value using the equals sign (=). No special keywords needed!"
          },
          {
            type: "code",
            title: "Example: Creating a Variable",
            code: "name = 'Sifiso'\nprint(name)",
            output: "Sifiso",
            explanation: "We create a variable called 'name', store the text 'Sifiso', and print it"
          },
          {
            type: "heading",
            text: "Variable Naming Rules (PEP 8)"
          },
          {
            type: "paragraph",
            text: "Python has specific rules for naming variables. Following these conventions makes your code readable and professional."
          },
          {
            type: "list",
            items: [
              "Must start with a letter or underscore (_)",
              "Can contain letters, numbers, and underscores",
              "Cannot contain spaces or special characters",
              "Case-sensitive: name, Name, and NAME are different variables",
              "Use lowercase with underscores: my_variable (snake_case)"
            ]
          },
          {
            type: "code",
            title: "Example: Valid and Invalid Names",
            code: "# Valid names\nuser_name = 'Alice'\nage23 = 25\n_private = 'secret'\n\n# Invalid names (would cause errors)\n# 2fast = 'wrong'  # Can't start with number\n# user-name = 'wrong'  # No hyphens allowed\n# my name = 'wrong'  # No spaces allowed",
            explanation: "Always follow naming rules to avoid errors"
          },
          {
            type: "warning",
            text: "Avoid using Python keywords like print, if, for, class as variable names - they have special meanings!"
          },
          {
            type: "keyPoints",
            points: [
              "Variables store data for reuse",
              "Created with: variable_name = value",
              "Follow PEP 8 naming conventions",
              "Python dynamically determines variable types"
            ]
          }
        ]
      }
    ]
  },

  3: {
    sections: [
      {
        title: "Data Types in Python",
        duration: 6,
        content: [
          {
            type: "paragraph",
            text: "Data types define what kind of value a variable can store. Python has several built-in data types, and understanding them is crucial for effective programming."
          },
          {
            type: "heading",
            text: "Common Python Data Types"
          },
          {
            type: "paragraph",
            text: "Python has three fundamental data types you'll use constantly:"
          },
          {
            type: "list",
            items: [
              "int (integer) - Whole numbers: 42, -17, 0",
              "str (string) - Text: 'hello', \"world\"",
              "bool (boolean) - True or False values"
            ]
          },
          {
            type: "heading",
            text: "The type() Function"
          },
          {
            type: "paragraph",
            text: "Python provides the type() function to check what type a value is. This is incredibly useful for debugging and understanding your code."
          },
          {
            type: "code",
            title: "Example: Checking Types",
            code: "print(type(42))\nprint(type('text'))\nprint(type(True))",
            output: "<class 'int'>\n<class 'str'>\n<class 'bool'>",
            explanation: "Each value has a specific type that Python tracks automatically"
          },
          {
            type: "heading",
            text: "Dynamic Typing"
          },
          {
            type: "paragraph",
            text: "Python uses dynamic typing - you don't need to declare types explicitly. Python figures it out automatically based on the value you assign."
          },
          {
            type: "code",
            title: "Example: Dynamic Typing",
            code: "x = 42          # x is an integer\nprint(type(x))\nx = 'hello'     # Now x is a string\nprint(type(x))",
            output: "<class 'int'>\n<class 'str'>",
            explanation: "Variables can change types - Python handles it automatically!"
          },
          {
            type: "note",
            text: "This flexibility is powerful but requires care - make sure variables contain what you expect!"
          },
          {
            type: "keyPoints",
            points: [
              "int: whole numbers (42, -17)",
              "str: text in quotes ('hello')",
              "bool: True or False",
              "Use type() to check data types",
              "Python uses dynamic typing - types determined at runtime"
            ]
          }
        ]
      }
    ]
  },

  4: {
    sections: [
      {
        title: "Arithmetic Operations in Python",
        duration: 5,
        content: [
          {
            type: "paragraph",
            text: "Python supports all standard mathematical operations. Understanding operators and their precedence (order of operations) is essential for writing correct calculations."
          },
          {
            type: "heading",
            text: "Basic Arithmetic Operators"
          },
          {
            type: "list",
            items: [
              "+ (addition): 5 + 3 = 8",
              "- (subtraction): 5 - 3 = 2",
              "* (multiplication): 5 * 3 = 15",
              "/ (division): 10 / 3 = 3.333...",
              "// (floor division): 10 // 3 = 3",
              "% (modulo/remainder): 10 % 3 = 1",
              "** (exponentiation): 2 ** 3 = 8"
            ]
          },
          {
            type: "code",
            title: "Example: Basic Operations",
            code: "result = (10 + 5) * 2\nprint(result)",
            output: "30",
            explanation: "Parentheses force addition first: 15 * 2 = 30"
          },
          {
            type: "heading",
            text: "Operator Precedence"
          },
          {
            type: "paragraph",
            text: "Python follows mathematical order of operations (PEMDAS): Parentheses, Exponents, Multiplication/Division, Addition/Subtraction."
          },
          {
            type: "code",
            title: "Example: Order Matters",
            code: "print(10 + 5 * 2)     # Multiplication first\nprint((10 + 5) * 2)   # Parentheses first",
            output: "20\n30",
            explanation: "Without parentheses: 10 + (5*2) = 20. With parentheses: (10+5) * 2 = 30"
          },
          {
            type: "tip",
            text: "When in doubt, use parentheses to make your intentions clear!"
          },
          {
            type: "keyPoints",
            points: [
              "Python supports all standard math operators",
              "/ gives float division, // gives integer division",
              "** is exponentiation (power)",
              "Follow PEMDAS order of operations",
              "Use parentheses to control evaluation order"
            ]
          }
        ]
      }
    ]
  },

  5: {
    sections: [
      {
        title: "Working with Strings",
        duration: 6,
        content: [
          {
            type: "paragraph",
            text: "Strings are sequences of characters used to store text. Python provides powerful tools for manipulating and combining strings."
          },
          {
            type: "heading",
            text: "String Concatenation"
          },
          {
            type: "paragraph",
            text: "Concatenation means joining strings together. Python offers multiple ways to combine strings."
          },
          {
            type: "code",
            title: "Example: Using the + Operator",
            code: "greeting = 'Hello' + ' ' + 'World'\nprint(greeting)",
            output: "Hello World",
            explanation: "The + operator joins strings together"
          },
          {
            type: "heading",
            text: "F-Strings (Formatted String Literals)"
          },
          {
            type: "paragraph",
            text: "F-strings are the modern, Pythonic way to format strings. Prefix a string with 'f' and use {curly braces} to embed expressions."
          },
          {
            type: "code",
            title: "Example: F-String",
            code: "first = 'Hello'\nsecond = 'World'\nresult = f'{first} {second}'\nprint(result)",
            output: "Hello World",
            explanation: "F-strings are cleaner and more readable than concatenation"
          },
          {
            type: "heading",
            text: "String Methods"
          },
          {
            type: "paragraph",
            text: "Strings have built-in methods for common operations:"
          },
          {
            type: "list",
            items: [
              "upper() - Convert to uppercase",
              "lower() - Convert to lowercase",
              "len() - Get string length",
              "replace() - Replace text"
            ]
          },
          {
            type: "tip",
            text: "F-strings are the preferred method in modern Python (3.6+) - they're faster and more readable!"
          },
          {
            type: "keyPoints",
            points: [
              "Strings store text data",
              "Concatenate with + operator",
              "F-strings provide elegant formatting",
              "Strings are immutable (cannot be changed in-place)",
              "Many built-in methods for manipulation"
            ]
          }
        ]
      }
    ]
  },

// Learning Content for Levels 6-40
// Story-led curriculum aligned with stories.js
// Matches the structure and quality of Levels 1-5

  6: {
    sections: [
      {
        title: "Conditional Statements: Making Decisions with if/else",
        duration: 8,
        content: [
          {
            type: "paragraph",
            text: "Every program needs to make decisions. Should I display this message? Should I perform this calculation? Is this password correct? Python's conditional statements let your programs choose different paths based on whether something is true or false."
          },
          {
            type: "heading",
            text: "Real-World Decision Making"
          },
          {
            type: "paragraph",
            text: "Think about Bongani at his fruit stall. When he looks at the sky, he makes a decision: if it's clear, he sets up his full display. If it looks like rain, he keeps stock protected. This is exactly how programs work—they look at a condition and choose what to do."
          },
          {
            type: "heading",
            text: "The if Statement"
          },
          {
            type: "paragraph",
            text: "The if statement checks a condition. If the condition is True, Python executes the indented code below it. If False, Python skips that code entirely."
          },
          {
            type: "code",
            title: "Example: Simple if Statement",
            code: "weather = 'clear'\n\nif weather == 'clear':\n    print('Setting up full display')",
            output: "Setting up full display",
            explanation: "Because weather equals 'clear', the condition is True, so the message prints"
          },
          {
            type: "note",
            text: "Notice the colon (:) at the end of the if line, and the indentation (4 spaces) for the code inside. Both are required in Python!"
          },
          {
            type: "heading",
            text: "The else Statement"
          },
          {
            type: "paragraph",
            text: "Often you need to do one thing when a condition is True and something different when it's False. That's what else is for."
          },
          {
            type: "code",
            title: "Example: if-else Statement",
            code: "weather = 'rainy'\n\nif weather == 'clear':\n    print('Setting up full display')\nelse:\n    print('Keeping stock protected')",
            output: "Keeping stock protected",
            explanation: "Since weather is 'rainy' (not 'clear'), the else block executes"
          },
          {
            type: "heading",
            text: "Comparison Operators"
          },
          {
            type: "paragraph",
            text: "Conditions use comparison operators to check relationships:"
          },
          {
            type: "list",
            items: [
              "== (equals): checks if two values are the same",
              "!= (not equals): checks if two values are different",
              "> (greater than): checks if left value is bigger",
              "< (less than): checks if left value is smaller",
              ">= (greater than or equal to)",
              "<= (less than or equal to)"
            ]
          },
          {
            type: "code",
            title: "Example: Price Checking",
            code: "price = 25\nbudget = 30\n\nif price <= budget:\n    print('I can afford this')\nelse:\n    print('Too expensive')",
            output: "I can afford this",
            explanation: "Since 25 is less than or equal to 30, the first message prints"
          },
          {
            type: "tip",
            text: "Remember: = assigns a value (price = 25), while == compares values (price == 25). Using = in a condition is a common mistake!"
          },
          {
            type: "heading",
            text: "Practical Application"
          },
          {
            type: "paragraph",
            text: "Conditionals are everywhere in real programs. Checking passwords, validating ages, determining discounts, controlling access—they all use if/else logic."
          },
          {
            type: "code",
            title: "Example: Market Vendor Discount",
            code: "customer_type = 'regular'\n\nif customer_type == 'regular':\n    print('10% discount applied')\nelse:\n    print('Standard price')",
            output: "10% discount applied",
            explanation: "Regular customers get a discount, others pay full price"
          },
          {
            type: "keyPoints",
            points: [
              "if statements make decisions based on conditions",
              "Conditions evaluate to True or False",
              "else provides an alternative when the condition is False",
              "Indentation (4 spaces) defines which code belongs to if/else",
              "Use == to compare, = to assign",
              "Conditionals are fundamental to all programming logic"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What happens when an if condition is False and there's no else?",
          options: [
            "Python throws an error",
            "The indented code runs anyway",
            "Python skips the indented code and continues",
            "The program stops"
          ],
          correct: 2,
          explanation: "If the condition is False and there's no else, Python simply skips that block of code and moves on."
        },
        {
          question: "Which operator checks if two values are equal?",
          options: [
            "=",
            "==",
            "!=",
            "equals"
          ],
          correct: 1,
          explanation: "== checks equality. = is for assignment (giving a value to a variable)."
        },
        {
          question: "What must appear at the end of an if statement line?",
          options: [
            "A semicolon ;",
            "A period .",
            "A colon :",
            "Nothing special"
          ],
          correct: 2,
          explanation: "Python requires a colon : at the end of if, else, and other statement headers."
        },
        {
          question: "How do you structure code that should run when the if condition is False?",
          options: [
            "Use another if with the opposite condition",
            "Use else:",
            "Use not if:",
            "Put it before the if"
          ],
          correct: 1,
          explanation: "The else: statement runs when the if condition is False."
        },
        {
          question: "What's wrong with this code?\nif price = 50:\n    print('expensive')",
          options: [
            "Should use == instead of =",
            "Missing quotes around 50",
            "Indentation is wrong",
            "Nothing wrong"
          ],
          correct: 0,
          explanation: "= is assignment, not comparison. Use == to check if price equals 50."
        }
      ]
    }
  },

  7: {
    sections: [
      {
        title: "Multiple Conditions with elif",
        duration: 7,
        content: [
          {
            type: "paragraph",
            text: "Life rarely presents simple yes/no choices. Often there are multiple distinct options, each requiring different action. Python's elif (else if) lets you check multiple conditions in sequence."
          },
          {
            type: "heading",
            text: "Beyond Yes or No"
          },
          {
            type: "paragraph",
            text: "Remember Jabu the taxi driver? He doesn't ask 'yes or no'—he asks 'where to?' The answer determines the fare: R8 to the rank, R12 to the market, R20 to the hospital. Three different destinations, three different prices, one clear decision process."
          },
          {
            type: "heading",
            text: "The elif Statement"
          },
          {
            type: "paragraph",
            text: "elif stands for 'else if'. It lets you check additional conditions when the first if is False. Python checks each condition in order until it finds one that's True, then executes that block and skips the rest."
          },
          {
            type: "code",
            title: "Example: Taxi Fare Calculation",
            code: "destination = 'market'\n\nif destination == 'rank':\n    fare = 8\nelif destination == 'market':\n    fare = 12\nelif destination == 'hospital':\n    fare = 20\nelse:\n    fare = 15  # default fare\n\nprint(f'Fare: R{fare}')",
            output: "Fare: R12",
            explanation: "Python checks each condition in order. When it finds destination == 'market', it executes that block and skips the rest"
          },
          {
            type: "heading",
            text: "How elif Flows"
          },
          {
            type: "paragraph",
            text: "Think of if/elif/else as a series of gates. Python tries the first gate (if). If it's locked (False), try the next gate (elif). If that's locked, try the next elif. If all gates are locked, use the final fallback (else)."
          },
          {
            type: "list",
            items: [
              "Only ONE block executes - the first True condition",
              "Conditions are checked from top to bottom",
              "Once a condition is True, remaining conditions are skipped",
              "else is optional - it catches everything not matched above",
              "You can have as many elif statements as needed"
            ]
          },
          {
            type: "code",
            title: "Example: Grade Classification",
            code: "score = 78\n\nif score >= 80:\n    grade = 'Distinction'\nelif score >= 65:\n    grade = 'Pass'\nelif score >= 50:\n    grade = 'Conditional Pass'\nelse:\n    grade = 'Fail'\n\nprint(f'Result: {grade}')",
            output: "Result: Pass",
            explanation: "78 is not >= 80, but it IS >= 65, so grade becomes 'Pass' and the remaining conditions are skipped"
          },
          {
            type: "warning",
            text: "Order matters! If you check score >= 50 before score >= 80, a score of 85 would match the first condition and get 'Conditional Pass' incorrectly. Always order from most specific to least specific."
          },
          {
            type: "heading",
            text: "Real-World Applications"
          },
          {
            type: "paragraph",
            text: "Multiple conditions appear constantly in real programs: age brackets for pricing, shipping costs by weight, account types with different features, time-based pricing, priority levels in support systems."
          },
          {
            type: "code",
            title: "Example: Market Vendor Pricing",
            code: "quantity = 8\n\nif quantity >= 10:\n    price_per_item = 20  # bulk discount\nelif quantity >= 5:\n    price_per_item = 23  # moderate discount\nelse:\n    price_per_item = 25  # regular price\n\ntotal = quantity * price_per_item\nprint(f'{quantity} items at R{price_per_item} each: R{total}')",
            output: "8 items at R23 each: R184",
            explanation: "Buying 8 items qualifies for the moderate discount tier"
          },
          {
            type: "tip",
            text: "You can use multiple elif statements, but if you have many conditions checking the same variable, consider learning about dictionaries later (Level 22) for more elegant solutions."
          },
          {
            type: "keyPoints",
            points: [
              "elif checks additional conditions when if is False",
              "Only the first True condition executes",
              "Order matters - check most specific conditions first",
              "else catches anything not matched above (optional)",
              "Use elif instead of multiple separate if statements when only one action should happen",
              "Perfect for categorizing values into multiple distinct groups"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What happens if both the if and elif conditions are True?",
          options: [
            "Both blocks execute",
            "Only the if block executes",
            "Only the elif block executes",
            "Python throws an error"
          ],
          correct: 1,
          explanation: "Only the FIRST True condition executes. Once a condition is True, Python skips all remaining elif and else blocks."
        },
        {
          question: "How many elif statements can you have?",
          options: [
            "Maximum of 1",
            "Maximum of 5",
            "Maximum of 10",
            "As many as you need"
          ],
          correct: 3,
          explanation: "You can have as many elif statements as your logic requires."
        },
        {
          question: "What's the difference between multiple elif statements and multiple separate if statements?",
          options: [
            "No difference, they work the same",
            "elif statements are faster",
            "With elif, only one block executes; with multiple ifs, all True conditions execute",
            "elif can't have an else"
          ],
          correct: 2,
          explanation: "elif creates a chain where only one block runs. Separate if statements check independently—multiple blocks could execute."
        },
        {
          question: "What's wrong with this code?\nif age >= 65:\n    print('senior')\nelif age >= 18:\n    print('adult')\nelif age >= 80:\n    print('elderly')",
          options: [
            "Too many elif statements",
            "Missing else",
            "The age >= 80 check will never execute",
            "Nothing wrong"
          ],
          correct: 2,
          explanation: "Anyone 80 or older will match the first condition (age >= 65) and print 'senior', so the age >= 80 check never runs. Order matters!"
        },
        {
          question: "When should you use elif instead of a new if?",
          options: [
            "Always use elif, never use separate ifs",
            "When conditions are mutually exclusive and only one should execute",
            "When you want all True conditions to execute",
            "Only when you have exactly 2 conditions"
          ],
          correct: 1,
          explanation: "Use elif when you want exactly one outcome from multiple options. Use separate ifs when multiple conditions might need to execute."
        }
      ]
    }
  },

  8: {
    sections: [
      {
        title: "Boolean Logic: Combining Conditions",
        duration: 9,
        content: [
          {
            type: "paragraph",
            text: "Real decisions often depend on multiple factors. You need both your ID and a good payment history to get credit. You can pay with either cash or mobile money. Sometimes combining simple conditions creates powerful logic."
          },
          {
            type: "heading",
            text: "Real-World Combined Conditions"
          },
          {
            type: "paragraph",
            text: "Mrs. Dlamini runs her shop wisely. For credit, a customer must have BOTH an ID and good history. Missing either one? No credit. This isn't two separate checks—it's one decision based on two conditions both being true."
          },
          {
            type: "heading",
            text: "The and Operator"
          },
          {
            type: "paragraph",
            text: "and combines conditions where BOTH must be True for the whole thing to be True. If either condition is False, the entire expression is False."
          },
          {
            type: "code",
            title: "Example: Credit Approval",
            code: "has_id = True\ngood_history = True\n\nif has_id and good_history:\n    print('Credit approved')\nelse:\n    print('Credit denied')",
            output: "Credit approved",
            explanation: "Both conditions are True, so credit is approved"
          },
          {
            type: "code",
            title: "Example: Missing One Requirement",
            code: "has_id = True\ngood_history = False\n\nif has_id and good_history:\n    print('Credit approved')\nelse:\n    print('Credit denied')",
            output: "Credit denied",
            explanation: "Even though has_id is True, good_history is False, so the combined condition is False"
          },
          {
            type: "heading",
            text: "The or Operator"
          },
          {
            type: "paragraph",
            text: "or combines conditions where AT LEAST ONE must be True. If any condition is True, the whole expression is True. All must be False for the expression to be False."
          },
          {
            type: "code",
            title: "Example: Payment Methods",
            code: "has_cash = False\nhas_mobile_money = True\n\nif has_cash or has_mobile_money:\n    print('Payment accepted')\nelse:\n    print('Cannot pay')",
            output: "Payment accepted",
            explanation: "has_mobile_money is True, so even though has_cash is False, the payment works"
          },
          {
            type: "heading",
            text: "The not Operator"
          },
          {
            type: "paragraph",
            text: "not reverses a boolean value. True becomes False, False becomes True. It's useful for checking when something is NOT the case."
          },
          {
            type: "code",
            title: "Example: Access Control",
            code: "is_closed = False\n\nif not is_closed:\n    print('Shop is open, come in')\nelse:\n    print('Shop is closed')",
            output: "Shop is open, come in",
            explanation: "is_closed is False, so 'not is_closed' becomes True"
          },
          {
            type: "heading",
            text: "Combining Multiple Operators"
          },
          {
            type: "paragraph",
            text: "You can combine and, or, and not to create complex logic. Use parentheses to make your intention clear."
          },
          {
            type: "code",
            title: "Example: Store Access Policy",
            code: "age = 25\nhas_id = True\nis_member = False\n\n# Adults with ID, OR members of any age\nif (age >= 18 and has_id) or is_member:\n    print('Access granted')\nelse:\n    print('Access denied')",
            output: "Access granted",
            explanation: "The person is 25 with ID (first part is True), so access is granted even though they're not a member"
          },
          {
            type: "list",
            items: [
              "and: ALL conditions must be True",
              "or: AT LEAST ONE condition must be True",
              "not: REVERSES True/False",
              "Use parentheses to group conditions clearly",
              "Python evaluates 'not' first, then 'and', then 'or'"
            ]
          },
          {
            type: "tip",
            text: "When combining operators, use parentheses even if not strictly required. It makes your logic clear to other humans reading your code!"
          },
          {
            type: "heading",
            text: "Truth Tables"
          },
          {
            type: "paragraph",
            text: "Understanding how and/or work with different True/False combinations:"
          },
          {
            type: "code",
            title: "Example: and Truth Table",
            code: "print(True and True)    # True\nprint(True and False)   # False\nprint(False and True)   # False\nprint(False and False)  # False",
            output: "True\nFalse\nFalse\nFalse",
            explanation: "and only returns True when BOTH sides are True"
          },
          {
            type: "code",
            title: "Example: or Truth Table",
            code: "print(True or True)    # True\nprint(True or False)   # True\nprint(False or True)   # True\nprint(False or False)  # False",
            output: "True\nTrue\nTrue\nFalse",
            explanation: "or returns True when AT LEAST ONE side is True"
          },
          {
            type: "keyPoints",
            points: [
              "and requires ALL conditions to be True",
              "or requires AT LEAST ONE condition to be True",
              "not reverses True to False and False to True",
              "Combine operators to create complex decision logic",
              "Use parentheses to clarify precedence",
              "Boolean logic is fundamental to all programming decisions"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does 'True and False' evaluate to?",
          options: [
            "True",
            "False",
            "Error",
            "None"
          ],
          correct: 1,
          explanation: "and requires BOTH conditions to be True. Since one is False, the result is False."
        },
        {
          question: "What does 'False or True' evaluate to?",
          options: [
            "True",
            "False",
            "Error",
            "None"
          ],
          correct: 0,
          explanation: "or requires AT LEAST ONE condition to be True. Since the second is True, the result is True."
        },
        {
          question: "What does 'not True' evaluate to?",
          options: [
            "True",
            "False",
            "Error",
            "Not True"
          ],
          correct: 1,
          explanation: "not reverses the boolean value. True becomes False."
        },
        {
          question: "Which expression is True?\nage = 30, has_license = False",
          options: [
            "age >= 18 and has_license",
            "age >= 18 or has_license",
            "age < 18 and has_license",
            "not age >= 18"
          ],
          correct: 1,
          explanation: "age >= 18 is True. With 'or', only one condition needs to be True, so the expression is True even though has_license is False."
        },
        {
          question: "How do you check if a number is between 10 and 20 (inclusive)?",
          options: [
            "10 <= number <= 20",
            "number >= 10 and number <= 20",
            "number >= 10 or number <= 20",
            "Both A and B are correct"
          ],
          correct: 3,
          explanation: "Both work! Python allows chained comparisons (10 <= number <= 20), which is equivalent to (number >= 10 and number <= 20)."
        }
      ]
    }
  },

  9: {
    sections: [
      {
        title: "Comparison Operators: Understanding Relationships",
        duration: 6,
        content: [
          {
            type: "paragraph",
            text: "Every day involves comparisons. Is this price higher or lower? Do I have enough stock? Is the temperature above 30 degrees? Comparison operators let programs make these same evaluations."
          },
          {
            type: "heading",
            text: "Real-World Comparisons"
          },
          {
            type: "paragraph",
            text: "Lindiwe at the market constantly compares: her tomato price versus competitors, her stock level versus demand, customer offers versus her costs. These comparisons guide every business decision. Programs work the same way—comparing values to determine what action to take."
          },
          {
            type: "heading",
            text: "All Comparison Operators"
          },
          {
            type: "paragraph",
            text: "Python provides six comparison operators that compare two values and return True or False:"
          },
          {
            type: "list",
            items: [
              "== (equal to): checks if values are the same",
              "!= (not equal to): checks if values are different",
              "> (greater than): left value is bigger",
              "< (less than): left value is smaller",
              ">= (greater than or equal to): left value is bigger or the same",
              "<= (less than or equal to): left value is smaller or the same"
            ]
          },
          {
            type: "code",
            title: "Example: Price Comparisons",
            code: "my_price = 25\ncompetitor_price = 22\n\nprint(my_price > competitor_price)   # True\nprint(my_price < competitor_price)   # False\nprint(my_price == competitor_price)  # False\nprint(my_price != competitor_price)  # True",
            output: "True\nFalse\nFalse\nTrue",
            explanation: "Each comparison returns True or False based on the relationship between values"
          },
          {
            type: "heading",
            text: "Using Comparisons in Decisions"
          },
          {
            type: "paragraph",
            text: "Comparisons are most powerful when combined with if statements to make decisions based on the results."
          },
          {
            type: "code",
            title: "Example: Stock Level Check",
            code: "stock = 8\nreorder_level = 10\n\nif stock < reorder_level:\n    print('Need to reorder')\nelse:\n    print('Stock is sufficient')",
            output: "Need to reorder",
            explanation: "Since 8 is less than 10, the program alerts us to reorder"
          },
          {
            type: "code",
            title: "Example: Temperature Check",
            code: "temperature = 32\n\nif temperature >= 30:\n    print('Hot day - keep products in shade')\nelif temperature >= 20:\n    print('Moderate temperature')\nelse:\n    print('Cool day')",
            output: "Hot day - keep products in shade",
            explanation: "32 is greater than or equal to 30, so special handling is needed"
          },
          {
            type: "heading",
            text: "Comparing Strings"
          },
          {
            type: "paragraph",
            text: "Comparisons aren't just for numbers. You can compare strings too, though == and != are most useful. String comparison is case-sensitive."
          },
          {
            type: "code",
            title: "Example: Customer Type Check",
            code: "customer = 'regular'\n\nif customer == 'regular':\n    discount = 10\nelif customer == 'vip':\n    discount = 20\nelse:\n    discount = 0\n\nprint(f'Discount: {discount}%')",
            output: "Discount: 10%",
            explanation: "String equality checks are case-sensitive: 'regular' equals 'regular' but not 'Regular'"
          },
          {
            type: "warning",
            text: "Remember: = assigns a value (x = 5), while == compares values (x == 5). Using = in a comparison is one of the most common beginner mistakes!"
          },
          {
            type: "heading",
            text: "Chaining Comparisons"
          },
          {
            type: "paragraph",
            text: "Python allows elegant chaining of comparisons for range checks:"
          },
          {
            type: "code",
            title: "Example: Age Range",
            code: "age = 25\n\nif 18 <= age <= 65:\n    print('Working age')\nelse:\n    print('Not working age')",
            output: "Working age",
            explanation: "This checks if age is between 18 and 65 (inclusive) in one elegant expression"
          },
          {
            type: "tip",
            text: "Chained comparisons like 'x < y < z' are unique to Python and much clearer than '(x < y) and (y < z)'!"
          },
          {
            type: "keyPoints",
            points: [
              "Comparison operators return True or False",
              "== checks equality, = assigns values (different!)",
              "Use >, <, >=, <= for numeric comparisons",
              "String comparisons are case-sensitive",
              "Comparisons power all conditional logic",
              "Python allows elegant chained comparisons (10 <= x <= 20)"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does '10 > 5' return?",
          options: [
            "10",
            "5",
            "True",
            "False"
          ],
          correct: 2,
          explanation: "Comparison operators return True or False. Since 10 is greater than 5, it returns True."
        },
        {
          question: "What's the difference between = and ==?",
          options: [
            "No difference",
            "= assigns a value, == compares values",
            "== assigns a value, = compares values",
            "= is for numbers, == is for strings"
          ],
          correct: 1,
          explanation: "= is assignment (giving a value to a variable), == is comparison (checking if values are equal)."
        },
        {
          question: "What does '5 != 5' return?",
          options: [
            "True",
            "False",
            "Error",
            "5"
          ],
          correct: 1,
          explanation: "!= means 'not equal'. Since 5 equals 5, the statement '5 is not equal to 5' is False."
        },
        {
          question: "Which comparison checks if x is between 1 and 10 (inclusive)?",
          options: [
            "1 <= x <= 10",
            "x >= 1 and x <= 10",
            "1 < x < 10",
            "Both A and B"
          ],
          correct: 3,
          explanation: "Both work correctly. 1 <= x <= 10 uses Python's chained comparison syntax, which is equivalent to x >= 1 and x <= 10."
        },
        {
          question: "What does 'hello' == 'Hello' return?",
          options: [
            "True",
            "False",
            "Error",
            "hello"
          ],
          correct: 1,
          explanation: "String comparison is case-sensitive. 'hello' and 'Hello' are different strings, so they're not equal."
        }
      ]
    }
  },

  10: {
    sections: [
      {
        title: "Nested Conditionals: Decisions Within Decisions",
        duration: 8,
        content: [
          {
            type: "paragraph",
            text: "Some decisions only make sense after other decisions. You only check employment after confirming someone has ID. You only calculate discount after confirming membership. This is nested logic—decisions inside decisions."
          },
          {
            type: "heading",
            text: "Sequential Decision Making"
          },
          {
            type: "paragraph",
            text: "When Thembi reviews loan applications, she doesn't check everything at once. First: ID? If no, stop there. If yes, then: employed? If no, stop. If yes, then: amount under R5000? Each question only matters if the previous answer was yes."
          },
          {
            type: "heading",
            text: "What Are Nested Conditionals?"
          },
          {
            type: "paragraph",
            text: "Nested conditionals are if statements inside other if statements. The inner if only gets checked if the outer if is True. This creates layers of logic where each level depends on the one before it."
          },
          {
            type: "code",
            title: "Example: Basic Nesting",
            code: "has_id = True\nis_employed = True\n\nif has_id:\n    print('ID verified')\n    if is_employed:\n        print('Employment confirmed')\n        print('Approved')\n    else:\n        print('Not employed - Denied')\nelse:\n    print('No ID - Denied')",
            output: "ID verified\nEmployment confirmed\nApproved",
            explanation: "The employment check only happens because has_id is True. Notice the increased indentation for nested code."
          },
          {
            type: "heading",
            text: "Indentation Defines Nesting"
          },
          {
            type: "paragraph",
            text: "In Python, indentation isn't just style—it's syntax. Each level of nesting requires an additional level of indentation (4 spaces)."
          },
          {
            type: "list",
            items: [
              "Level 0: No indentation (main code)",
              "Level 1: 4 spaces (first if)",
              "Level 2: 8 spaces (nested if inside first if)",
              "Level 3: 12 spaces (if inside if inside if)",
              "Each level adds 4 spaces"
            ]
          },
          {
            type: "code",
            title: "Example: Loan Approval Process",
            code: "has_id = True\nis_employed = True\nloan_amount = 4000\n\nif has_id:\n    if is_employed:\n        if loan_amount <= 5000:\n            print('Loan approved')\n        else:\n            print('Amount too high')\n    else:\n        print('Employment required')\nelse:\n    print('ID required')",
            output: "Loan approved",
            explanation: "Three levels of checking: ID, then employment, then amount. All three conditions pass."
          },
          {
            type: "heading",
            text: "When to Use Nesting vs. and"
          },
          {
            type: "paragraph",
            text: "Sometimes you can use 'and' instead of nesting. When should you nest?"
          },
          {
            type: "code",
            title: "Example: Using 'and' (Simple Check)",
            code: "has_id = True\nis_employed = True\n\nif has_id and is_employed:\n    print('Approved')\nelse:\n    print('Denied')",
            output: "Approved",
            explanation: "When you just need a yes/no result and don't need intermediate messages, 'and' is cleaner"
          },
          {
            type: "code",
            title: "Example: Using Nesting (Specific Messages)",
            code: "has_id = True\nis_employed = False\n\nif has_id:\n    if is_employed:\n        print('Approved')\n    else:\n        print('Denied: Need employment')\nelse:\n    print('Denied: Need ID')",
            output: "Denied: Need employment",
            explanation: "Nesting lets you give specific feedback at each decision point"
          },
          {
            type: "tip",
            text: "Use 'and' for simple all-or-nothing checks. Use nesting when you need different messages or actions at each decision level."
          },
          {
            type: "heading",
            text: "Real-World Application"
          },
          {
            type: "paragraph",
            text: "Nested conditionals appear in permission systems, multi-step validations, game logic, discount calculations with multiple criteria, and any process where later decisions depend on earlier ones."
          },
          {
            type: "code",
            title: "Example: Market Vendor Pricing",
            code: "is_member = True\nquantity = 12\n\nif is_member:\n    if quantity >= 10:\n        price = 18  # member bulk price\n    else:\n        price = 20  # member regular price\nelse:\n    if quantity >= 10:\n        price = 22  # non-member bulk price\n    else:\n        price = 25  # non-member regular price\n\nprint(f'Price per item: R{price}')",
            output: "Price per item: R18",
            explanation: "Four different prices depending on both membership status and quantity"
          },
          {
            type: "warning",
            text: "Too many levels of nesting (more than 3-4) make code hard to read. Consider restructuring using functions (Level 26) or combining conditions differently."
          },
          {
            type: "keyPoints",
            points: [
              "Nested conditionals are if statements inside other ifs",
              "Inner conditions only check if outer conditions are True",
              "Each nesting level adds 4 spaces of indentation",
              "Use nesting for sequential decision-making",
              "Use 'and' for simple combined conditions",
              "Nesting allows specific feedback at each decision point",
              "Avoid nesting more than 3-4 levels deep"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "When does a nested if statement get checked?",
          options: [
            "Always, regardless of the outer if",
            "Only if the outer if condition is True",
            "Only if the outer if condition is False",
            "Never"
          ],
          correct: 1,
          explanation: "Nested if statements only execute if their containing (outer) if condition is True."
        },
        {
          question: "How many spaces should you indent a nested if?",
          options: [
            "2 spaces",
            "4 spaces from the start",
            "8 spaces from the start (4 more than the outer if)",
            "It doesn't matter"
          ],
          correct: 2,
          explanation: "Each nesting level adds 4 spaces. If the outer if is at 4 spaces, the nested if should be at 8 spaces."
        },
        {
          question: "What's the advantage of nesting over using 'and'?",
          options: [
            "Nesting is always faster",
            "Nesting allows different messages/actions at each decision level",
            "There is no advantage",
            "Nesting uses less memory"
          ],
          correct: 1,
          explanation: "Nesting lets you provide specific feedback or take different actions at each decision point, not just a final yes/no."
        },
        {
          question: "What's wrong with this code?\nif has_id:\nif is_employed:\nprint('Approved')",
          options: [
            "Missing colons after if statements",
            "Wrong indentation",
            "Should use elif instead",
            "Both A and B"
          ],
          correct: 3,
          explanation: "Both issues: if statements need colons (:) at the end, and the nested if and print need proper indentation."
        },
        {
          question: "When should you avoid nesting?",
          options: [
            "Always avoid it",
            "When you have more than 3-4 levels",
            "When checking two conditions",
            "Never avoid it"
          ],
          correct: 1,
          explanation: "Deep nesting (more than 3-4 levels) becomes hard to read and understand. Consider restructuring the logic."
        }
      ]
    }
  },

  11: {
    sections: [
      {
        title: "While Loops: Repeating Until Complete",
        duration: 9,
        content: [
          {
            type: "paragraph",
            text: "Much of life involves repeating actions until conditions change. Washing dishes until the basin is empty. Serving customers while they're waiting. Saving money until you reach your goal. While loops let programs do exactly this—repeat until something changes."
          },
          {
            type: "heading",
            text: "Real-World Repetition"
          },
          {
            type: "paragraph",
            text: "After dinner, Thandiwe doesn't count dishes. She simply keeps washing while there are dirty dishes. Each cycle is the same: pick up dish, scrub, rinse, set aside. The basin empties gradually. When it's empty, she stops naturally. No counting required—just response to current conditions."
          },
          {
            type: "heading",
            text: "What Is a While Loop?"
          },
          {
            type: "paragraph",
            text: "A while loop repeats a block of code as long as a condition remains True. Each time through the loop, Python checks the condition. If True, run the code again. If False, stop and continue with the rest of the program."
          },
          {
            type: "code",
            title: "Example: Simple Counter",
            code: "count = 1\n\nwhile count <= 3:\n    print(f'Count: {count}')\n    count = count + 1\n\nprint('Done')",
            output: "Count: 1\nCount: 2\nCount: 3\nDone",
            explanation: "The loop runs while count is 3 or less. After count becomes 4, the condition is False and the loop stops"
          },
          {
            type: "heading",
            text: "The Loop Structure"
          },
          {
            type: "list",
            items: [
              "Start with: while condition:",
              "Condition evaluates to True or False",
              "Code block is indented (4 spaces)",
              "Condition is checked BEFORE each iteration",
              "Loop continues until condition becomes False",
              "You must change something inside the loop that eventually makes the condition False"
            ]
          },
          {
            type: "warning",
            text: "CRITICAL: The loop must eventually make the condition False. Otherwise you create an infinite loop that never stops!"
          },
          {
            type: "code",
            title: "Example: Saving Toward a Goal",
            code: "balance = 0\ngoal = 100\nweekly_savings = 30\nweeks = 0\n\nwhile balance < goal:\n    balance = balance + weekly_savings\n    weeks = weeks + 1\n    print(f'Week {weeks}: Saved R{balance}')\n\nprint(f'Goal reached in {weeks} weeks!')",
            output: "Week 1: Saved R30\nWeek 2: Saved R60\nWeek 3: Saved R90\nWeek 4: Saved R120\nGoal reached in 4 weeks!",
            explanation: "The loop continues until balance reaches the goal. We don't know in advance how many weeks it will take."
          },
          {
            type: "heading",
            text: "User Input Loops"
          },
          {
            type: "paragraph",
            text: "While loops are perfect for repeating until a user provides valid input or signals they're done."
          },
          {
            type: "code",
            title: "Example: Menu System",
            code: "choice = ''\n\nwhile choice != 'quit':\n    print('Enter command (help/info/quit):')\n    choice = input().lower()\n    \n    if choice == 'help':\n        print('Help: Commands available')\n    elif choice == 'info':\n        print('Info: System version 1.0')\n    elif choice == 'quit':\n        print('Goodbye!')\n    else:\n        print('Unknown command')",
            output: "Enter command (help/info/quit):\nhelp\nHelp: Commands available\nEnter command (help/info/quit):\nquit\nGoodbye!",
            explanation: "Loop continues until user types 'quit'. We don't know how many commands they'll enter."
          },
          {
            type: "heading",
            text: "Accumulation Pattern"
          },
          {
            type: "paragraph",
            text: "A common pattern: start with an initial value, then repeatedly add or update in the loop."
          },
          {
            type: "code",
            title: "Example: Daily Sales Total",
            code: "total = 0\nday = 1\ndays = 5\n\nwhile day <= days:\n    daily_sale = 150 * day  # increasing sales\n    total = total + daily_sale\n    print(f'Day {day}: R{daily_sale} (Total: R{total})')\n    day = day + 1\n\nprint(f'Week total: R{total}')",
            output: "Day 1: R150 (Total: R150)\nDay 2: R300 (Total: R450)\nDay 3: R450 (Total: R900)\nDay 4: R600 (Total: R1500)\nDay 5: R750 (Total: R2250)\nWeek total: R2250",
            explanation: "We accumulate (add up) sales each day. The total grows with each iteration."
          },
          {
            type: "heading",
            text: "Common Pitfalls"
          },
          {
            type: "paragraph",
            text: "Watch out for these common mistakes with while loops:"
          },
          {
            type: "list",
            items: [
              "Forgetting to update the loop variable (infinite loop)",
              "Condition that's never True (loop never runs)",
              "Updating the wrong variable",
              "Checking the condition incorrectly (off-by-one errors)"
            ]
          },
          {
            type: "code",
            title: "Example: Infinite Loop (DON'T DO THIS)",
            code: "# WARNING: This never stops!\n# count = 1\n# while count <= 5:\n#     print(count)\n#     # Forgot to increment count!\n\n# Always make sure the condition eventually becomes False",
            explanation: "If you forget to change count, it stays 1 forever, and the loop never ends"
          },
          {
            type: "tip",
            text: "While loops are for 'keep doing this until X happens'. When you know exactly how many times to repeat, use a for loop instead (Level 12)."
          },
          {
            type: "keyPoints",
            points: [
              "while loops repeat as long as a condition is True",
              "Condition is checked BEFORE each iteration",
              "Must update something inside the loop to eventually make condition False",
              "Perfect for unknown number of repetitions",
              "Common with user input and accumulation patterns",
              "Forgetting to update loop variables creates infinite loops",
              "Use for loops when you know the exact count"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "When does a while loop check its condition?",
          options: [
            "Once, at the start",
            "Once, at the end",
            "Before every iteration",
            "After every iteration"
          ],
          correct: 2,
          explanation: "The condition is checked BEFORE each iteration. If False, the loop doesn't run (or stops if already running)."
        },
        {
          question: "What happens if the while condition is never False?",
          options: [
            "Python throws an error",
            "The loop runs once",
            "Infinite loop - the program never stops",
            "The loop doesn't run at all"
          ],
          correct: 2,
          explanation: "If the condition never becomes False, the loop continues forever (infinite loop). You must eventually change something to make it False."
        },
        {
          question: "What's printed?\nx = 5\nwhile x > 0:\n    x = x - 1\nprint(x)",
          options: [
            "5, 4, 3, 2, 1",
            "5, 4, 3, 2, 1, 0",
            "0",
            "Nothing"
          ],
          correct: 2,
          explanation: "The loop decrements x until it reaches 0 (making condition False), then prints x (which is 0)."
        },
        {
          question: "What's wrong with this code?\nx = 1\nwhile x <= 5:\n    print(x)",
          options: [
            "Missing indentation",
            "Should use if instead",
            "x is never updated - infinite loop",
            "Nothing wrong"
          ],
          correct: 2,
          explanation: "x never changes inside the loop, so the condition remains True forever. Always update your loop variable!"
        },
        {
          question: "When should you use a while loop instead of a for loop?",
          options: [
            "Always use while, never for",
            "When you don't know in advance how many times to repeat",
            "When you know exactly how many iterations",
            "While is always better"
          ],
          correct: 1,
          explanation: "Use while when the number of iterations depends on changing conditions. Use for when you know the count in advance."
        }
      ]
    }
  },

  12: {
    sections: [
      {
        title: "For Loops: Iterating Through Collections",
        duration: 8,
        content: [
          {
            type: "paragraph",
            text: "When you know exactly what you need to process—a list of names, a box of items, a sequence of numbers—for loops are the perfect tool. They work through each item systematically, one at a time, until the collection is complete."
          },
          {
            type: "heading",
            text: "Real-World Systematic Processing"
          },
          {
            type: "paragraph",
            text: "A delivery truck arrives at Mandla's shop with 12 boxes. He knows the count. He'll check each box: count items, verify contents, mark as received. Box 1, check and mark. Box 2, check and mark. He processes each one in order until all 12 are done. This is exactly how for loops work."
          },
          {
            type: "heading",
            text: "What Is a For Loop?"
          },
          {
            type: "paragraph",
            text: "A for loop processes each item in a collection (like a list) or sequence. You don't need to manage a counter or check conditions—Python handles that. You simply say 'for each item, do this'."
          },
          {
            type: "code",
            title: "Example: Processing a List",
            code: "fruits = ['apple', 'banana', 'orange']\n\nfor fruit in fruits:\n    print(f'Selling: {fruit}')",
            output: "Selling: apple\nSelling: banana\nSelling: orange",
            explanation: "The loop automatically goes through each item in the list, one at a time"
          },
          {
            type: "heading",
            text: "For Loop Structure"
          },
          {
            type: "list",
            items: [
              "Start with: for variable in collection:",
              "variable is assigned each item in turn",
              "Code block is indented (4 spaces)",
              "Runs once for each item in the collection",
              "No need to manage counters or indices",
              "When collection is exhausted, loop ends naturally"
            ]
          },
          {
            type: "code",
            title: "Example: Calculating Total",
            code: "prices = [25, 30, 15, 40]\ntotal = 0\n\nfor price in prices:\n    total = total + price\n    print(f'Added R{price}, total now R{total}')\n\nprint(f'Final total: R{total}')",
            output: "Added R25, total now R25\nAdded R30, total now R55\nAdded R15, total now R70\nAdded R40, total now R110\nFinal total: R110",
            explanation: "The loop processes each price, accumulating the total"
          },
          {
            type: "heading",
            text: "For Loops vs. While Loops"
          },
          {
            type: "paragraph",
            text: "When do you use each type of loop?"
          },
          {
            type: "list",
            items: [
              "Use FOR when: you have a collection to process, you know the count, you're iterating through items",
              "Use WHILE when: you don't know how many times, you're waiting for a condition, you're repeating until user input"
            ]
          },
          {
            type: "code",
            title: "Example: Customer Names",
            code: "customers = ['Thandi', 'Bongani', 'Lindiwe']\n\nfor customer in customers:\n    print(f'Hello, {customer}!')\n    print('Thank you for your business')\n    print()  # blank line",
            output: "Hello, Thandi!\nThank you for your business\n\nHello, Bongani!\nThank you for your business\n\nHello, Lindiwe!\nThank you for your business\n",
            explanation: "Each customer gets the same personalized greeting"
          },
          {
            type: "heading",
            text: "Processing Strings"
          },
          {
            type: "paragraph",
            text: "Strings are collections of characters, so you can loop through each character:"
          },
          {
            type: "code",
            title: "Example: Counting Vowels",
            code: "word = 'hello'\nvowel_count = 0\n\nfor letter in word:\n    if letter in 'aeiou':\n        vowel_count = vowel_count + 1\n\nprint(f'Vowels in \"{word}\": {vowel_count}')",
            output: "Vowels in \"hello\": 2",
            explanation: "The loop examines each character in the string, counting vowels (e and o)"
          },
          {
            type: "heading",
            text: "Practical Applications"
          },
          {
            type: "paragraph",
            text: "For loops appear everywhere: processing orders, validating data, calculating statistics, sending notifications, formatting reports."
          },
          {
            type: "code",
            title: "Example: Stock Check",
            code: "stock = [12, 5, 20, 8, 15]\nitem_names = ['Bread', 'Milk', 'Sugar', 'Eggs', 'Soap']\n\nprint('Low stock items:')\nfor i in range(len(stock)):\n    if stock[i] < 10:\n        print(f'- {item_names[i]}: {stock[i]} units')",
            output: "Low stock items:\n- Milk: 5 units\n- Eggs: 8 units",
            explanation: "Loop through stock levels, reporting items below 10 units"
          },
          {
            type: "tip",
            text: "For loops are cleaner and safer than while loops for processing collections. Python handles the iteration details, reducing bugs."
          },
          {
            type: "heading",
            text: "The Loop Variable"
          },
          {
            type: "paragraph",
            text: "The variable after 'for' (like 'fruit' or 'price') automatically takes each value from the collection. Choose descriptive names: for customer in customers, for item in items."
          },
          {
            type: "warning",
            text: "The loop variable is temporary—it only exists during the loop. After the loop, it holds the LAST value from the collection."
          },
          {
            type: "keyPoints",
            points: [
              "for loops process each item in a collection",
              "Python automatically handles iteration details",
              "Structure: for item in collection:",
              "No need to manage counters or indices",
              "Works with lists, strings, and other sequences",
              "Use for when you know what to process",
              "Use while when you don't know how many times",
              "Choose descriptive loop variable names"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does 'for item in collection:' mean?",
          options: [
            "Check if item is in collection",
            "Process each item in collection, one at a time",
            "Count how many items are in collection",
            "Remove item from collection"
          ],
          correct: 1,
          explanation: "A for loop processes each item in the collection sequentially, running the code block once per item."
        },
        {
          question: "How many times does this loop run?\nfor x in [1, 2, 3]:",
          options: [
            "1 time",
            "2 times",
            "3 times",
            "Infinite"
          ],
          correct: 2,
          explanation: "The loop runs once for each item in the list. The list has 3 items, so it runs 3 times."
        },
        {
          question: "What's printed?\nfor letter in 'abc':\n    print(letter)",
          options: [
            "abc",
            "a b c (on one line)",
            "a\nb\nc (on separate lines)",
            "Error"
          ],
          correct: 2,
          explanation: "Strings are sequences of characters. The loop prints each character on a separate line."
        },
        {
          question: "When should you use a for loop instead of a while loop?",
          options: [
            "When you have a collection to process",
            "When you don't know how many iterations",
            "When waiting for user input",
            "Never, while is always better"
          ],
          correct: 0,
          explanation: "Use for loops when you have a definite collection (list, string, etc.) to process. Use while for indefinite repetition."
        },
        {
          question: "What's the value of x after this code?\nfor x in [10, 20, 30]:\n    pass\nprint(x)",
          options: [
            "10",
            "20",
            "30",
            "Error - x doesn't exist"
          ],
          correct: 2,
          explanation: "After the loop, the loop variable holds the LAST value from the collection, which is 30."
        }
      ]
    }
  },

  13: {
    sections: [
      {
        title: "The range() Function: Generating Number Sequences",
        duration: 7,
        content: [
          {
            type: "paragraph",
            text: "Sometimes you don't have an existing collection—you just need to repeat something a specific number of times. Count from 1 to 20. Process 10 orders. Try 5 times. The range() function generates number sequences perfect for these situations."
          },
          {
            type: "heading",
            text: "Real-World Counting"
          },
          {
            type: "paragraph",
            text: "Nandi bakes 20 loaves of bread each morning. She doesn't have a list called 'loaves'. She simply counts: 1, 2, 3... through 20. Each loaf follows the same process. The range() function does exactly this—creates a sequence of numbers to count through."
          },
          {
            type: "heading",
            text: "What Is range()?"
          },
          {
            type: "paragraph",
            text: "range() generates a sequence of numbers. It's commonly used with for loops when you need to repeat something a specific number of times or process numbers in a sequence."
          },
          {
            type: "code",
            title: "Example: Simple Count",
            code: "for i in range(5):\n    print(i)",
            output: "0\n1\n2\n3\n4",
            explanation: "range(5) generates numbers from 0 to 4. It starts at 0 and stops BEFORE 5."
          },
          {
            type: "note",
            text: "range() starts at 0 by default and stops ONE BEFORE the number you specify. This is a common source of confusion for beginners!"
          },
          {
            type: "heading",
            text: "Three Ways to Use range()"
          },
          {
            type: "paragraph",
            text: "range() accepts 1, 2, or 3 arguments:"
          },
          {
            type: "list",
            items: [
              "range(stop): generates 0 to stop-1",
              "range(start, stop): generates start to stop-1",
              "range(start, stop, step): generates start to stop-1, incrementing by step"
            ]
          },
          {
            type: "code",
            title: "Example: range(stop)",
            code: "# Count from 0 to 2\nfor i in range(3):\n    print(i)",
            output: "0\n1\n2",
            explanation: "range(3) generates 0, 1, 2"
          },
          {
            type: "code",
            title: "Example: range(start, stop)",
            code: "# Count from 1 to 5\nfor i in range(1, 6):\n    print(i)",
            output: "1\n2\n3\n4\n5",
            explanation: "range(1, 6) generates 1 through 5. Remember: stops BEFORE 6."
          },
          {
            type: "code",
            title: "Example: range(start, stop, step)",
            code: "# Even numbers from 0 to 10\nfor i in range(0, 11, 2):\n    print(i)",
            output: "0\n2\n4\n6\n8\n10",
            explanation: "range(0, 11, 2) starts at 0, goes to 10, incrementing by 2 each time"
          },
          {
            type: "heading",
            text: "Counting Backwards"
          },
          {
            type: "paragraph",
            text: "Use a negative step to count down:"
          },
          {
            type: "code",
            title: "Example: Countdown",
            code: "for i in range(5, 0, -1):\n    print(i)\nprint('Go!')",
            output: "5\n4\n3\n2\n1\nGo!",
            explanation: "Start at 5, stop before 0, step by -1 (count down)"
          },
          {
            type: "heading",
            text: "Practical Applications"
          },
          {
            type: "paragraph",
            text: "range() is perfect for counting, repeating actions, generating ticket numbers, processing sequences."
          },
          {
            type: "code",
            title: "Example: Baking Bread",
            code: "total_loaves = 20\n\nfor loaf_num in range(1, total_loaves + 1):\n    print(f'Baking loaf {loaf_num}')\n\nprint(f'All {total_loaves} loaves complete!')",
            output: "Baking loaf 1\nBaking loaf 2\nBaking loaf 3\n...\nBaking loaf 20\nAll 20 loaves complete!",
            explanation: "We use range(1, 21) to count from 1 to 20 (inclusive)"
          },
          {
            type: "code",
            title: "Example: Total Calculation",
            code: "total = 0\nfor i in range(1, 6):\n    total = total + i\n    print(f'Adding {i}, total is now {total}')\n\nprint(f'Sum of 1-5: {total}')",
            output: "Adding 1, total is now 1\nAdding 2, total is now 3\nAdding 3, total is now 6\nAdding 4, total is now 10\nAdding 5, total is now 15\nSum of 1-5: 15",
            explanation: "Accumulate the sum of numbers 1 through 5"
          },
          {
            type: "tip",
            text: "To count from 1 to N (inclusive), use range(1, N+1). To repeat something N times (and don't care about the number), use range(N)."
          },
          {
            type: "heading",
            text: "range() Is Memory Efficient"
          },
          {
            type: "paragraph",
            text: "range() doesn't create a full list in memory. It generates numbers on-demand as the loop needs them. This makes it efficient even for large ranges like range(1000000)."
          },
          {
            type: "warning",
            text: "Remember: range(5) gives you 0, 1, 2, 3, 4 (not 1-5!). For 1-5, use range(1, 6)."
          },
          {
            type: "keyPoints",
            points: [
              "range() generates sequences of numbers",
              "range(stop): 0 to stop-1",
              "range(start, stop): start to stop-1",
              "range(start, stop, step): custom increment",
              "Stops ONE BEFORE the stop value",
              "Use negative step to count backwards",
              "Perfect for counting and repeating N times",
              "Memory efficient—generates values on demand"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What numbers does range(5) generate?",
          options: [
            "1, 2, 3, 4, 5",
            "0, 1, 2, 3, 4",
            "0, 1, 2, 3, 4, 5",
            "1, 2, 3, 4"
          ],
          correct: 1,
          explanation: "range(5) starts at 0 (default) and stops BEFORE 5, generating 0, 1, 2, 3, 4."
        },
        {
          question: "How do you generate numbers 1 through 10 (inclusive)?",
          options: [
            "range(10)",
            "range(1, 10)",
            "range(1, 11)",
            "range(0, 10)"
          ],
          correct: 2,
          explanation: "range() stops BEFORE the stop value, so range(1, 11) gives you 1 through 10."
        },
        {
          question: "What does range(0, 10, 2) generate?",
          options: [
            "0, 2, 4, 6, 8, 10",
            "0, 2, 4, 6, 8",
            "2, 4, 6, 8, 10",
            "0, 1, 2, 3, 4"
          ],
          correct: 1,
          explanation: "Starts at 0, stops before 10, increments by 2. Generates 0, 2, 4, 6, 8 (stops before 10)."
        },
        {
          question: "How do you count down from 5 to 1?",
          options: [
            "range(5, 1)",
            "range(5, 0, -1)",
            "range(1, 5, -1)",
            "range(5, 1, -1)"
          ],
          correct: 1,
          explanation: "Start at 5, stop before 0 (so include 1), step by -1. range(5, 0, -1) gives 5, 4, 3, 2, 1."
        },
        {
          question: "How many times does this loop run?\nfor i in range(3):\n    print('Hello')",
          options: [
            "2 times",
            "3 times",
            "4 times",
            "Infinite"
          ],
          correct: 1,
          explanation: "range(3) generates 0, 1, 2 (three numbers), so the loop runs 3 times."
        }
      ]
    }
  },

  14: {
    sections: [
      {
        title: "Break and Continue: Controlling Loop Flow",
        duration: 8,
        content: [
          {
            type: "paragraph",
            text: "Sometimes you need more control over loops. Skip processing one item but continue with others. Stop the entire loop early when you've found what you need. Python's break and continue statements give you this precise control."
          },
          {
            type: "heading",
            text: "Real-World Loop Control"
          },
          {
            type: "paragraph",
            text: "Sipho sorts tomatoes for his stall. Most tomatoes: check and display. Slightly bruised: skip to discount box but keep sorting. Rotten: stop immediately—the whole crate might be bad. Two different types of control: skip one item (continue) vs. stop everything (break)."
          },
          {
            type: "heading",
            text: "The break Statement"
          },
          {
            type: "paragraph",
            text: "break immediately exits a loop, regardless of the loop condition. It's like saying 'we're done here' before reaching the natural end."
          },
          {
            type: "code",
            title: "Example: Finding an Item",
            code: "customers = ['Thandi', 'Bongani', 'Lindiwe', 'Sipho']\ntarget = 'Lindiwe'\n\nfor customer in customers:\n    if customer == target:\n        print(f'Found {target}!')\n        break  # Stop searching\n    print(f'Checking {customer}')\n\nprint('Search complete')",
            output: "Checking Thandi\nChecking Bongani\nFound Lindiwe!\nSearch complete",
            explanation: "Once we find Lindiwe, we break out of the loop. Sipho is never checked."
          },
          {
            type: "heading",
            text: "The continue Statement"
          },
          {
            type: "paragraph",
            text: "continue skips the rest of the current iteration and moves to the next one. The loop continues, but this particular item is skipped."
          },
          {
            type: "code",
            title: "Example: Processing Valid Items",
            code: "prices = [25, -5, 30, 0, 15, -10, 20]\n\nfor price in prices:\n    if price <= 0:\n        print(f'Skipping invalid price: {price}')\n        continue  # Skip to next price\n    \n    # This only runs for valid prices\n    print(f'Processing: R{price}')",
            output: "Processing: R25\nSkipping invalid price: -5\nProcessing: R30\nSkipping invalid price: 0\nProcessing: R15\nSkipping invalid price: -10\nProcessing: R20",
            explanation: "Invalid prices are skipped, but the loop continues processing remaining items"
          },
          {
            type: "heading",
            text: "When to Use Each"
          },
          {
            type: "list",
            items: [
              "Use BREAK when: you found what you need, an error requires stopping, a condition makes continuing pointless",
              "Use CONTINUE when: this item is invalid but others might be valid, you want to skip certain items, processing depends on conditions"
            ]
          },
          {
            type: "code",
            title: "Example: Tomato Sorting (break)",
            code: "tomatoes = ['good', 'good', 'bruised', 'rotten', 'good']\n\nfor tomato in tomatoes:\n    if tomato == 'rotten':\n        print('Found rot! Rejecting entire crate.')\n        break\n    elif tomato == 'bruised':\n        print('Bruised - putting in discount box')\n    else:\n        print('Good - putting on display')\n\nprint('Sorting stopped')",
            output: "Good - putting on display\nGood - putting on display\nBruised - putting in discount box\nFound rot! Rejecting entire crate.\nSorting stopped",
            explanation: "As soon as rot is found, we break—no point checking the rest"
          },
          {
            type: "code",
            title: "Example: Stock Check (continue)",
            code: "stock = ['Bread: 12', 'Milk: 0', 'Sugar: 25', 'Eggs: 0', 'Soap: 8']\n\nprint('Items in stock:')\nfor item in stock:\n    if ': 0' in item:\n        continue  # Skip out-of-stock items\n    print(f'- {item}')",
            output: "Items in stock:\n- Bread: 12\n- Sugar: 25\n- Soap: 8",
            explanation: "Out-of-stock items are skipped, but we continue checking the rest"
          },
          {
            type: "heading",
            text: "Practical Applications"
          },
          {
            type: "paragraph",
            text: "break: searching for specific items, validation that fails early, user requesting to quit. continue: filtering data, skipping invalid entries, conditional processing."
          },
          {
            type: "code",
            title: "Example: Password Attempts",
            code: "attempts = 0\nmax_attempts = 3\n\nwhile attempts < max_attempts:\n    password = input('Enter password: ')\n    attempts = attempts + 1\n    \n    if password == 'secret':\n        print('Access granted!')\n        break\n    else:\n        remaining = max_attempts - attempts\n        if remaining > 0:\n            print(f'Wrong. {remaining} attempts left')\n        else:\n            print('Access denied. Too many attempts.')",
            output: "Enter password: wrong\nWrong. 2 attempts left\nEnter password: secret\nAccess granted!",
            explanation: "break exits the loop as soon as the correct password is entered"
          },
          {
            type: "tip",
            text: "break and continue only affect the innermost loop they're in. If you have nested loops, they won't break out of outer loops."
          },
          {
            type: "warning",
            text: "Overusing break can make code hard to follow. Consider if the loop condition could be restructured instead."
          },
          {
            type: "keyPoints",
            points: [
              "break exits the loop immediately",
              "continue skips to the next iteration",
              "break is for 'stop everything now'",
              "continue is for 'skip this one, continue with others'",
              "Only affects the innermost loop",
              "Useful for searches, validation, filtering",
              "Use judiciously to maintain readable code"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does break do in a loop?",
          options: [
            "Skips to the next iteration",
            "Exits the loop immediately",
            "Pauses the loop",
            "Restarts the loop"
          ],
          correct: 1,
          explanation: "break immediately exits the loop, stopping all further iterations."
        },
        {
          question: "What does continue do in a loop?",
          options: [
            "Exits the loop",
            "Skips the rest of this iteration and moves to the next",
            "Restarts the loop from the beginning",
            "Does nothing"
          ],
          correct: 1,
          explanation: "continue skips the remaining code in the current iteration and jumps to the next iteration."
        },
        {
          question: "What's printed?\nfor i in [1, 2, 3, 4, 5]:\n    if i == 3:\n        break\n    print(i)",
          options: [
            "1 2 3 4 5",
            "1 2 3",
            "1 2",
            "Nothing"
          ],
          correct: 2,
          explanation: "Prints 1 and 2. When i becomes 3, break exits the loop before printing 3."
        },
        {
          question: "What's printed?\nfor i in [1, 2, 3, 4, 5]:\n    if i == 3:\n        continue\n    print(i)",
          options: [
            "1 2 3 4 5",
            "1 2 4 5",
            "1 2",
            "3"
          ],
          correct: 1,
          explanation: "Prints all numbers except 3. When i is 3, continue skips the print statement, but the loop continues."
        },
        {
          question: "When is break most useful?",
          options: [
            "When you want to skip invalid data",
            "When you've found what you're looking for and can stop searching",
            "When you want to process every item",
            "When you want to count iterations"
          ],
          correct: 1,
          explanation: "break is perfect for search operations—once you find what you need, there's no point continuing."
        }
      ]
    }
  },

  15: {
    sections: [
      {
        title: "Nested Loops: Loops Within Loops",
        duration: 9,
        content: [
          {
            type: "paragraph",
            text: "Many real-world tasks involve repeating patterns that themselves repeat. Deliver to five shops, six days a week. Check each student's grade in each subject. Process each row and each column. These are nested loops—loops inside other loops."
          },
          {
            type: "heading",
            text: "Real-World Nested Patterns"
          },
          {
            type: "paragraph",
            text: "Thabo delivers bread every morning. Six days: Monday through Saturday. Five shops each day. That's 30 deliveries total. For each day (outer loop), visit each shop (inner loop). A repeating task inside a repeating task."
          },
          {
            type: "heading",
            text: "What Are Nested Loops?"
          },
          {
            type: "paragraph",
            text: "A nested loop is a loop inside another loop. The outer loop runs once, the inner loop completes all its iterations, then the outer loop runs again, and the inner loop completes all its iterations again. This pattern continues."
          },
          {
            type: "code",
            title: "Example: Basic Nested Loop",
            code: "for day in ['Mon', 'Tue', 'Wed']:\n    print(f'{day}:')\n    for shop in ['Shop A', 'Shop B']:\n        print(f'  Delivering to {shop}')\n    print()  # blank line",
            output: "Mon:\n  Delivering to Shop A\n  Delivering to Shop B\n\nTue:\n  Delivering to Shop A\n  Delivering to Shop B\n\nWed:\n  Delivering to Shop A\n  Delivering to Shop B\n",
            explanation: "For each day (outer loop), we visit all shops (inner loop). Total: 3 days × 2 shops = 6 deliveries"
          },
          {
            type: "heading",
            text: "How Nested Loops Execute"
          },
          {
            type: "list",
            items: [
              "Outer loop starts first iteration",
              "Inner loop completes ALL its iterations",
              "Outer loop starts second iteration",
              "Inner loop completes ALL its iterations again",
              "Pattern continues until outer loop finishes",
              "Total iterations = outer count × inner count"
            ]
          },
          {
            type: "code",
            title: "Example: Multiplication Table",
            code: "for i in range(1, 4):\n    for j in range(1, 4):\n        result = i * j\n        print(f'{i} × {j} = {result}')\n    print()  # blank line after each row",
            output: "1 × 1 = 1\n1 × 2 = 2\n1 × 3 = 3\n\n2 × 1 = 2\n2 × 2 = 4\n2 × 3 = 6\n\n3 × 1 = 3\n3 × 2 = 6\n3 × 3 = 9\n",
            explanation: "For each i (outer), we calculate i×j for all j values (inner)"
          },
          {
            type: "heading",
            text: "Practical Application: Grid Processing"
          },
          {
            type: "paragraph",
            text: "Nested loops are perfect for anything organized in rows and columns: spreadsheets, grids, game boards, seating charts."
          },
          {
            type: "code",
            title: "Example: Stock Inventory",
            code: "aisles = ['A', 'B', 'C']\nshelves = [1, 2, 3]\n\nprint('Checking all locations:')\nfor aisle in aisles:\n    for shelf in shelves:\n        location = f'{aisle}{shelf}'\n        print(f'Checking {location}')",
            output: "Checking all locations:\nChecking A1\nChecking A2\nChecking A3\nChecking B1\nChecking B2\nChecking B3\nChecking C1\nChecking C2\nChecking C3",
            explanation: "Every combination of aisle and shelf is checked systematically"
          },
          {
            type: "heading",
            text: "Nested Loops with Lists"
          },
          {
            type: "paragraph",
            text: "Processing each item in multiple lists is a common pattern:"
          },
          {
            type: "code",
            title: "Example: Greeting Multiple Groups",
            code: "teams = ['Sales', 'Support']\nshifts = ['Morning', 'Afternoon']\n\nfor team in teams:\n    for shift in shifts:\n        print(f'{shift} briefing for {team} team')",
            output: "Morning briefing for Sales team\nAfternoon briefing for Sales team\nMorning briefing for Support team\nAfternoon briefing for Support team",
            explanation: "Each team gets both briefings"
          },
          {
            type: "heading",
            text: "Counting with Nested Loops"
          },
          {
            type: "paragraph",
            text: "Nested loops can accumulate totals or count occurrences:"
          },
          {
            type: "code",
            title: "Example: Total Deliveries",
            code: "days = 6\nshops_per_day = 5\ntotal = 0\n\nfor day in range(1, days + 1):\n    for shop in range(1, shops_per_day + 1):\n        total = total + 1\n\nprint(f'Total deliveries: {total}')",
            output: "Total deliveries: 30",
            explanation: "6 days × 5 shops = 30 total deliveries"
          },
          {
            type: "heading",
            text: "break and continue in Nested Loops"
          },
          {
            type: "paragraph",
            text: "break and continue only affect the innermost loop they're in:"
          },
          {
            type: "code",
            title: "Example: break in Inner Loop",
            code: "for day in ['Mon', 'Tue']:\n    print(f'{day}:')\n    for hour in range(1, 6):\n        if hour == 3:\n            print('  Lunch break!')\n            break  # Only exits inner loop\n        print(f'  Hour {hour}')\n    print()  # still executes",
            output: "Mon:\n  Hour 1\n  Hour 2\n  Lunch break!\n\nTue:\n  Hour 1\n  Hour 2\n  Lunch break!\n",
            explanation: "break exits the inner (hour) loop but outer (day) loop continues"
          },
          {
            type: "tip",
            text: "Keep nesting to 2-3 levels max. Deeper nesting becomes hard to read and debug. Consider using functions to break up complex nested logic."
          },
          {
            type: "warning",
            text: "Nested loops multiply iterations: 100 outer × 100 inner = 10,000 total iterations! Watch performance with large data sets."
          },
          {
            type: "keyPoints",
            points: [
              "Nested loops are loops inside other loops",
              "Inner loop completes fully for each outer iteration",
              "Total iterations = outer count × inner count",
              "Perfect for grids, tables, combinations",
              "Each loop has its own variable",
              "break/continue only affect innermost loop",
              "Keep nesting to 2-3 levels for readability",
              "Watch performance with large nested loops"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "How many times does the inner loop run in total?\nfor i in range(3):\n    for j in range(4):\n        print('X')",
          options: [
            "3 times",
            "4 times",
            "7 times",
            "12 times"
          ],
          correct: 3,
          explanation: "The inner loop runs 4 times for each of 3 outer iterations: 3 × 4 = 12 total inner iterations."
        },
        {
          question: "What does this print?\nfor i in range(2):\n    for j in range(2):\n        print(f'{i}{j}')",
          options: [
            "00 01 10 11",
            "0 1 0 1",
            "00 11",
            "0 0 1 1"
          ],
          correct: 0,
          explanation: "For i=0: prints 00, 01. For i=1: prints 10, 11. Each combination of i and j is printed."
        },
        {
          question: "In nested loops, where does break take you?",
          options: [
            "Out of all loops",
            "Out of the innermost loop only",
            "To the beginning of the outer loop",
            "Nowhere, break doesn't work in nested loops"
          ],
          correct: 1,
          explanation: "break only exits the loop it's directly in (the innermost loop). The outer loop continues."
        },
        {
          question: "What's the purpose of nested loops?",
          options: [
            "To make code run faster",
            "To process combinations or multi-dimensional data",
            "To save memory",
            "They have no real purpose"
          ],
          correct: 1,
          explanation: "Nested loops handle combinations (like days×shops) and multi-dimensional data (like rows and columns)."
        },
        {
          question: "How many total iterations?\nfor x in range(2):\n    for y in range(3):\n        for z in range(2):",
          options: [
            "7",
            "8",
            "12",
            "Not enough information"
          ],
          correct: 2,
          explanation: "2 × 3 × 2 = 12 total iterations. Multiply all loop counts for total iterations in nested loops."
        }
      ]
    }
  },

// Levels 16-25: Lists and Collections

  16: {
    sections: [
      {
        title: "Creating and Accessing Lists",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Imagine trying to manage a shopping list where each item is stored in a separate variable: item1, item2, item3... By the time you have 20 items, your code becomes impossible to manage. Lists solve this by letting you store multiple related items in a single, organized collection."
          },
          {
            type: "heading",
            text: "What is a List?"
          },
          {
            type: "paragraph",
            text: "A list is an ordered collection of items, like Zanele's shopping list. Each item has a position (called an index), starting from 0. You can access any item by its index, add new items, or remove items you don't need anymore."
          },
          {
            type: "code",
            title: "Example: Creating a Shopping List",
            code: "shopping = ['Bread', 'Milk', 'Sugar', 'Eggs', 'Soap']\n\nprint(shopping)\nprint(type(shopping))",
            output: "['Bread', 'Milk', 'Sugar', 'Eggs', 'Soap']\n<class 'list'>",
            explanation: "Square brackets [] create a list. Items are separated by commas."
          },
          {
            type: "heading",
            text: "Accessing Items by Index"
          },
          {
            type: "paragraph",
            text: "Lists are zero-indexed: the first item is at position 0, the second at position 1, and so on. You use square brackets with the index to get a specific item."
          },
          {
            type: "code",
            title: "Example: Accessing List Items",
            code: "shopping = ['Bread', 'Milk', 'Sugar', 'Eggs', 'Soap']\n\nprint(shopping[0])  # First item\nprint(shopping[2])  # Third item\nprint(shopping[4])  # Fifth item",
            output: "Bread\nSugar\nSoap",
            explanation: "Remember: index 0 = first item, index 2 = third item"
          },
          {
            type: "code",
            title: "Example: Negative Indexing",
            code: "shopping = ['Bread', 'Milk', 'Sugar', 'Eggs', 'Soap']\n\nprint(shopping[-1])  # Last item\nprint(shopping[-2])  # Second-to-last",
            output: "Soap\nEggs",
            explanation: "Negative indices count from the end: -1 is last, -2 is second-to-last"
          },
          {
            type: "heading",
            text: "Lists Can Hold Different Types"
          },
          {
            type: "code",
            title: "Example: Mixed Data Types",
            code: "customer = ['Thandi', 28, True, 1245.50]\n\nprint(customer[0])  # Name\nprint(customer[1])  # Age\nprint(customer[2])  # Active?\nprint(customer[3])  # Balance",
            output: "Thandi\n28\nTrue\n1245.5",
            explanation: "Lists can contain strings, numbers, booleans, or any other type"
          },
          {
            type: "heading",
            text: "Getting List Length"
          },
          {
            type: "code",
            title: "Example: Using len()",
            code: "shopping = ['Bread', 'Milk', 'Sugar', 'Eggs', 'Soap']\n\nprint(f'Items on list: {len(shopping)}')\nprint(f'Last item: {shopping[len(shopping) - 1]}')",
            output: "Items on list: 5\nLast item: Soap",
            explanation: "len() returns the number of items. Since indices start at 0, the last index is len(list) - 1"
          },
          {
            type: "note",
            text: "Trying to access an index that doesn't exist (like shopping[10] when there are only 5 items) causes an IndexError."
          },
          {
            type: "keyPoints",
            points: [
              "Lists store multiple items in one variable using square brackets []",
              "Items are accessed by index (position), starting from 0",
              "Negative indices count from the end: -1 is last item",
              "Lists can hold any data type, including mixed types",
              "len() returns the number of items in the list"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What is the index of the first item in a list?",
          options: ["0", "1", "-1", "It depends on the list"],
          correct: 0,
          explanation: "Python lists are zero-indexed, so the first item is always at index 0."
        },
        {
          question: "What does shopping[-1] return?",
          options: ["The first item", "The last item", "Error", "The second item"],
          correct: 1,
          explanation: "Negative index -1 always refers to the last item in the list."
        },
        {
          question: "If a list has 5 items, what is the last valid index?",
          options: ["5", "4", "-1", "Both 4 and -1"],
          correct: 3,
          explanation: "Both index 4 (since counting starts at 0) and index -1 (counting from the end) access the last item."
        },
        {
          question: "Can a list contain both strings and numbers?",
          options: ["Yes", "No", "Only if converted", "Only in Python 3"],
          correct: 0,
          explanation: "Lists can contain any mix of data types: strings, numbers, booleans, even other lists."
        },
        {
          question: "What does len([10, 20, 30]) return?",
          options: ["60", "3", "30", "2"],
          correct: 1,
          explanation: "len() returns the number of items in the list, which is 3."
        }
      ]
    }
  },

  17: {
    sections: [
      {
        title: "List Methods: Adding, Removing, and Modifying",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Lists aren't static—they change as your program runs. Like Nomvula's task list that gets updated throughout the day, Python lists have methods that let you add new items, remove completed ones, and rearrange as needed."
          },
          {
            type: "heading",
            text: "Adding Items to a List"
          },
          {
            type: "paragraph",
            text: "The append() method adds an item to the end of a list. It's like adding a new task at the bottom of your to-do list."
          },
          {
            type: "code",
            title: "Example: Using append()",
            code: "tasks = ['Buy vegetables', 'Pay rent', 'Call Mama']\nprint('Before:', tasks)\n\ntasks.append('Deliver parcel')\nprint('After:', tasks)",
            output: "Before: ['Buy vegetables', 'Pay rent', 'Call Mama']\nAfter: ['Buy vegetables', 'Pay rent', 'Call Mama', 'Deliver parcel']",
            explanation: "append() adds the new item to the end of the list"
          },
          {
            type: "heading",
            text: "Inserting at a Specific Position"
          },
          {
            type: "paragraph",
            text: "Sometimes you need to add an item at a specific position, not just at the end. The insert() method takes two arguments: the index where you want to insert, and the item to insert."
          },
          {
            type: "code",
            title: "Example: Using insert()",
            code: "tasks = ['Buy vegetables', 'Call Mama']\ntasks.insert(1, 'Pay rent')\n\nprint(tasks)",
            output: "['Buy vegetables', 'Pay rent', 'Call Mama']",
            explanation: "insert(1, 'Pay rent') puts 'Pay rent' at index 1, shifting other items to the right"
          },
          {
            type: "heading",
            text: "Removing Items"
          },
          {
            type: "paragraph",
            text: "There are several ways to remove items from a list, depending on what information you have."
          },
          {
            type: "code",
            title: "Example: Using remove()",
            code: "tasks = ['Buy vegetables', 'Pay rent', 'Call Mama']\ntasks.remove('Buy vegetables')\n\nprint(tasks)",
            output: "['Pay rent', 'Call Mama']",
            explanation: "remove() deletes the first occurrence of the specified value"
          },
          {
            type: "code",
            title: "Example: Using pop()",
            code: "tasks = ['Buy vegetables', 'Pay rent', 'Call Mama']\nlast_task = tasks.pop()  # Removes and returns last item\nfirst_task = tasks.pop(0)  # Removes and returns item at index 0\n\nprint('Removed:', last_task)\nprint('Removed:', first_task)\nprint('Remaining:', tasks)",
            output: "Removed: Call Mama\nRemoved: Buy vegetables\nRemaining: ['Pay rent']",
            explanation: "pop() removes and returns an item. Without an argument, it removes the last item. With an index, it removes that specific item."
          },
          {
            type: "heading",
            text: "Other Useful List Methods"
          },
          {
            type: "code",
            title: "Example: Sorting and Reversing",
            code: "prices = [45, 12, 89, 23, 67]\nprint('Original:', prices)\n\nprices.sort()\nprint('Sorted:', prices)\n\nprices.reverse()\nprint('Reversed:', prices)",
            output: "Original: [45, 12, 89, 23, 67]\nSorted: [12, 23, 45, 67, 89]\nReversed: [89, 67, 45, 23, 12]",
            explanation: "sort() arranges items in order. reverse() flips the order."
          },
          {
            type: "code",
            title: "Example: Counting and Finding",
            code: "items = ['bread', 'milk', 'bread', 'eggs', 'bread']\n\nprint('Bread count:', items.count('bread'))\nprint('Milk position:', items.index('milk'))",
            output: "Bread count: 3\nMilk position: 1",
            explanation: "count() returns how many times a value appears. index() returns the position of the first occurrence."
          },
          {
            type: "tip",
            text: "Methods like append(), insert(), remove(), sort(), and reverse() modify the list in place—they don't return a new list."
          },
          {
            type: "keyPoints",
            points: [
              "append(item) adds to the end of the list",
              "insert(index, item) adds at a specific position",
              "remove(value) deletes the first occurrence of a value",
              "pop() removes and returns the last item (or item at specified index)",
              "sort() arranges items in order, reverse() flips the order",
              "count(value) returns how many times value appears",
              "index(value) returns the position of first occurrence"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does list.append('new') do?",
          options: [
            "Adds 'new' to the beginning",
            "Adds 'new' to the end",
            "Replaces all items with 'new'",
            "Inserts 'new' at position 1"
          ],
          correct: 1,
          explanation: "append() always adds items to the end of the list."
        },
        {
          question: "How do you add an item at the beginning of a list?",
          options: [
            "list.append(0, item)",
            "list.insert(0, item)",
            "list.prepend(item)",
            "list.add(item, 0)"
          ],
          correct: 1,
          explanation: "insert(0, item) puts the item at index 0 (the beginning)."
        },
        {
          question: "What's the difference between remove() and pop()?",
          options: [
            "No difference, they're the same",
            "remove() takes a value, pop() takes an index",
            "remove() is faster",
            "pop() doesn't actually delete the item"
          ],
          correct: 1,
          explanation: "remove() deletes by value (e.g., remove('bread')). pop() deletes by index and returns the removed item."
        },
        {
          question: "What happens if you call sort() on a list of strings?",
          options: [
            "Error - can't sort strings",
            "Sorts alphabetically",
            "Sorts by length",
            "Does nothing"
          ],
          correct: 1,
          explanation: "sort() works on strings and sorts them alphabetically (A-Z)."
        },
        {
          question: "If nums = [1,2,3,2,2], what does nums.count(2) return?",
          options: ["1", "2", "3", "5"],
          correct: 2,
          explanation: "count(2) returns 3 because the value 2 appears three times in the list."
        }
      ]
    }
  },

  18: {
    sections: [
      {
        title: "List Slicing: Working with Portions",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Often you don't need an entire list—just a portion of it. Like when a taxi takes the first 7 people in a queue, or when you need the last 5 transactions. Slicing lets you extract a continuous section of a list."
          },
          {
            type: "heading",
            text: "Basic Slicing Syntax"
          },
          {
            type: "paragraph",
            text: "Slicing uses the format list[start:stop]. It returns items from index 'start' up to (but not including) index 'stop'."
          },
          {
            type: "code",
            title: "Example: Basic Slicing",
            code: "queue = ['Person 1', 'Person 2', 'Person 3', 'Person 4', 'Person 5', 'Person 6', 'Person 7', 'Person 8']\n\nfirst_three = queue[0:3]\nprint('First three:', first_three)\n\nmiddle = queue[2:5]\nprint('Middle section:', middle)",
            output: "First three: ['Person 1', 'Person 2', 'Person 3']\nMiddle section: ['Person 3', 'Person 4', 'Person 5']",
            explanation: "queue[0:3] means 'start at index 0, stop before index 3' (so indices 0, 1, 2)"
          },
          {
            type: "heading",
            text: "Omitting Start or Stop"
          },
          {
            type: "paragraph",
            text: "You can leave out the start or stop index to mean 'from the beginning' or 'to the end'."
          },
          {
            type: "code",
            title: "Example: Shortcuts",
            code: "queue = ['Person 1', 'Person 2', 'Person 3', 'Person 4', 'Person 5']\n\nprint('First three:', queue[:3])    # Same as queue[0:3]\nprint('From index 2 onward:', queue[2:])  # From index 2 to end\nprint('Everything:', queue[:])      # Copy of the whole list",
            output: "First three: ['Person 1', 'Person 2', 'Person 3']\nFrom index 2 onward: ['Person 3', 'Person 4', 'Person 5']\nEverything: ['Person 1', 'Person 2', 'Person 3', 'Person 4', 'Person 5']",
            explanation: "Omitting start defaults to 0. Omitting stop defaults to end of list."
          },
          {
            type: "heading",
            text: "Negative Indices in Slicing"
          },
          {
            type: "code",
            title: "Example: Negative Slicing",
            code: "items = ['A', 'B', 'C', 'D', 'E', 'F']\n\nprint('Last three:', items[-3:])\nprint('Everything except last two:', items[:-2])",
            output: "Last three: ['D', 'E', 'F']\nEverything except last two: ['A', 'B', 'C', 'D']",
            explanation: "Negative indices work in slicing too. -3: means 'from third-to-last to end'. :-2 means 'beginning until (but not including) second-to-last'"
          },
          {
            type: "heading",
            text: "Step Size in Slicing"
          },
          {
            type: "paragraph",
            text: "You can add a third number to specify the step size: list[start:stop:step]. The default step is 1."
          },
          {
            type: "code",
            title: "Example: Every Second Item",
            code: "numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]\n\nprint('Every second number:', numbers[::2])\nprint('Every third from index 1:', numbers[1::3])\nprint('Reversed:', numbers[::-1])",
            output: "Every second number: [0, 2, 4, 6, 8]\nEvery third from index 1: [1, 4, 7]\nReversed: [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]",
            explanation: "[::2] means 'whole list, every 2nd item'. [::-1] means 'whole list, step backwards' (reverses the list)"
          },
          {
            type: "note",
            text: "Slicing creates a new list—it doesn't modify the original. The original list remains unchanged."
          },
          {
            type: "keyPoints",
            points: [
              "list[start:stop] gets items from start up to (not including) stop",
              "Omit start to slice from beginning, omit stop to slice to end",
              "Negative indices count from the end",
              "list[start:stop:step] lets you skip items with a step size",
              "list[::-1] is a quick way to reverse a list",
              "Slicing creates a new list—original is unchanged"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does nums[1:4] return if nums = [10, 20, 30, 40, 50]?",
          options: [
            "[10, 20, 30, 40]",
            "[20, 30, 40]",
            "[20, 30, 40, 50]",
            "[10, 20, 30]"
          ],
          correct: 1,
          explanation: "nums[1:4] returns indices 1, 2, 3 (stop before 4): [20, 30, 40]"
        },
        {
          question: "What does list[:3] mean?",
          options: [
            "Last 3 items",
            "First 3 items",
            "Every 3rd item",
            "Items at index 3"
          ],
          correct: 1,
          explanation: "[:3] means 'from beginning (index 0) up to (not including) index 3', which is the first 3 items."
        },
        {
          question: "How do you get the last 2 items of a list?",
          options: [
            "list[:-2]",
            "list[-2:]",
            "list[2:]",
            "list[:2]"
          ],
          correct: 1,
          explanation: "list[-2:] means 'from second-to-last to end', which gives the last 2 items."
        },
        {
          question: "What does list[::2] do?",
          options: [
            "Doubles each item",
            "Takes every second item",
            "Splits list in half",
            "Creates 2 copies"
          ],
          correct: 1,
          explanation: "[::2] means 'whole list, step by 2', so it takes every second item."
        },
        {
          question: "Does slicing modify the original list?",
          options: [
            "Yes, always",
            "No, it creates a new list",
            "Only if you use negative indices",
            "Depends on the Python version"
          ],
          correct: 1,
          explanation: "Slicing always creates a new list. The original list is never modified."
        }
      ]
    }
  },

  19: {
    sections: [
      {
        title: "List Comprehensions: Transforming Lists Efficiently",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Imagine having to calculate 20% off for 50 products. You could write a loop that goes through each price one by one, calculates the discount, and adds it to a new list. Or you could use a list comprehension—a one-line way to transform entire lists."
          },
          {
            type: "heading",
            text: "The Traditional Way: Loop + Append"
          },
          {
            type: "code",
            title: "Example: Loop Method",
            code: "prices = [100, 250, 50, 175, 80]\nsale_prices = []\n\nfor price in prices:\n    sale_price = price * 0.8  # 20% off\n    sale_prices.append(sale_price)\n\nprint(sale_prices)",
            output: "[80.0, 200.0, 40.0, 140.0, 64.0]",
            explanation: "This works, but requires 4 lines and explicit append() calls"
          },
          {
            type: "heading",
            text: "The List Comprehension Way"
          },
          {
            type: "paragraph",
            text: "A list comprehension does the same thing in one line. It follows the pattern: [expression for item in list]"
          },
          {
            type: "code",
            title: "Example: List Comprehension",
            code: "prices = [100, 250, 50, 175, 80]\nsale_prices = [price * 0.8 for price in prices]\n\nprint(sale_prices)",
            output: "[80.0, 200.0, 40.0, 140.0, 64.0]",
            explanation: "One line does the same work: transform each price and create a new list"
          },
          {
            type: "paragraph",
            text: "Read it like: 'Create a list of (price * 0.8) for each price in prices.'"
          },
          {
            type: "heading",
            text: "List Comprehensions with Conditions"
          },
          {
            type: "paragraph",
            text: "You can add an if condition to filter which items get included."
          },
          {
            type: "code",
            title: "Example: Filtering with if",
            code: "prices = [100, 250, 50, 175, 80]\nexpensive = [price for price in prices if price > 100]\n\nprint('Expensive items:', expensive)",
            output: "Expensive items: [250, 175]",
            explanation: "Only items where price > 100 are included in the new list"
          },
          {
            type: "code",
            title: "Example: Transform and Filter",
            code: "prices = [100, 250, 50, 175, 80]\n# Apply discount only to items over R100\ndiscounted = [price * 0.8 for price in prices if price > 100]\n\nprint(discounted)",
            output: "[200.0, 140.0]",
            explanation: "First filters (if price > 100), then transforms (price * 0.8)"
          },
          {
            type: "heading",
            text: "More List Comprehension Examples"
          },
          {
            type: "code",
            title: "Example: Working with Strings",
            code: "names = ['thandi', 'sipho', 'nomsa']\ncapitalized = [name.title() for name in names]\n\nprint(capitalized)",
            output: "['Thandi', 'Sipho', 'Nomsa']",
            explanation: "Applies .title() method to each name"
          },
          {
            type: "code",
            title: "Example: Generating Numbers",
            code: "squares = [x**2 for x in range(1, 6)]\nevens = [x for x in range(20) if x % 2 == 0]\n\nprint('Squares:', squares)\nprint('Evens:', evens)",
            output: "Squares: [1, 4, 9, 16, 25]\nEvens: [0, 2, 4, 6, 8, 10, 12, 14, 16, 18]",
            explanation: "First creates list of squares. Second creates list of even numbers 0-19."
          },
          {
            type: "tip",
            text: "List comprehensions are faster and more 'Pythonic' than loops for simple transformations. But if the logic is complex, a regular loop might be clearer."
          },
          {
            type: "keyPoints",
            points: [
              "List comprehensions create new lists in one line",
              "Basic syntax: [expression for item in list]",
              "With filtering: [expression for item in list if condition]",
              "They're more concise and often faster than loops",
              "Use them for simple transformations; use loops for complex logic",
              "List comprehensions don't modify the original list"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does [x * 2 for x in [1, 2, 3]] produce?",
          options: [
            "[1, 2, 3, 1, 2, 3]",
            "[2, 4, 6]",
            "[1, 4, 9]",
            "[3, 6, 9]"
          ],
          correct: 1,
          explanation: "Multiplies each item by 2: 1*2=2, 2*2=4, 3*2=6"
        },
        {
          question: "What does [x for x in range(5) if x > 2] produce?",
          options: [
            "[3, 4]",
            "[0, 1, 2]",
            "[2, 3, 4]",
            "[1, 2, 3, 4]"
          ],
          correct: 0,
          explanation: "range(5) gives [0,1,2,3,4]. Filter keeps only values > 2: [3, 4]"
        },
        {
          question: "Which is equivalent to: result = []\nfor x in nums:\n    result.append(x + 10)?",
          options: [
            "[x + 10 in nums]",
            "[x + 10 for x in nums]",
            "[x for x in nums + 10]",
            "[nums + 10]"
          ],
          correct: 1,
          explanation: "[x + 10 for x in nums] adds 10 to each item in nums"
        },
        {
          question: "What's the main advantage of list comprehensions?",
          options: [
            "They modify lists in place",
            "They're more concise and often faster",
            "They work with any data type",
            "They don't need square brackets"
          ],
          correct: 1,
          explanation: "List comprehensions are clearer, shorter, and usually faster than equivalent loops."
        },
        {
          question: "Can you have multiple for clauses in a list comprehension?",
          options: [
            "No, only one for allowed",
            "Yes, for nested iteration",
            "Only in Python 3",
            "Only with tuples"
          ],
          correct: 1,
          explanation: "You can write [x+y for x in list1 for y in list2] for nested iteration (though it gets harder to read)."
        }
      ]
    }
  },

  20: {
    sections: [
      {
        title: "Iterating Over Lists: Processing Each Item",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Like Mr. Mthembu calling attendance, programs constantly need to go through lists doing the same thing to each item: send an email to each address, check each product for stock, verify each transaction. This is called iteration."
          },
          {
            type: "heading",
            text: "The for Loop with Lists"
          },
          {
            type: "paragraph",
            text: "The most common way to process each item in a list is with a for loop. Python automatically goes through the list from first to last."
          },
          {
            type: "code",
            title: "Example: Basic Iteration",
            code: "students = ['Andile', 'Buhle', 'Cebo', 'Dintle']\n\nfor student in students:\n    print(f'{student} - Present')",
            output: "Andile - Present\nBuhle - Present\nCebo - Present\nDintle - Present",
            explanation: "The loop automatically handles each item, one at a time"
          },
          {
            type: "heading",
            text: "Iterating with Index: enumerate()"
          },
          {
            type: "paragraph",
            text: "Sometimes you need both the item AND its position. The enumerate() function gives you both."
          },
          {
            type: "code",
            title: "Example: Using enumerate()",
            code: "students = ['Andile', 'Buhle', 'Cebo']\n\nfor index, student in enumerate(students):\n    print(f'{index + 1}. {student}')",
            output: "1. Andile\n2. Buhle\n3. Cebo",
            explanation: "enumerate() provides both the index and the item. We add 1 because indices start at 0 but we want numbering from 1."
          },
          {
            type: "code",
            title: "Example: Starting enumerate() at 1",
            code: "students = ['Andile', 'Buhle', 'Cebo']\n\nfor number, student in enumerate(students, start=1):\n    print(f'{number}. {student}')",
            output: "1. Andile\n2. Buhle\n3. Cebo",
            explanation: "enumerate(list, start=1) makes the counter start at 1 instead of 0"
          },
          {
            type: "heading",
            text: "Modifying Lists During Iteration"
          },
          {
            type: "paragraph",
            text: "You can modify list items while iterating, but be careful about adding or removing items during iteration—that can cause unexpected behavior."
          },
          {
            type: "code",
            title: "Example: Modifying Items",
            code: "prices = [100, 50, 75, 200]\n\n# Apply 10% discount to each\nfor i in range(len(prices)):\n    prices[i] = prices[i] * 0.9\n\nprint(prices)",
            output: "[90.0, 45.0, 67.5, 180.0]",
            explanation: "Using range(len(prices)) gives us indices, so we can modify items in place"
          },
          {
            type: "warning",
            text: "Avoid removing items from a list while iterating over it directly. This causes the iteration to skip items. Instead, create a new list or iterate over a copy."
          },
          {
            type: "heading",
            text: "Iterating Over Multiple Lists: zip()"
          },
          {
            type: "code",
            title: "Example: Parallel Iteration with zip()",
            code: "names = ['Thandi', 'Sipho', 'Nomsa']\nages = [23, 31, 28]\n\nfor name, age in zip(names, ages):\n    print(f'{name} is {age} years old')",
            output: "Thandi is 23 years old\nSipho is 31 years old\nNomsa is 28 years old",
            explanation: "zip() combines multiple lists, pairing up items at the same position"
          },
          {
            type: "keyPoints",
            points: [
              "for item in list: processes each item in order",
              "enumerate(list) provides both index and item",
              "Use enumerate(list, start=1) to start counting from 1",
              "Modify items using range(len(list)) to get indices",
              "zip(list1, list2) lets you iterate over multiple lists together",
              "Avoid removing items while iterating—create a new list instead"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does 'for item in mylist:' do?",
          options: [
            "Checks if 'item' is in mylist",
            "Processes each item in mylist one by one",
            "Creates a copy of mylist",
            "Sorts mylist"
          ],
          correct: 1,
          explanation: "The for loop iterates through each item in the list, processing them sequentially."
        },
        {
          question: "What does enumerate(['A', 'B', 'C']) provide?",
          options: [
            "Just the items: A, B, C",
            "Just the indices: 0, 1, 2",
            "Both: (0,'A'), (1,'B'), (2,'C')",
            "The length of the list"
          ],
          correct: 2,
          explanation: "enumerate() returns pairs of (index, item) for each element."
        },
        {
          question: "How do you start enumerate() counting from 1 instead of 0?",
          options: [
            "enumerate(list, 1)",
            "enumerate(list, start=1)",
            "enumerate(list, begin=1)",
            "enumerate(list) + 1"
          ],
          correct: 1,
          explanation: "Use enumerate(list, start=1) to begin counting from 1."
        },
        {
          question: "What's the safest way to modify list items while iterating?",
          options: [
            "for item in list:",
            "for i in range(len(list)):",
            "while iterating backwards",
            "You can't modify items while iterating"
          ],
          correct: 1,
          explanation: "Using indices (range(len(list))) lets you safely modify items by their position."
        },
        {
          question: "What does zip([1,2], ['a','b']) produce?",
          options: [
            "[1, 2, 'a', 'b']",
            "[(1,'a'), (2,'b')]",
            "{'1':'a', '2':'b'}",
            "Error - can't zip numbers and strings"
          ],
          correct: 1,
          explanation: "zip() pairs up elements from each list: (1,'a') and (2,'b')"
        }
      ]
    }
  },

  21: {
    sections: [
      {
        title: "Tuples: Immutable Sequences",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Sometimes you have data that belongs together and should never change. Like GPS coordinates (latitude, longitude) or RGB colors (red, green, blue)—you don't want to accidentally modify just one part. Tuples are like lists, but immutable: once created, they can't be changed."
          },
          {
            type: "heading",
            text: "Creating Tuples"
          },
          {
            type: "paragraph",
            text: "Tuples use parentheses () instead of square brackets. They can hold any data types, like lists."
          },
          {
            type: "code",
            title: "Example: Creating Tuples",
            code: "coordinates = (-29.8587, 31.0218)\nrgb_color = (255, 100, 50)\ndate = (2024, 3, 15)\n\nprint(coordinates)\nprint(type(coordinates))",
            output: "(-29.8587, 31.0218)\n<class 'tuple'>",
            explanation: "Parentheses create tuples. Like lists, they maintain order and can contain any types."
          },
          {
            type: "note",
            text: "Single-item tuples need a comma: (5,) not (5). Without the comma, Python treats it as just a number in parentheses."
          },
          {
            type: "heading",
            text: "Accessing Tuple Items"
          },
          {
            type: "paragraph",
            text: "Tuples work like lists for accessing items: use indices, negative indices, and slicing."
          },
          {
            type: "code",
            title: "Example: Indexing Tuples",
            code: "coordinates = (-29.8587, 31.0218)\n\nlatitude = coordinates[0]\nlongitude = coordinates[1]\n\nprint(f'Lat: {latitude}, Long: {longitude}')",
            output: "Lat: -29.8587, Long: 31.0218",
            explanation: "Access tuple items the same way as lists, using indices"
          },
          {
            type: "heading",
            text: "Tuple Immutability"
          },
          {
            type: "paragraph",
            text: "The key difference: you cannot change tuple items after creation. No modifying, adding, or removing."
          },
          {
            type: "code",
            title: "Example: Tuples Can't Be Modified",
            code: "date = (2024, 3, 15)\n# This would cause an error:\n# date[1] = 4  # Can't modify!\n\nprint('Original date:', date)\nprint('Date is protected from accidental changes')",
            output: "Original date: (2024, 3, 15)\nDate is protected from accidental changes",
            explanation: "Attempting date[1] = 4 would raise: TypeError: 'tuple' object does not support item assignment"
          },
          {
            type: "heading",
            text: "Tuple Unpacking"
          },
          {
            type: "paragraph",
            text: "A powerful feature of tuples is unpacking: assigning multiple variables at once."
          },
          {
            type: "code",
            title: "Example: Unpacking Tuples",
            code: "coordinates = (-29.8587, 31.0218)\nlat, long = coordinates\n\nprint(f'Latitude: {lat}')\nprint(f'Longitude: {long}')",
            output: "Latitude: -29.8587\nLongitude: 31.0218",
            explanation: "Unpacking assigns each tuple value to a variable in one line"
          },
          {
            type: "code",
            title: "Example: Swapping Values",
            code: "a = 10\nb = 20\nprint(f'Before: a={a}, b={b}')\n\na, b = b, a\nprint(f'After: a={a}, b={b}')",
            output: "Before: a=10, b=20\nAfter: a=20, b=10",
            explanation: "Tuple unpacking makes swapping values elegant and clear"
          },
          {
            type: "heading",
            text: "When to Use Tuples vs Lists"
          },
          {
            type: "paragraph",
            text: "Use tuples when data shouldn't change (coordinates, dates, RGB colors). Use lists when data needs to grow, shrink, or be modified (shopping lists, task lists)."
          },
          {
            type: "keyPoints",
            points: [
              "Tuples use parentheses (), lists use square brackets []",
              "Tuples are immutable—can't be modified after creation",
              "Access items with indices, just like lists",
              "Tuple unpacking: lat, long = (x, y) assigns both at once",
              "Use tuples for fixed data that belongs together",
              "Use lists when data needs to change",
              "Single-item tuple needs comma: (5,) not (5)"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What's the main difference between lists and tuples?",
          options: [
            "Lists are faster",
            "Tuples are immutable (can't be changed)",
            "Tuples can only hold numbers",
            "Lists use () and tuples use []"
          ],
          correct: 1,
          explanation: "Tuples cannot be modified after creation—they're immutable. Lists can be changed."
        },
        {
          question: "How do you create a single-item tuple?",
          options: [
            "(5)",
            "[5]",
            "(5,)",
            "tuple(5)"
          ],
          correct: 2,
          explanation: "Single-item tuples need a comma: (5,). Without it, (5) is just a number in parentheses."
        },
        {
          question: "What does x, y = (10, 20) do?",
          options: [
            "Creates a tuple with x and y",
            "Assigns 10 to x and 20 to y",
            "Adds 10 and 20",
            "Error - invalid syntax"
          ],
          correct: 1,
          explanation: "This is tuple unpacking: x gets 10, y gets 20."
        },
        {
          question: "Can you append items to a tuple?",
          options: [
            "Yes, using tuple.append()",
            "Yes, using tuple += item",
            "No, tuples are immutable",
            "Only if created with ()"
          ],
          correct: 2,
          explanation: "Tuples are immutable—you cannot add, remove, or modify items."
        },
        {
          question: "When should you use a tuple instead of a list?",
          options: [
            "When you need to save memory",
            "When data shouldn't change (like coordinates)",
            "When working with numbers",
            "Never, lists are always better"
          ],
          correct: 1,
          explanation: "Use tuples for fixed data that belongs together and shouldn't be modified."
        }
      ]
    }
  },

  22: {
    sections: [
      {
        title: "Dictionaries: Key-Value Pairs",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Imagine looking up a phone number. You don't flip through every page—you find the person's name and read the number next to it. Dictionaries work the same way: they store pairs of keys (like names) and values (like phone numbers), letting you look up information directly."
          },
          {
            type: "heading",
            text: "Creating Dictionaries"
          },
          {
            type: "paragraph",
            text: "Dictionaries use curly braces {} with key-value pairs separated by colons. Each key maps to exactly one value."
          },
          {
            type: "code",
            title: "Example: Creating a Dictionary",
            code: "contacts = {\n    'Mama': '073 555 1234',\n    'Doctor': '011 555 9876',\n    'Landlord': '082 555 4321'\n}\n\nprint(contacts)\nprint(type(contacts))",
            output: "{'Mama': '073 555 1234', 'Doctor': '011 555 9876', 'Landlord': '082 555 4321'}\n<class 'dict'>",
            explanation: "Keys are strings, values are strings. The format is {key: value, key: value}"
          },
          {
            type: "heading",
            text: "Accessing Values"
          },
          {
            type: "paragraph",
            text: "Use square brackets with the key to get the corresponding value."
          },
          {
            type: "code",
            title: "Example: Looking Up Values",
            code: "contacts = {'Mama': '073 555 1234', 'Doctor': '011 555 9876'}\n\nprint(contacts['Mama'])\nprint(contacts['Doctor'])",
            output: "073 555 1234\n011 555 9876",
            explanation: "Use the key (like 'Mama') to get its value (the phone number)"
          },
          {
            type: "heading",
            text: "Adding and Modifying Items"
          },
          {
            type: "paragraph",
            text: "Unlike tuples, dictionaries are mutable. You can add new key-value pairs or change existing values."
          },
          {
            type: "code",
            title: "Example: Adding Items",
            code: "contacts = {'Mama': '073 555 1234'}\nprint('Before:', contacts)\n\ncontacts['Friend'] = '082 555 7777'\nprint('After adding:', contacts)\n\ncontacts['Mama'] = '073 555 9999'\nprint('After updating:', contacts)",
            output: "Before: {'Mama': '073 555 1234'}\nAfter adding: {'Mama': '073 555 1234', 'Friend': '082 555 7777'}\nAfter updating: {'Mama': '073 555 9999', 'Friend': '082 555 7777'}",
            explanation: "If key doesn't exist, it's added. If key exists, value is updated."
          },
          {
            type: "heading",
            text: "Checking if a Key Exists"
          },
          {
            type: "paragraph",
            text: "Use the 'in' keyword to check if a key exists before accessing it."
          },
          {
            type: "code",
            title: "Example: Checking Keys",
            code: "prices = {'bread': 15, 'milk': 25, 'eggs': 40}\n\nif 'bread' in prices:\n    print(f'Bread costs R{prices[\"bread\"]}')\n\nif 'butter' in prices:\n    print('Butter found')\nelse:\n    print('Butter not in stock')",
            output: "Bread costs R15\nButter not in stock",
            explanation: "'bread' is a key in the dictionary, but 'butter' is not"
          },
          {
            type: "heading",
            text: "Dictionary with Different Value Types"
          },
          {
            type: "code",
            title: "Example: Mixed Value Types",
            code: "customer = {\n    'name': 'Thandi Mthembu',\n    'age': 28,\n    'active': True,\n    'balance': 1240.50\n}\n\nprint(f\"{customer['name']} is {customer['age']} years old\")\nprint(f\"Balance: R{customer['balance']}\")",
            output: "Thandi Mthembu is 28 years old\nBalance: R1240.5",
            explanation: "Dictionary values can be any type: strings, numbers, booleans, even other dictionaries or lists"
          },
          {
            type: "note",
            text: "Dictionary keys must be immutable types (strings, numbers, tuples). You can't use lists or other dictionaries as keys."
          },
          {
            type: "keyPoints",
            points: [
              "Dictionaries store key-value pairs using {key: value} syntax",
              "Access values with square brackets: dict[key]",
              "Add or update: dict[key] = value",
              "Check if key exists: if key in dict:",
              "Keys must be immutable (strings, numbers, tuples)",
              "Values can be any type",
              "Dictionaries are unordered (Python 3.7+ maintains insertion order)"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What's the main purpose of dictionaries?",
          options: [
            "Store items in order",
            "Map keys to values for fast lookup",
            "Store only numbers",
            "Replace lists"
          ],
          correct: 1,
          explanation: "Dictionaries map keys to values, allowing fast direct lookup like a phone book."
        },
        {
          question: "How do you create an empty dictionary?",
          options: [
            "[]",
            "()",
            "{}",
            "dict[]"
          ],
          correct: 2,
          explanation: "{} creates an empty dictionary. [] creates an empty list."
        },
        {
          question: "What happens if you access a key that doesn't exist?",
          options: [
            "Returns None",
            "Returns empty string",
            "Raises KeyError",
            "Creates the key"
          ],
          correct: 2,
          explanation: "Accessing a non-existent key raises KeyError. Use 'in' to check first, or use dict.get()."
        },
        {
          question: "Can dictionary values be lists?",
          options: [
            "Yes",
            "No",
            "Only if keys are strings",
            "Only in Python 3"
          ],
          correct: 0,
          explanation: "Dictionary values can be any type, including lists, other dictionaries, or any object."
        },
        {
          question: "Which is NOT a valid dictionary key?",
          options: [
            "'name'",
            "123",
            "(1, 2)",
            "[1, 2]"
          ],
          correct: 3,
          explanation: "Lists [1,2] can't be keys because they're mutable. Keys must be immutable (strings, numbers, tuples)."
        }
      ]
    }
  },

  23: {
    sections: [
      {
        title: "Dictionary Methods: Working with Key-Value Data",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Mandla updating his delivery address book throughout the day, dictionaries need methods to add, remove, update, and search through their data efficiently."
          },
          {
            type: "heading",
            text: "Getting Values Safely with get()"
          },
          {
            type: "paragraph",
            text: "The get() method is safer than using dict[key] because it doesn't crash if the key doesn't exist."
          },
          {
            type: "code",
            title: "Example: Using get()",
            code: "addresses = {'Thandi': '12 Main Rd', 'Sipho': '45 Church St'}\n\nprint(addresses.get('Thandi'))\nprint(addresses.get('Nomsa'))  # Key doesn't exist\nprint(addresses.get('Nomsa', 'Address not found'))",
            output: "12 Main Rd\nNone\nAddress not found",
            explanation: "get() returns None if key doesn't exist, or you can provide a default value"
          },
          {
            type: "heading",
            text: "Getting All Keys, Values, or Items"
          },
          {
            type: "code",
            title: "Example: keys(), values(), items()",
            code: "prices = {'bread': 15, 'milk': 25, 'eggs': 40}\n\nprint('Keys:', list(prices.keys()))\nprint('Values:', list(prices.values()))\nprint('Items:', list(prices.items()))",
            output: "Keys: ['bread', 'milk', 'eggs']\nValues: [15, 25, 40]\nItems: [('bread', 15), ('milk', 25), ('eggs', 40)]",
            explanation: "keys() returns all keys, values() returns all values, items() returns key-value pairs as tuples"
          },
          {
            type: "heading",
            text: "Iterating Over Dictionaries"
          },
          {
            type: "code",
            title: "Example: Looping Through Dictionary",
            code: "prices = {'bread': 15, 'milk': 25, 'eggs': 40}\n\nfor item, price in prices.items():\n    print(f'{item}: R{price}')",
            output: "bread: R15\nmilk: R25\neggs: R40",
            explanation: "items() lets you loop through both keys and values at once"
          },
          {
            type: "heading",
            text: "Removing Items"
          },
          {
            type: "paragraph",
            text: "Use pop() to remove a key and get its value, or del to just remove."
          },
          {
            type: "code",
            title: "Example: Removing Items",
            code: "inventory = {'apples': 50, 'bananas': 30, 'oranges': 20}\nprint('Before:', inventory)\n\nremoved = inventory.pop('bananas')\nprint(f'Removed {removed} bananas')\nprint('After:', inventory)",
            output: "Before: {'apples': 50, 'bananas': 30, 'oranges': 20}\nRemoved 30 bananas\nAfter: {'apples': 50, 'oranges': 20}",
            explanation: "pop(key) removes the key and returns its value"
          },
          {
            type: "heading",
            text: "Updating Dictionaries"
          },
          {
            type: "paragraph",
            text: "The update() method adds multiple key-value pairs at once, or updates existing ones."
          },
          {
            type: "code",
            title: "Example: Using update()",
            code: "inventory = {'apples': 50, 'bananas': 30}\nprint('Before:', inventory)\n\nnew_stock = {'oranges': 40, 'apples': 70}\ninventory.update(new_stock)\nprint('After:', inventory)",
            output: "Before: {'apples': 50, 'bananas': 30}\nAfter: {'apples': 70, 'bananas': 30, 'oranges': 40}",
            explanation: "update() adds new keys (oranges) and updates existing keys (apples)"
          },
          {
            type: "heading",
            text: "Clearing and Copying"
          },
          {
            type: "code",
            title: "Example: clear() and copy()",
            code: "original = {'a': 1, 'b': 2}\nbackup = original.copy()\n\noriginal.clear()\nprint('Original:', original)\nprint('Backup:', backup)",
            output: "Original: {}\nBackup: {'a': 1, 'b': 2}",
            explanation: "clear() removes all items. copy() creates an independent copy."
          },
          {
            type: "tip",
            text: "Use get() instead of dict[key] when you're not sure if a key exists. It prevents KeyError crashes."
          },
          {
            type: "keyPoints",
            points: [
              "get(key, default) safely retrieves values without errors",
              "keys() returns all keys, values() returns all values",
              "items() returns (key, value) tuples for iteration",
              "pop(key) removes and returns a value",
              "update(other_dict) adds/updates multiple items",
              "clear() removes all items",
              "copy() creates an independent copy"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What's the advantage of get() over dict[key]?",
          options: [
            "It's faster",
            "It doesn't raise KeyError for missing keys",
            "It returns all values",
            "No advantage"
          ],
          correct: 1,
          explanation: "get() returns None (or a default value) if key doesn't exist, instead of raising KeyError."
        },
        {
          question: "What does items() return?",
          options: [
            "Just the keys",
            "Just the values",
            "(key, value) pairs",
            "The number of items"
          ],
          correct: 2,
          explanation: "items() returns tuples of (key, value) pairs, useful for iteration."
        },
        {
          question: "What does pop('key') do?",
          options: [
            "Returns the value only",
            "Deletes the key only",
            "Removes key and returns its value",
            "Checks if key exists"
          ],
          correct: 2,
          explanation: "pop(key) removes the key-value pair and returns the value."
        },
        {
          question: "If d = {'a': 1} and you do d.update({'b': 2, 'a': 3}), what's d?",
          options: [
            "{'a': 1}",
            "{'b': 2, 'a': 3}",
            "{'a': 3, 'b': 2}",
            "Error"
          ],
          correct: 2,
          explanation: "update() adds new keys (b) and updates existing ones (a becomes 3)."
        },
        {
          question: "How do you loop through both keys and values?",
          options: [
            "for k, v in dict:",
            "for k, v in dict.items():",
            "for k, v in dict.pairs():",
            "for dict[k, v]:"
          ],
          correct: 1,
          explanation: "for key, value in dict.items(): unpacks each (key, value) tuple."
        }
      ]
    }
  },

  24: {
    sections: [
      {
        title: "Sets: Unique Collections",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Imagine Thembi receiving ingredient orders from multiple customers. Three people order onions, two want tomatoes, four need salt. She doesn't care how many times something was requested—she just needs to know which unique ingredients to buy. Sets automatically handle uniqueness."
          },
          {
            type: "heading",
            text: "What Are Sets?"
          },
          {
            type: "paragraph",
            text: "A set is an unordered collection of unique items. Duplicates are automatically removed."
          },
          {
            type: "code",
            title: "Example: Creating Sets",
            code: "orders = {'onions', 'tomatoes', 'onions', 'salt', 'tomatoes', 'onions'}\nprint(orders)\nprint(type(orders))",
            output: "{'onions', 'tomatoes', 'salt'}\n<class 'set'>",
            explanation: "Even though 'onions' appeared 3 times, the set contains it only once. Sets use curly braces like dictionaries, but without key-value pairs."
          },
          {
            type: "code",
            title: "Example: Creating Set from List",
            code: "numbers = [1, 2, 3, 2, 1, 4, 5, 4, 3]\nunique = set(numbers)\nprint('List:', numbers)\nprint('Set:', unique)",
            output: "List: [1, 2, 3, 2, 1, 4, 5, 4, 3]\nSet: {1, 2, 3, 4, 5}",
            explanation: "set() converts a list to a set, automatically removing duplicates"
          },
          {
            type: "heading",
            text: "Adding and Removing Items"
          },
          {
            type: "code",
            title: "Example: Adding to Sets",
            code: "ingredients = {'onions', 'tomatoes'}\nprint('Before:', ingredients)\n\ningredients.add('salt')\ningredients.add('onions')  # Already exists - won't be added again\nprint('After:', ingredients)",
            output: "Before: {'onions', 'tomatoes'}\nAfter: {'onions', 'tomatoes', 'salt'}",
            explanation: "add() adds an item, but duplicates are ignored"
          },
          {
            type: "code",
            title: "Example: Removing from Sets",
            code: "ingredients = {'onions', 'tomatoes', 'salt'}\n\ningredients.remove('salt')\nprint('After remove:', ingredients)\n\ningredients.discard('pepper')  # Doesn't exist, but no error\nprint('After discard:', ingredients)",
            output: "After remove: {'onions', 'tomatoes'}\nAfter discard: {'onions', 'tomatoes'}",
            explanation: "remove() raises error if item doesn't exist. discard() does not."
          },
          {
            type: "heading",
            text: "Set Operations"
          },
          {
            type: "paragraph",
            text: "Sets support mathematical operations: union (combine), intersection (common items), difference (items in one but not the other)."
          },
          {
            type: "code",
            title: "Example: Set Operations",
            code: "customer1 = {'onions', 'tomatoes', 'salt'}\ncustomer2 = {'tomatoes', 'pepper', 'garlic'}\n\nall_ingredients = customer1.union(customer2)\ncommon = customer1.intersection(customer2)\nonly_customer1 = customer1.difference(customer2)\n\nprint('All:', all_ingredients)\nprint('Common:', common)\nprint('Only in customer1:', only_customer1)",
            output: "All: {'onions', 'tomatoes', 'salt', 'pepper', 'garlic'}\nCommon: {'tomatoes'}\nOnly in customer1: {'onions', 'salt'}",
            explanation: "union combines both, intersection finds common items, difference finds items in first set but not second"
          },
          {
            type: "heading",
            text: "Checking Membership"
          },
          {
            type: "code",
            title: "Example: Testing Membership",
            code: "available = {'bread', 'milk', 'eggs', 'cheese'}\n\nif 'milk' in available:\n    print('We have milk')\n\nif 'butter' not in available:\n    print('Butter is out of stock')",
            output: "We have milk\nButter is out of stock",
            explanation: "'in' checks if an item exists in the set (very fast operation)"
          },
          {
            type: "note",
            text: "Sets are unordered—items don't have positions. You can't use indexing like set[0]."
          },
          {
            type: "keyPoints",
            points: [
              "Sets store unique items only—duplicates are automatically removed",
              "Use {} or set() to create sets",
              "add() adds an item, remove() or discard() removes",
              "union() combines sets, intersection() finds common items",
              "difference() finds items in one set but not another",
              "Sets are unordered—no indexing",
              "Membership testing (in) is very fast in sets"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What happens to duplicates in a set?",
          options: [
            "They're kept but hidden",
            "They cause an error",
            "They're automatically removed",
            "They're converted to strings"
          ],
          correct: 2,
          explanation: "Sets automatically remove duplicates, keeping only unique values."
        },
        {
          question: "Can you access set items by index like set[0]?",
          options: [
            "Yes",
            "No, sets are unordered",
            "Only if sorted first",
            "Only in Python 3"
          ],
          correct: 1,
          explanation: "Sets are unordered collections, so they don't support indexing."
        },
        {
          question: "What does {1,2,3}.union({3,4,5}) return?",
          options: [
            "{3}",
            "{1,2,3,4,5}",
            "{1,2,4,5}",
            "Error"
          ],
          correct: 1,
          explanation: "union() combines both sets, keeping all unique values: {1,2,3,4,5}"
        },
        {
          question: "What's the difference between remove() and discard()?",
          options: [
            "No difference",
            "remove() raises error if item doesn't exist, discard() doesn't",
            "discard() is faster",
            "remove() works only on sets"
          ],
          correct: 1,
          explanation: "remove() raises KeyError if item doesn't exist. discard() silently does nothing."
        },
        {
          question: "What does {1,2,3}.intersection({2,3,4}) return?",
          options: [
            "{1,2,3,4}",
            "{2,3}",
            "{1,4}",
            "{}"
          ],
          correct: 1,
          explanation: "intersection() returns items present in both sets: {2, 3}"
        }
      ]
    }
  },

  25: {
    sections: [
      {
        title: "Choosing the Right Collection",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Like Lungile the store manager choosing between clipboards, notebooks, and filing cabinets for different types of records, programmers must choose the right data structure for each situation. Each collection type has strengths and trade-offs."
          },
          {
            type: "heading",
            text: "When to Use Lists"
          },
          {
            type: "paragraph",
            text: "Lists are the workhorse of Python. Use them when you need ordered, changeable collections."
          },
          {
            type: "list",
            items: [
              "Order matters (like steps in a recipe, tasks in sequence)",
              "You need to modify items (add, remove, change)",
              "Duplicates are meaningful (like recording multiple sales of the same item)",
              "You need to access items by position (first, last, or index)"
            ]
          },
          {
            type: "code",
            title: "Example: List Use Case",
            code: "# Daily tasks must be done in order\ntasks = ['Open shop', 'Count cash', 'Check stock', 'Serve customers', 'Close shop']\n\nfor i, task in enumerate(tasks, 1):\n    print(f'{i}. {task}')",
            output: "1. Open shop\n2. Count cash\n3. Check stock\n4. Serve customers\n5. Close shop",
            explanation: "Order matters, so a list is perfect"
          },
          {
            type: "heading",
            text: "When to Use Tuples"
          },
          {
            type: "paragraph",
            text: "Use tuples when data should stay together and never change."
          },
          {
            type: "list",
            items: [
              "Fixed data that belongs together (coordinates, RGB colors)",
              "Data that shouldn't be modified (dates, configuration)",
              "Dictionary keys (lists can't be keys, but tuples can)",
              "Function returns multiple values"
            ]
          },
          {
            type: "code",
            title: "Example: Tuple Use Case",
            code: "# GPS locations should never be partially modified\nlocations = [\n    ('Store 1', -29.8587, 31.0218),\n    ('Store 2', -26.2041, 28.0473),\n    ('Store 3', -33.9249, 18.4241)\n]\n\nfor name, lat, lon in locations:\n    print(f'{name}: {lat}, {lon}')",
            output: "Store 1: -29.8587, 31.0218\nStore 2: -26.2041, 28.0473\nStore 3: -33.9249, 18.4241",
            explanation: "Each coordinate pair is protected from accidental modification"
          },
          {
            type: "heading",
            text: "When to Use Dictionaries"
          },
          {
            type: "paragraph",
            text: "Dictionaries are perfect for labeled data and lookups."
          },
          {
            type: "list",
            items: [
              "Data has natural labels (names, IDs, product codes)",
              "You need fast lookup by key",
              "Data is pairs: username→password, product→price",
              "You need to store structured records"
            ]
          },
          {
            type: "code",
            title: "Example: Dictionary Use Case",
            code: "# Product prices - lookup by name\nprices = {\n    'bread': 15,\n    'milk': 25,\n    'eggs': 40,\n    'cheese': 60\n}\n\nitem = 'milk'\nif item in prices:\n    print(f'{item}: R{prices[item]}')",
            output: "milk: R25",
            explanation: "Direct lookup by product name—faster than searching through a list"
          },
          {
            type: "heading",
            text: "When to Use Sets"
          },
          {
            type: "paragraph",
            text: "Sets excel at uniqueness and membership testing."
          },
          {
            type: "list",
            items: [
              "You only care about unique items (no duplicates)",
              "You need fast membership testing (is this item present?)",
              "You need set operations (union, intersection, difference)",
              "Order doesn't matter"
            ]
          },
          {
            type: "code",
            title: "Example: Set Use Case",
            code: "# Track which items were sold today (duplicates don't matter)\nitems_sold_today = set()\nitems_sold_today.add('bread')\nitems_sold_today.add('milk')\nitems_sold_today.add('bread')  # Duplicate - ignored\n\nprint(f'Unique items sold: {len(items_sold_today)}')\nprint(items_sold_today)",
            output: "Unique items sold: 2\n{'bread', 'milk'}",
            explanation: "Set automatically tracks unique items, perfect for this use case"
          },
          {
            type: "heading",
            text: "Quick Reference"
          },
          {
            type: "keyPoints",
            points: [
              "List: ordered, changeable, allows duplicates → [item1, item2]",
              "Tuple: ordered, unchangeable, allows duplicates → (item1, item2)",
              "Dictionary: unordered (3.7+: insertion order), changeable, no duplicate keys → {key1: value1}",
              "Set: unordered, changeable, no duplicates → {item1, item2}",
              "Default choice: start with list, switch if you need specific features",
              "Multiple types can coexist: list of tuples, dict of sets, etc."
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "You need to store customer names in the order they arrived. Which collection?",
          options: ["Set", "Tuple", "List", "Dictionary"],
          correct: 2,
          explanation: "Lists maintain order and allow modification (adding new arrivals), perfect for this."
        },
        {
          question: "You need to store RGB color as (255, 100, 50) that shouldn't change. Which collection?",
          options: ["List", "Tuple", "Set", "Dictionary"],
          correct: 1,
          explanation: "Tuples are immutable and perfect for fixed grouped data like RGB values."
        },
        {
          question: "You need to look up phone numbers by person's name. Which collection?",
          options: ["List of tuples", "Dictionary", "Set", "Tuple of lists"],
          correct: 1,
          explanation: "Dictionary maps names to phone numbers, allowing fast direct lookup."
        },
        {
          question: "You need to track which unique items were ordered (duplicates don't matter). Which collection?",
          options: ["List", "Tuple", "Dictionary", "Set"],
          correct: 3,
          explanation: "Sets automatically handle uniqueness, perfect when you just need to know what items exist."
        },
        {
          question: "Can you have a list containing tuples?",
          options: [
            "Yes",
            "No",
            "Only if tuples are empty",
            "Only in Python 3"
          ],
          correct: 0,
          explanation: "Collections can contain other collections: lists of tuples, dicts of lists, etc. are all valid."
        }
      ]
    }
  },
// Levels 26-40: Functions, Files, Exceptions, Projects

  26: {
    sections: [
      {
        title: "Defining Functions: Reusable Code Blocks",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Siphiwe who repairs the same types of problems repeatedly—checking fuses, testing switches, replacing bulbs—programs often need to perform the same operations multiple times. Functions let you write code once and use it many times."
          },
          {
            type: "heading",
            text: "What Are Functions?"
          },
          {
            type: "paragraph",
            text: "A function is a named block of code that performs a specific task. Instead of writing the same code repeatedly, you write it once in a function and call it whenever needed."
          },
          {
            type: "code",
            title: "Example: Defining a Simple Function",
            code: "def greet():\n    print('Sawubona!')\n    print('Welcome to our shop.')\n\n# Call the function\ngreet()\nprint()\ngreet()  # Can call it multiple times",
            output: "Sawubona!\nWelcome to our shop.\n\nSawubona!\nWelcome to our shop.",
            explanation: "def creates a function. The indented code runs each time you call the function."
          },
          {
            type: "note",
            text: "Function definition (def) doesn't run the code—it just creates the function. You must call it explicitly with () to execute."
          },
          {
            type: "heading",
            text: "Why Use Functions?"
          },
          {
            type: "list",
            items: [
              "Avoid repetition: Write once, use many times",
              "Organization: Group related code together",
              "Testing: Test one function at a time",
              "Maintenance: Fix bugs in one place",
              "Readability: Give meaningful names to operations"
            ]
          },
          {
            type: "code",
            title: "Example: Real-World Function",
            code: "def check_stock_level(quantity):\n    if quantity < 10:\n        print('⚠️ Low stock - reorder needed')\n    else:\n        print('✓ Stock level OK')\n\n# Use the function for different items\ncheck_stock_level(5)\ncheck_stock_level(25)\ncheck_stock_level(8)",
            output: "⚠️ Low stock - reorder needed\n✓ Stock level OK\n⚠️ Low stock - reorder needed",
            explanation: "Same logic applied to different values—this is the power of functions"
          },
          {
            type: "heading",
            text: "Function Naming Rules"
          },
          {
            type: "paragraph",
            text: "Function names follow the same rules as variables: lowercase with underscores, descriptive, start with letter."
          },
          {
            type: "list",
            items: [
              "Use verbs: calculate_total(), send_message(), check_password()",
              "Be descriptive: process_order() not do_stuff()",
              "Lowercase with underscores: check_stock_level not CheckStockLevel",
              "Avoid Python keywords: def, if, for, etc."
            ]
          },
          {
            type: "code",
            title: "Example: Multiple Functions",
            code: "def morning_routine():\n    print('1. Count cash')\n    print('2. Check stock')\n    print('3. Open doors')\n\ndef evening_routine():\n    print('1. Count register')\n    print('2. Lock doors')\n    print('3. Set alarm')\n\nprint('=== Morning ===')\nmorning_routine()\nprint('\\n=== Evening ===')\nevening_routine()",
            output: "=== Morning ===\n1. Count cash\n2. Check stock\n3. Open doors\n\n=== Evening ===\n1. Count register\n2. Lock doors\n3. Set alarm",
            explanation: "Each function encapsulates a distinct set of operations"
          },
          {
            type: "keyPoints",
            points: [
              "Functions group related code into reusable blocks",
              "def function_name(): defines a function",
              "Call functions with function_name() to execute",
              "Functions reduce repetition and improve organization",
              "Use descriptive verb-based names",
              "Functions must be defined before you call them"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What keyword defines a function?",
          options: ["function", "def", "define", "func"],
          correct: 1,
          explanation: "def is the Python keyword for defining functions."
        },
        {
          question: "When does a function's code actually run?",
          options: [
            "When it's defined with def",
            "When the file loads",
            "When you call it with ()",
            "Automatically at the end of the program"
          ],
          correct: 2,
          explanation: "Defining a function doesn't run it—you must call it explicitly with () to execute."
        },
        {
          question: "What's a benefit of using functions?",
          options: [
            "Makes programs run faster",
            "Reduces repetition and improves organization",
            "Uses less memory",
            "Prevents all errors"
          ],
          correct: 1,
          explanation: "Functions reduce code repetition and make programs more organized and maintainable."
        },
        {
          question: "Which is a good function name?",
          options: [
            "calculate",
            "calculateTotalPrice",
            "calculate_total_price",
            "CalculateTotalPrice"
          ],
          correct: 2,
          explanation: "Python convention: lowercase_with_underscores, descriptive verb names."
        },
        {
          question: "Can you define multiple functions in one program?",
          options: [
            "No, only one function allowed",
            "Yes, as many as needed",
            "Maximum of 10",
            "Only if they have different names"
          ],
          correct: 1,
          explanation: "You can define as many functions as you need, each with its own unique name."
        }
      ]
    }
  },

  27: {
    sections: [
      {
        title: "Function Parameters: Passing Information",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Nosipho's taxi fare calculator that needs different distances and passenger counts each time, functions often need input data to work with. Parameters let you pass information into functions."
          },
          {
            type: "heading",
            text: "Functions with Parameters"
          },
          {
            type: "paragraph",
            text: "Parameters are variables defined in the function definition. When you call the function, you provide actual values (called arguments)."
          },
          {
            type: "code",
            title: "Example: Function with One Parameter",
            code: "def greet_customer(name):\n    print(f'Sawubona, {name}!')\n    print('Welcome to our shop.')\n\ngreet_customer('Thandi')\ngreet_customer('Sipho')",
            output: "Sawubona, Thandi!\nWelcome to our shop.\nSawubona, Sipho!\nWelcome to our shop.",
            explanation: "name is a parameter. 'Thandi' and 'Sipho' are arguments passed in."
          },
          {
            type: "heading",
            text: "Multiple Parameters"
          },
          {
            type: "paragraph",
            text: "Functions can accept multiple parameters, separated by commas."
          },
          {
            type: "code",
            title: "Example: Calculate Taxi Fare",
            code: "def calculate_fare(distance, passengers):\n    base_fare = distance * 12\n    total = base_fare * passengers\n    print(f'{distance}km, {passengers} passengers: R{total}')\n\ncalculate_fare(5, 2)\ncalculate_fare(10, 1)\ncalculate_fare(3, 4)",
            output: "5km, 2 passengers: R120\n10km, 1 passengers: R120\n3km, 4 passengers: R144",
            explanation: "Function takes two parameters and uses both in its calculation"
          },
          {
            type: "heading",
            text: "Default Parameter Values"
          },
          {
            type: "paragraph",
            text: "You can provide default values for parameters. If the caller doesn't provide a value, the default is used."
          },
          {
            type: "code",
            title: "Example: Default Parameters",
            code: "def calculate_total(price, tax_rate=0.15):\n    tax = price * tax_rate\n    total = price + tax\n    print(f'Price: R{price}, Tax: R{tax:.2f}, Total: R{total:.2f}')\n\ncalculate_total(100)      # Uses default tax_rate\ncalculate_total(100, 0.20)  # Override tax_rate",
            output: "Price: R100, Tax: R15.00, Total: R115.00\nPrice: R100, Tax: R20.00, Total: R120.00",
            explanation: "tax_rate defaults to 0.15 but can be overridden"
          },
          {
            type: "heading",
            text: "Keyword Arguments"
          },
          {
            type: "paragraph",
            text: "You can pass arguments by name (keyword arguments), making code clearer and order-independent."
          },
          {
            type: "code",
            title: "Example: Keyword Arguments",
            code: "def create_order(item, quantity, urgent=False):\n    status = 'URGENT' if urgent else 'Normal'\n    print(f'{status}: {quantity}x {item}')\n\n# Positional arguments\ncreate_order('Bread', 5)\n\n# Keyword arguments (order doesn't matter)\ncreate_order(quantity=10, item='Milk', urgent=True)",
            output: "Normal: 5x Bread\nURGENT: 10x Milk",
            explanation: "Keyword arguments make the code more readable and flexible"
          },
          {
            type: "heading",
            text: "Common Mistakes"
          },
          {
            type: "code",
            title: "Example: Argument Count Must Match",
            code: "def add_three_numbers(a, b, c):\n    return a + b + c\n\nresult = add_three_numbers(5, 10, 15)\nprint(result)\n\n# This would cause an error:\n# add_three_numbers(5, 10)  # Missing one argument!",
            output: "30",
            explanation: "Function expects exactly 3 arguments (unless defaults are provided)"
          },
          {
            type: "tip",
            text: "Use default parameters for optional settings. Required data should be positional parameters without defaults."
          },
          {
            type: "keyPoints",
            points: [
              "Parameters are variables in the function definition",
              "Arguments are actual values passed when calling",
              "Multiple parameters: def func(param1, param2, param3):",
              "Default values: def func(param=default_value):",
              "Keyword arguments: func(param2=value, param1=value)",
              "Number of arguments must match unless defaults are provided"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What's the difference between parameters and arguments?",
          options: [
            "No difference, same thing",
            "Parameters are in definition, arguments are values passed in",
            "Parameters are numbers, arguments are strings",
            "Arguments come first, parameters second"
          ],
          correct: 1,
          explanation: "Parameters are variables in the function definition. Arguments are the actual values you pass when calling."
        },
        {
          question: "What's the output of def greet(name='Guest'): print(name) called with greet()?",
          options: ["Error", "Guest", "name", "None"],
          correct: 1,
          explanation: "Default parameter value 'Guest' is used when no argument is provided."
        },
        {
          question: "Can you call func(b=2, a=1) if defined as def func(a, b)?",
          options: [
            "No, order must match",
            "Yes, keyword arguments can be in any order",
            "Only in Python 3",
            "Only if both have defaults"
          ],
          correct: 1,
          explanation: "Keyword arguments can be passed in any order—Python matches by name."
        },
        {
          question: "If def calc(x, y=5): return x + y, what's calc(10)?",
          options: ["15", "10", "5", "Error"],
          correct: 0,
          explanation: "x=10, y uses default 5, so 10 + 5 = 15"
        },
        {
          question: "Which parameter should have a default value?",
          options: [
            "All of them",
            "None of them",
            "Optional parameters only",
            "The first one"
          ],
          correct: 2,
          explanation: "Default values are for optional parameters. Required data shouldn't have defaults."
        }
      ]
    }
  },

  28: {
    sections: [
      {
        title: "Return Values: Getting Results Back",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Buhle's currency converter that calculates the conversion but needs to give you the answer, functions often need to send results back to the code that called them. The return statement does this."
          },
          {
            type: "heading",
            text: "Functions Without Return"
          },
          {
            type: "paragraph",
            text: "So far, our functions have printed results directly. But what if you want to use the result in further calculations?"
          },
          {
            type: "code",
            title: "Example: Function That Only Prints",
            code: "def calculate_total(price, quantity):\n    total = price * quantity\n    print(f'Total: R{total}')\n\n# This prints, but we can't use the result\ncalculate_total(50, 3)\n\n# Can't do this:\n# result = calculate_total(50, 3) + 100  # Can't add to None",
            output: "Total: R150",
            explanation: "Function prints but doesn't return a value we can use"
          },
          {
            type: "heading",
            text: "Using return to Send Values Back"
          },
          {
            type: "code",
            title: "Example: Function That Returns",
            code: "def calculate_total(price, quantity):\n    total = price * quantity\n    return total\n\n# Now we can capture and use the result\norder1 = calculate_total(50, 3)\norder2 = calculate_total(25, 2)\ngrand_total = order1 + order2\n\nprint(f'Order 1: R{order1}')\nprint(f'Order 2: R{order2}')\nprint(f'Grand Total: R{grand_total}')",
            output: "Order 1: R150\nOrder 2: R50\nGrand Total: R200",
            explanation: "return sends the value back, allowing us to use it in other calculations"
          },
          {
            type: "heading",
            text: "Returning Multiple Values"
          },
          {
            type: "paragraph",
            text: "Functions can return multiple values as a tuple. Python automatically unpacks them."
          },
          {
            type: "code",
            title: "Example: Multiple Return Values",
            code: "def calculate_order(price, quantity, tax_rate=0.15):\n    subtotal = price * quantity\n    tax = subtotal * tax_rate\n    total = subtotal + tax\n    return subtotal, tax, total\n\n# Unpack the three returned values\nsubtotal, tax, total = calculate_order(100, 2)\n\nprint(f'Subtotal: R{subtotal}')\nprint(f'Tax: R{tax}')\nprint(f'Total: R{total}')",
            output: "Subtotal: R200\nTax: R30.0\nTotal: R230.0",
            explanation: "Function returns three values as a tuple, which we unpack into three variables"
          },
          {
            type: "heading",
            text: "Early Return"
          },
          {
            type: "paragraph",
            text: "return immediately exits the function. Code after return doesn't run."
          },
          {
            type: "code",
            title: "Example: Early Return for Validation",
            code: "def divide_numbers(a, b):\n    if b == 0:\n        print('Error: Cannot divide by zero')\n        return None  # Exit early\n    \n    result = a / b\n    return result\n\nprint(divide_numbers(10, 2))\nprint(divide_numbers(10, 0))",
            output: "5.0\nError: Cannot divide by zero\nNone",
            explanation: "When b is 0, function returns early with None. The division never happens."
          },
          {
            type: "heading",
            text: "Return vs Print"
          },
          {
            type: "paragraph",
            text: "This is a common confusion: print displays to the screen, return sends a value back to the caller."
          },
          {
            type: "code",
            title: "Example: Return vs Print",
            code: "def print_result(x):\n    print(x * 2)\n\ndef return_result(x):\n    return x * 2\n\n# With print: just displays\nprint_result(5)  # Shows: 10\n\n# With return: can use the value\nresult = return_result(5)\nprint(f'Result: {result}')\nprint(f'Double: {result * 2}')",
            output: "10\nResult: 10\nDouble: 20",
            explanation: "print is for humans. return is for code that needs the value."
          },
          {
            type: "tip",
            text: "If a function doesn't explicitly return a value, Python returns None automatically."
          },
          {
            type: "keyPoints",
            points: [
              "return sends a value back to the caller",
              "Without return, functions return None",
              "Can return multiple values: return val1, val2, val3",
              "return immediately exits the function",
              "Use return for values your code needs, print for human-readable output",
              "Returned values can be used in further calculations"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does a function return if it has no return statement?",
          options: ["0", "Empty string", "None", "Error"],
          correct: 2,
          explanation: "Functions without explicit return statements automatically return None."
        },
        {
          question: "What's the difference between print() and return?",
          options: [
            "No difference",
            "print displays to screen, return sends value to caller",
            "return is faster",
            "print works only in functions"
          ],
          correct: 1,
          explanation: "print shows output to humans. return sends a value back to code that can use it."
        },
        {
          question: "Can a function have multiple return statements?",
          options: [
            "No, only one allowed",
            "Yes, but only the first one that's reached executes",
            "Only in Python 3",
            "Yes, all of them execute"
          ],
          correct: 1,
          explanation: "Functions can have multiple returns (e.g., in different if branches), but only one executes per call."
        },
        {
          question: "What happens to code after a return statement?",
          options: [
            "It still runs",
            "It doesn't run—return exits the function",
            "It runs if return returns None",
            "Depends on the value returned"
          ],
          correct: 1,
          explanation: "return immediately exits the function. Any code after it is not executed."
        },
        {
          question: "How do you return two values from a function?",
          options: [
            "return value1 return value2",
            "return value1, value2",
            "return [value1, value2]",
            "Can't return two values"
          ],
          correct: 1,
          explanation: "return value1, value2 returns them as a tuple, which can be unpacked."
        }
      ]
    }
  },

  29: {
    sections: [
      {
        title: "Variable Scope: Local vs Global",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "Like Nomsa's kitchen where some ingredients are private to one dish while others are shared, variables in programs have scope—the region where they're visible and can be used."
          },
          {
            type: "heading",
            text: "Local Variables"
          },
          {
            type: "paragraph",
            text: "Variables created inside a function are local—they only exist within that function and disappear when it finishes."
          },
          {
            type: "code",
            title: "Example: Local Variables",
            code: "def calculate_order():\n    price = 100  # Local variable\n    quantity = 3\n    total = price * quantity\n    print(f'Inside function: {total}')\n\ncalculate_order()\n\n# This would cause an error:\n# print(price)  # NameError: price not defined outside function",
            output: "Inside function: 300",
            explanation: "price, quantity, and total only exist inside calculate_order()"
          },
          {
            type: "heading",
            text: "Global Variables"
          },
          {
            type: "paragraph",
            text: "Variables created outside all functions are global—they're visible everywhere in your code."
          },
          {
            type: "code",
            title: "Example: Global Variables",
            code: "# Global variable\nshop_name = 'Thandi\\'s Spaza'\n\ndef print_receipt():\n    # Can access global variable\n    print(f'Receipt from: {shop_name}')\n\ndef print_welcome():\n    print(f'Welcome to {shop_name}')\n\nprint_receipt()\nprint_welcome()",
            output: "Receipt from: Thandi's Spaza\nWelcome to Thandi's Spaza",
            explanation: "shop_name is global—both functions can access it"
          },
          {
            type: "heading",
            text: "Local Variables Shadow Globals"
          },
          {
            type: "paragraph",
            text: "If a function creates a local variable with the same name as a global one, the local version takes priority inside that function."
          },
          {
            type: "code",
            title: "Example: Local Shadows Global",
            code: "tax_rate = 0.15  # Global\n\ndef calculate_special_order():\n    tax_rate = 0.20  # Local variable with same name\n    print(f'Inside function: {tax_rate}')\n\ncalculate_special_order()\nprint(f'Outside function: {tax_rate}')",
            output: "Inside function: 0.2\nOutside function: 0.15",
            explanation: "Local tax_rate doesn't affect global tax_rate"
          },
          {
            type: "heading",
            text: "Modifying Global Variables"
          },
          {
            type: "paragraph",
            text: "To modify a global variable from inside a function, you must declare it global (but this is generally discouraged—use parameters and return values instead)."
          },
          {
            type: "code",
            title: "Example: Using global Keyword",
            code: "counter = 0  # Global\n\ndef increment():\n    global counter  # Declare we're modifying the global\n    counter += 1\n\nprint(f'Before: {counter}')\nincrement()\nincrement()\nprint(f'After: {counter}')",
            output: "Before: 0\nAfter: 2",
            explanation: "global keyword lets function modify the global variable"
          },
          {
            type: "warning",
            text: "Modifying global variables from functions can make code hard to understand and debug. Prefer passing parameters and returning values."
          },
          {
            type: "heading",
            text: "Best Practices"
          },
          {
            type: "list",
            items: [
              "Use local variables for temporary calculations",
              "Use global variables for constants (values that never change)",
              "Pass data into functions as parameters",
              "Get data out of functions with return",
              "Avoid modifying global variables from within functions",
              "Keep functions self-contained and predictable"
            ]
          },
          {
            type: "keyPoints",
            points: [
              "Local variables exist only inside their function",
              "Global variables exist throughout the program",
              "Local variables disappear when function ends",
              "Functions can read global variables",
              "Use global keyword to modify global variables (discouraged)",
              "Prefer parameters and return values over global variables"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "Where can a local variable be accessed?",
          options: [
            "Anywhere in the program",
            "Only inside the function where it's created",
            "Only at the top of the file",
            "Only in other functions"
          ],
          correct: 1,
          explanation: "Local variables exist only within the function where they're defined."
        },
        {
          question: "Can a function read a global variable?",
          options: [
            "Yes, automatically",
            "No, never",
            "Only with global keyword",
            "Only if passed as parameter"
          ],
          correct: 0,
          explanation: "Functions can read global variables without any special keyword."
        },
        {
          question: "What happens if a local variable has the same name as a global one?",
          options: [
            "Error occurs",
            "Local variable shadows (hides) the global inside that function",
            "Global is deleted",
            "Both exist with same value"
          ],
          correct: 1,
          explanation: "Local variable takes priority inside the function, but global remains unchanged outside."
        },
        {
          question: "What's the preferred way to get data into a function?",
          options: [
            "Use global variables",
            "Pass as parameters",
            "Create local copies",
            "Use input()"
          ],
          correct: 1,
          explanation: "Parameters are the clean, standard way to pass data into functions."
        },
        {
          question: "When do local variables get deleted?",
          options: [
            "Never",
            "When the program ends",
            "When the function returns/ends",
            "When you call del"
          ],
          correct: 2,
          explanation: "Local variables are automatically deleted when the function finishes executing."
        }
      ]
    }
  },

  30: {
    sections: [
      {
        title: "Built-in Functions: Python's Ready-Made Tools",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Musa using his phone's calculator instead of building one, Python comes with dozens of pre-made functions that solve common problems. These are called built-in functions—they're always available without importing anything."
          },
          {
            type: "heading",
            text: "Common Built-in Functions You Already Know"
          },
          {
            type: "paragraph",
            text: "You've been using built-in functions all along: print(), input(), len(), type(). Let's explore more."
          },
          {
            type: "code",
            title: "Example: Functions You Know",
            code: "name = 'Thandi'\nage = input('Enter age: ')  # Simulated: assume user enters 28\nage = 28  # For this example\n\nprint(name)\nprint(f'Length: {len(name)}')\nprint(f'Type: {type(age)}')",
            output: "Thandi\nLength: 6\nType: <class 'int'>",
            explanation: "print(), input(), len(), type() are all built-in functions"
          },
          {
            type: "heading",
            text: "Math Functions"
          },
          {
            type: "code",
            title: "Example: Basic Math Functions",
            code: "numbers = [45, 12, 89, 23, 67]\n\nprint(f'Smallest: {min(numbers)}')\nprint(f'Largest: {max(numbers)}')\nprint(f'Sum: {sum(numbers)}')\nprint(f'Count: {len(numbers)}')\nprint(f'Average: {sum(numbers) / len(numbers):.1f}')",
            output: "Smallest: 12\nLargest: 89\nSum: 236\nCount: 5\nAverage: 47.2",
            explanation: "min(), max(), sum(), and len() are essential for working with numbers"
          },
          {
            type: "code",
            title: "Example: abs() and round()",
            code: "balance = -150.50\nprint(f'Absolute value: {abs(balance)}')\n\nprice = 123.456\nprint(f'Rounded: {round(price)}')\nprint(f'Rounded to 2 decimals: {round(price, 2)}')",
            output: "Absolute value: 150.5\nRounded: 123\nRounded to 2 decimals: 123.46",
            explanation: "abs() removes negative sign, round() rounds numbers"
          },
          {
            type: "heading",
            text: "Type Conversion Functions"
          },
          {
            type: "code",
            title: "Example: Converting Types",
            code: "# String to number\nage_str = '25'\nage_num = int(age_str)\nprint(f'{age_num} + 5 = {age_num + 5}')\n\n# Number to string\nscore = 98\nscore_str = str(score)\nprint(f'Score: {score_str}/100')\n\n# To float\nprice = float('123.45')\nprint(f'Price: R{price}')",
            output: "25 + 5 = 30\nScore: 98/100\nPrice: R123.45",
            explanation: "int(), str(), float() convert between types"
          },
          {
            type: "heading",
            text: "Checking and Comparing"
          },
          {
            type: "code",
            title: "Example: all() and any()",
            code: "# Check if all items meet a condition\nprices = [10, 20, 30, 40]\nall_positive = all(price > 0 for price in prices)\nprint(f'All prices positive: {all_positive}')\n\n# Check if any item meets a condition\nexpensive = [10, 20, 150, 30]\nhas_expensive = any(price > 100 for price in expensive)\nprint(f'Has expensive items: {has_expensive}')",
            output: "All prices positive: True\nHas expensive items: True",
            explanation: "all() checks if all are True, any() checks if at least one is True"
          },
          {
            type: "heading",
            text: "Working with Sequences"
          },
          {
            type: "code",
            title: "Example: sorted() and reversed()",
            code: "prices = [45, 12, 89, 23]\n\nprint(f'Original: {prices}')\nprint(f'Sorted: {sorted(prices)}')\nprint(f'Reversed: {list(reversed(prices))}')\nprint(f'Original unchanged: {prices}')",
            output: "Original: [45, 12, 89, 23]\nSorted: [12, 23, 45, 89]\nReversed: [23, 89, 12, 45]\nOriginal unchanged: [45, 12, 89, 23]",
            explanation: "sorted() and reversed() create new sequences—original is unchanged"
          },
          {
            type: "heading",
            text: "Useful Built-in Functions"
          },
          {
            type: "list",
            items: [
              "len(sequence) - length of list, string, etc.",
              "min(sequence) / max(sequence) - smallest/largest value",
              "sum(sequence) - total of all numbers",
              "abs(number) - absolute value (removes negative)",
              "round(number, digits) - round to specified decimal places",
              "sorted(sequence) - returns sorted copy",
              "reversed(sequence) - returns reversed iterator",
              "int(), float(), str() - type conversions",
              "all(sequence) - True if all items are True",
              "any(sequence) - True if at least one item is True"
            ]
          },
          {
            type: "keyPoints",
            points: [
              "Built-in functions are always available—no import needed",
              "Use len(), min(), max(), sum() for sequences",
              "Use abs(), round() for numbers",
              "Use int(), float(), str() for type conversion",
              "sorted() and reversed() create new sequences",
              "all() and any() check conditions across sequences",
              "These functions save time and make code more readable"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does len([1, 2, 3, 4]) return?",
          options: ["10", "4", "3", "[1, 2, 3, 4]"],
          correct: 1,
          explanation: "len() returns the number of items in the sequence: 4"
        },
        {
          question: "What's the difference between round(3.7) and int(3.7)?",
          options: [
            "No difference",
            "round() gives 4, int() gives 3",
            "round() gives 3, int() gives 4",
            "Both give 4"
          ],
          correct: 1,
          explanation: "round() rounds to nearest: 4. int() truncates (removes decimal): 3"
        },
        {
          question: "Do you need to import built-in functions?",
          options: [
            "Yes, always",
            "No, they're always available",
            "Only in Python 3",
            "Only the math functions"
          ],
          correct: 1,
          explanation: "Built-in functions are always available without any import statement."
        },
        {
          question: "What does any([False, False, True, False]) return?",
          options: ["False", "True", "[True]", "3"],
          correct: 1,
          explanation: "any() returns True if at least one item is True."
        },
        {
          question: "Does sorted() modify the original list?",
          options: [
            "Yes, it sorts in place",
            "No, it returns a new sorted list",
            "Only if you use sorted(list, modify=True)",
            "Depends on Python version"
          ],
          correct: 1,
          explanation: "sorted() returns a new sorted list. Original remains unchanged."
        }
      ]
    }
  },

  31: {
    sections: [
      {
        title: "String Methods: Manipulating Text",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Thandiwe cleaning up inconsistent name entries at the clinic, programs constantly need to manipulate text: convert case, remove spaces, search for words, replace information. String methods make this easy."
          },
          {
            type: "heading",
            text: "Case Conversion Methods"
          },
          {
            type: "code",
            title: "Example: Changing Case",
            code: "name = '  LINDA NKOSI  '\n\nprint(f'Original: \"{name}\"')\nprint(f'Lower: \"{name.lower()}\"')\nprint(f'Upper: \"{name.upper()}\"')\nprint(f'Title: \"{name.title()}\"')\nprint(f'Strip + Title: \"{name.strip().title()}\"')",
            output: 'Original: "  LINDA NKOSI  "\nLower: "  linda nkosi  "\nUpper: "  LINDA NKOSI  "\nTitle: "  Linda Nkosi  "\nStrip + Title: "Linda Nkosi"',
            explanation: "lower(), upper(), title() change case. Notice strip() removes leading/trailing spaces."
          },
          {
            type: "heading",
            text: "Removing Whitespace"
          },
          {
            type: "code",
            title: "Example: strip(), lstrip(), rstrip()",
            code: "text = '   Hello World   '\n\nprint(f'Original: \"{text}\"')\nprint(f'strip(): \"{text.strip()}\"')\nprint(f'lstrip(): \"{text.lstrip()}\"')  # Left strip\nprint(f'rstrip(): \"{text.rstrip()}\"')  # Right strip",
            output: 'Original: "   Hello World   "\nstrip(): "Hello World"\nlstrip(): "Hello World   "\nrstrip(): "   Hello World"',
            explanation: "strip() removes spaces from both ends. lstrip() removes from left, rstrip() from right."
          },
          {
            type: "heading",
            text: "Searching and Checking"
          },
          {
            type: "code",
            title: "Example: Checking String Content",
            code: "email = 'thandi@example.com'\n\nprint(f'Contains @: {\"@\" in email}')\nprint(f'Starts with \"thandi\": {email.startswith(\"thandi\")}')\nprint(f'Ends with \".com\": {email.endswith(\".com\")}')\nprint(f'Find @ position: {email.find(\"@\")}')",
            output: "Contains @: True\nStarts with \"thandi\": True\nEnds with \".com\": True\nFind @ position: 6",
            explanation: "startswith(), endswith() check beginning/end. find() returns position (or -1 if not found)."
          },
          {
            type: "heading",
            text: "Replacing Text"
          },
          {
            type: "code",
            title: "Example: replace() Method",
            code: "phone = '082-555-1234'\nprint(f'Original: {phone}')\n\n# Remove dashes\nclean = phone.replace('-', '')\nprint(f'Without dashes: {clean}')\n\n# Replace part\nmessage = 'Hello NAME, welcome!'\npersonalized = message.replace('NAME', 'Thandi')\nprint(personalized)",
            output: "Original: 082-555-1234\nWithout dashes: 0825551234\nHello Thandi, welcome!",
            explanation: "replace(old, new) replaces all occurrences of old with new"
          },
          {
            type: "heading",
            text: "Splitting and Joining"
          },
          {
            type: "code",
            title: "Example: split() and join()",
            code: "# Split string into list\nsentence = 'Bread Milk Eggs Cheese'\nitems = sentence.split()\nprint(f'Split: {items}')\n\n# Split by specific character\ndate = '2024-03-15'\nparts = date.split('-')\nprint(f'Date parts: {parts}')\n\n# Join list into string\nwords = ['Hello', 'world']\njoined = ' '.join(words)\nprint(f'Joined: {joined}')",
            output: "Split: ['Bread', 'Milk', 'Eggs', 'Cheese']\nDate parts: ['2024', '03', '15']\nJoined: Hello world",
            explanation: "split() breaks string into list. join() combines list items into string."
          },
          {
            type: "heading",
            text: "Checking String Types"
          },
          {
            type: "code",
            title: "Example: Type Checking Methods",
            code: "print('\"123\".isdigit():', '123'.isdigit())\nprint('\"abc\".isalpha():', 'abc'.isalpha())\nprint('\"abc123\".isalnum():', 'abc123'.isalnum())\nprint('\"   \".isspace():', '   '.isspace())",
            output: '"123".isdigit(): True\n"abc".isalpha(): True\n"abc123".isalnum(): True\n"   ".isspace(): True',
            explanation: "isdigit() checks for numbers, isalpha() for letters, isalnum() for letters/numbers, isspace() for whitespace"
          },
          {
            type: "tip",
            text: "String methods return new strings—they don't modify the original. Strings are immutable."
          },
          {
            type: "keyPoints",
            points: [
              "lower(), upper(), title() change case",
              "strip(), lstrip(), rstrip() remove whitespace",
              "startswith(), endswith(), find() search strings",
              "replace(old, new) substitutes text",
              "split() breaks strings into lists",
              "join() combines list items into strings",
              "isdigit(), isalpha(), isalnum() check content type",
              "String methods return new strings—originals unchanged"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does '  hello  '.strip() return?",
          options: ["'  hello'", "'hello  '", "'hello'", "'  hello  '"],
          correct: 2,
          explanation: "strip() removes leading and trailing whitespace: 'hello'"
        },
        {
          question: "What does 'Python'.lower() return?",
          options: ["'python'", "'PYTHON'", "'Python'", "Error"],
          correct: 0,
          explanation: "lower() converts all characters to lowercase: 'python'"
        },
        {
          question: "What does 'a-b-c'.split('-') return?",
          options: ["'abc'", "['a', 'b', 'c']", "['a-b-c']", "'a b c'"],
          correct: 1,
          explanation: "split('-') breaks the string at each '-', returning a list: ['a', 'b', 'c']"
        },
        {
          question: "Do string methods modify the original string?",
          options: [
            "Yes, always",
            "No, strings are immutable—methods return new strings",
            "Only upper() and lower()",
            "Depends on the method"
          ],
          correct: 1,
          explanation: "Strings are immutable. Methods return new strings; originals remain unchanged."
        },
        {
          question: "What does 'hello'.replace('l', 'L') return?",
          options: ["'heLlo'", "'heLLo'", "'hello'", "'HELLO'"],
          correct: 1,
          explanation: "replace() replaces ALL occurrences of 'l' with 'L': 'heLLo'"
        }
      ]
    }
  },

  32: {
    sections: [
      {
        title: "Modules and Imports: Using Python's Libraries",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Bongani borrowing his neighbor's socket wrench instead of buying his own, programmers use modules—pre-written code libraries—to access functionality without reinventing the wheel."
          },
          {
            type: "heading",
            text: "What Are Modules?"
          },
          {
            type: "paragraph",
            text: "A module is a file containing Python code (functions, variables, classes) that you can use in your own programs. Python comes with many built-in modules, and you can also install third-party modules."
          },
          {
            type: "heading",
            text: "Importing Modules"
          },
          {
            type: "code",
            title: "Example: Importing math Module",
            code: "import math\n\nprint(f'Pi: {math.pi}')\nprint(f'Square root of 16: {math.sqrt(16)}')\nprint(f'5 squared: {math.pow(5, 2)}')",
            output: "Pi: 3.141592653589793\nSquare root of 16: 4.0\n5 squared: 25.0",
            explanation: "import math makes the math module available. Access its contents with math.name"
          },
          {
            type: "heading",
            text: "Import Specific Functions"
          },
          {
            type: "code",
            title: "Example: from...import",
            code: "from math import sqrt, pi\n\n# Can use directly without math. prefix\nprint(f'Pi: {pi}')\nprint(f'Square root of 25: {sqrt(25)}')",
            output: "Pi: 3.141592653589793\nSquare root of 25: 5.0",
            explanation: "from math import sqrt, pi imports only specific items, usable without prefix"
          },
          {
            type: "heading",
            text: "Import with Alias"
          },
          {
            type: "code",
            title: "Example: Using as for Shorter Names",
            code: "import math as m\n\nprint(f'Pi: {m.pi}')\nprint(f'Ceiling of 3.2: {m.ceil(3.2)}')\nprint(f'Floor of 3.8: {m.floor(3.8)}')",
            output: "Pi: 3.141592653589793\nCeiling of 3.2: 4\nFloor of 3.8: 3",
            explanation: "as lets you create a shorter alias for the module name"
          },
          {
            type: "heading",
            text: "Common Useful Modules"
          },
          {
            type: "code",
            title: "Example: random Module",
            code: "import random\n\n# Random integer between 1 and 6 (like dice)\nprint(f'Dice roll: {random.randint(1, 6)}')\n\n# Random choice from list\noptions = ['Rock', 'Paper', 'Scissors']\nprint(f'Computer chose: {random.choice(options)}')",
            output: "Dice roll: 4\nComputer chose: Paper",
            explanation: "random module provides functions for generating random values"
          },
          {
            type: "code",
            title: "Example: datetime Module",
            code: "from datetime import datetime\n\nnow = datetime.now()\nprint(f'Current date/time: {now}')\nprint(f'Year: {now.year}')\nprint(f'Month: {now.month}')\nprint(f'Day: {now.day}')",
            output: "Current date/time: 2024-03-15 14:30:45.123456\nYear: 2024\nMonth: 3\nDay: 15",
            explanation: "datetime module works with dates and times"
          },
          {
            type: "heading",
            text: "Import Best Practices"
          },
          {
            type: "list",
            items: [
              "Put all imports at the top of your file",
              "Import standard library modules first, third-party modules second",
              "Use import module_name when you need many things from a module",
              "Use from module import item for just one or two specific items",
              "Avoid from module import * (imports everything—unclear what comes from where)"
            ]
          },
          {
            type: "code",
            title: "Example: Good Import Style",
            code: "# Good: Clear and organized\nimport math\nimport random\nfrom datetime import datetime\n\nprint(f'Random number: {random.randint(1, 100)}')\nprint(f'Square root: {math.sqrt(16)}')\nprint(f'Today: {datetime.now().date()}')",
            output: "Random number: 42\nSquare root: 4.0\nToday: 2024-03-15",
            explanation: "All imports at top, clear what comes from where"
          },
          {
            type: "keyPoints",
            points: [
              "Modules are files containing reusable Python code",
              "import module_name imports entire module",
              "from module import item imports specific items",
              "Use as to create shorter aliases",
              "Common modules: math, random, datetime",
              "Put imports at top of file",
              "Avoid from module import * (import everything)"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does import math do?",
          options: [
            "Runs the math module",
            "Makes math module available for use",
            "Downloads the math module",
            "Creates a math variable"
          ],
          correct: 1,
          explanation: "import makes the module available so you can use its functions and variables."
        },
        {
          question: "After import math, how do you use the sqrt function?",
          options: [
            "sqrt(16)",
            "math.sqrt(16)",
            "import.sqrt(16)",
            "math(sqrt(16))"
          ],
          correct: 1,
          explanation: "Use module.function format: math.sqrt(16)"
        },
        {
          question: "What's the difference between import math and from math import sqrt?",
          options: [
            "No difference",
            "First imports whole module, second imports just sqrt",
            "First is faster",
            "Second is deprecated"
          ],
          correct: 1,
          explanation: "import math imports everything (use math.sqrt). from math import sqrt imports just sqrt (use sqrt directly)."
        },
        {
          question: "What does import random as rnd do?",
          options: [
            "Error - invalid syntax",
            "Creates alias 'rnd' for random module",
            "Renames the module permanently",
            "Imports only random numbers"
          ],
          correct: 1,
          explanation: "as creates an alias so you can use rnd instead of random"
        },
        {
          question: "Where should import statements go?",
          options: [
            "Anywhere in the file",
            "At the top of the file",
            "Before each function",
            "At the end"
          ],
          correct: 1,
          explanation: "Best practice: put all imports at the top of your file"
        }
      ]
    }
  },

  33: {
    sections: [
      {
        title: "Reading Files: Loading Saved Data",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Sipho opening his ledger book to see yesterday's sales, programs need to read information saved in files. Files preserve data between program runs—even after turning off the computer."
          },
          {
            type: "heading",
            text: "Opening and Reading Files"
          },
          {
            type: "paragraph",
            text: "To read a file, you must: 1) open it, 2) read the contents, 3) close it. Python's with statement handles opening and closing automatically."
          },
          {
            type: "code",
            title: "Example: Reading Entire File",
            code: "# Assume 'sales.txt' contains:\n# Monday: R450\n# Tuesday: R780\n# Wednesday: R620\n\nwith open('sales.txt', 'r') as file:\n    content = file.read()\n    print(content)",
            output: "Monday: R450\nTuesday: R780\nWednesday: R620",
            explanation: "'r' means read mode. read() gets all content as one string. with automatically closes file."
          },
          {
            type: "heading",
            text: "Reading Line by Line"
          },
          {
            type: "code",
            title: "Example: Reading Lines",
            code: "# Read file line by line\nwith open('sales.txt', 'r') as file:\n    for line in file:\n        print(f'Line: {line.strip()}')",
            output: "Line: Monday: R450\nLine: Tuesday: R780\nLine: Wednesday: R620",
            explanation: "Iterating over file object gives one line at a time. strip() removes \\n newline character."
          },
          {
            type: "heading",
            text: "Reading into a List"
          },
          {
            type: "code",
            title: "Example: readlines() Method",
            code: "with open('sales.txt', 'r') as file:\n    lines = file.readlines()\n\nprint(f'Total lines: {len(lines)}')\nprint(f'First line: {lines[0].strip()}')\nprint(f'Last line: {lines[-1].strip()}')",
            output: "Total lines: 3\nFirst line: Monday: R450\nLast line: Wednesday: R620",
            explanation: "readlines() returns list of all lines. Each line includes \\n (use strip() to remove)."
          },
          {
            type: "heading",
            text: "Handling Missing Files"
          },
          {
            type: "code",
            title: "Example: File Not Found",
            code: "try:\n    with open('missing.txt', 'r') as file:\n        content = file.read()\n        print(content)\nexcept FileNotFoundError:\n    print('Error: File not found')\n    print('Please check the filename and try again')",
            output: "Error: File not found\nPlease check the filename and try again",
            explanation: "FileNotFoundError occurs when file doesn't exist. Use try/except to handle gracefully."
          },
          {
            type: "heading",
            text: "Processing File Data"
          },
          {
            type: "code",
            title: "Example: Extracting Data from File",
            code: "# Calculate total from sales.txt\ntotal = 0\n\nwith open('sales.txt', 'r') as file:\n    for line in file:\n        # Extract number from \"Day: R450\" format\n        amount = line.split('R')[1].strip()\n        total += int(amount)\n\nprint(f'Total sales: R{total}')",
            output: "Total sales: R1850",
            explanation: "Read file, process each line, extract and sum the numbers"
          },
          {
            type: "heading",
            text: "File Modes"
          },
          {
            type: "list",
            items: [
              "'r' - Read mode (default). Error if file doesn't exist.",
              "'w' - Write mode. Creates new file or overwrites existing.",
              "'a' - Append mode. Adds to end of existing file.",
              "'r+' - Read and write mode."
            ]
          },
          {
            type: "tip",
            text: "Always use 'with' statement when working with files. It automatically closes the file, even if an error occurs."
          },
          {
            type: "keyPoints",
            points: [
              "with open('file.txt', 'r') as file: opens file for reading",
              "file.read() reads entire file as string",
              "file.readlines() reads file into list of lines",
              "for line in file: iterates line by line",
              "with automatically closes file when done",
              "Use try/except to handle FileNotFoundError",
              "strip() removes \\n newline characters from lines"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does the 'r' in open('file.txt', 'r') mean?",
          options: [
            "Remove mode",
            "Read mode",
            "Rewrite mode",
            "Return mode"
          ],
          correct: 1,
          explanation: "'r' stands for read mode—opens file for reading only."
        },
        {
          question: "What's the advantage of using 'with open()...'?",
          options: [
            "It's faster",
            "It automatically closes the file",
            "It prevents errors",
            "It creates the file if missing"
          ],
          correct: 1,
          explanation: "with automatically closes the file when done, even if errors occur."
        },
        {
          question: "What does file.readlines() return?",
          options: [
            "A string with all content",
            "A list of lines",
            "The number of lines",
            "A dictionary"
          ],
          correct: 1,
          explanation: "readlines() returns a list where each line is an element."
        },
        {
          question: "What happens if you open a file that doesn't exist in 'r' mode?",
          options: [
            "File is created",
            "FileNotFoundError is raised",
            "Returns empty string",
            "Returns None"
          ],
          correct: 1,
          explanation: "Read mode raises FileNotFoundError if file doesn't exist."
        },
        {
          question: "Why use strip() when reading lines?",
          options: [
            "To remove quotes",
            "To remove newline characters (\\n)",
            "To remove numbers",
            "To remove spaces only"
          ],
          correct: 1,
          explanation: "Each line from a file includes \\n (newline). strip() removes it."
        }
      ]
    }
  },

  34: {
    sections: [
      {
        title: "Writing Files: Saving Data Permanently",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Nomsa recording daily totals in her permanent record book, programs need to save information so it survives after the program closes. Writing to files preserves data for later use."
          },
          {
            type: "heading",
            text: "Writing to a New File"
          },
          {
            type: "code",
            title: "Example: Creating and Writing",
            code: "# Write to file (creates if doesn't exist, overwrites if does)\nwith open('output.txt', 'w') as file:\n    file.write('Line 1: Hello\\n')\n    file.write('Line 2: World\\n')\n    file.write('Line 3: Python\\n')\n\nprint('File created successfully')\n\n# Read it back to verify\nwith open('output.txt', 'r') as file:\n    print(file.read())",
            output: "File created successfully\nLine 1: Hello\nLine 2: World\nLine 3: Python",
            explanation: "'w' mode creates file or overwrites existing. write() method adds content. Remember \\n for new lines!"
          },
          {
            type: "warning",
            text: "'w' mode overwrites the entire file! If the file exists, all previous content is deleted. Use 'a' mode to append instead."
          },
          {
            type: "heading",
            text: "Appending to Existing Files"
          },
          {
            type: "code",
            title: "Example: Append Mode",
            code: "# Add to existing file without deleting content\nwith open('output.txt', 'a') as file:\n    file.write('Line 4: Added later\\n')\n\nprint('Content appended')\n\n# Read file to see all content\nwith open('output.txt', 'r') as file:\n    print(file.read())",
            output: "Content appended\nLine 1: Hello\nLine 2: World\nLine 3: Python\nLine 4: Added later",
            explanation: "'a' mode appends to end of file without deleting existing content"
          },
          {
            type: "heading",
            text: "Writing Multiple Lines"
          },
          {
            type: "code",
            title: "Example: writelines()",
            code: "lines = [\n    'Monday: R450\\n',\n    'Tuesday: R780\\n',\n    'Wednesday: R620\\n'\n]\n\nwith open('sales.txt', 'w') as file:\n    file.writelines(lines)\n\nprint('Sales file created')",
            output: "Sales file created",
            explanation: "writelines() writes a list of strings. Remember to include \\n in each string!"
          },
          {
            type: "heading",
            text: "Writing Program Output to File"
          },
          {
            type: "code",
            title: "Example: Logging Results",
            code: "# Calculate and save results\nprices = [100, 250, 50, 175]\ntotal = sum(prices)\naverage = total / len(prices)\n\nwith open('report.txt', 'w') as file:\n    file.write(f'Sales Report\\n')\n    file.write(f'=============\\n')\n    file.write(f'Total: R{total}\\n')\n    file.write(f'Average: R{average:.2f}\\n')\n    file.write(f'Count: {len(prices)}\\n')\n\nprint('Report saved to report.txt')",
            output: "Report saved to report.txt",
            explanation: "Use f-strings to format data before writing to file"
          },
          {
            type: "heading",
            text: "Reading User Input and Saving"
          },
          {
            type: "code",
            title: "Example: Save User Data",
            code: "# Simulated user input\nname = 'Thandi'\nage = 28\ncity = 'Durban'\n\nwith open('user_data.txt', 'w') as file:\n    file.write(f'Name: {name}\\n')\n    file.write(f'Age: {age}\\n')\n    file.write(f'City: {city}\\n')\n\nprint('User data saved')",
            output: "User data saved",
            explanation: "Collect data from user (or calculations) and save to file for later use"
          },
          {
            type: "heading",
            text: "File Modes Summary"
          },
          {
            type: "list",
            items: [
              "'w' - Write mode. Creates new file or OVERWRITES existing file completely",
              "'a' - Append mode. Adds to end of existing file (creates if doesn't exist)",
              "'r' - Read mode. Cannot write. Error if file doesn't exist",
              "'r+' - Read and write. Can both read and modify file"
            ]
          },
          {
            type: "keyPoints",
            points: [
              "with open('file.txt', 'w') as file: opens for writing",
              "'w' mode overwrites file completely—use 'a' to append",
              "file.write(string) writes text to file",
              "file.writelines(list) writes list of strings",
              "Remember to include \\n for new lines",
              "with automatically closes and saves file",
              "Files persist after program ends—use for logging, reports, saving state"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What happens if you open an existing file in 'w' mode?",
          options: [
            "Content is appended",
            "All content is deleted and overwritten",
            "Error occurs",
            "File is renamed"
          ],
          correct: 1,
          explanation: "'w' mode completely overwrites the file—all previous content is lost."
        },
        {
          question: "Which mode adds to the end of a file without deleting content?",
          options: ["'w'", "'r'", "'a'", "'e'"],
          correct: 2,
          explanation: "'a' (append) mode adds to the end without deleting existing content."
        },
        {
          question: "Does file.write() automatically add newlines?",
          options: [
            "Yes, always",
            "No, you must include \\n explicitly",
            "Only at the end of files",
            "Only in 'w' mode"
          ],
          correct: 1,
          explanation: "write() does not add newlines—you must include \\n where needed."
        },
        {
          question: "What does with open('file.txt', 'w') as file: do?",
          options: [
            "Reads the file",
            "Deletes the file",
            "Opens file for writing and auto-closes when done",
            "Copies the file"
          ],
          correct: 2,
          explanation: "with opens file for writing and automatically closes it after the block."
        },
        {
          question: "When does the file actually get saved?",
          options: [
            "After each write() call",
            "When the 'with' block ends (or close() is called)",
            "When the program ends",
            "Immediately when opened"
          ],
          correct: 1,
          explanation: "File is saved when closed (automatically at end of with block, or explicitly with close())."
        }
      ]
    }
  },

  35: {
    sections: [
      {
        title: "Working with CSV Files: Table Data",
        duration: 14,
        content: [
          {
            type: "paragraph",
            text: "Like Thando's hardware store inventory spreadsheet with rows of items and columns for price and stock, many programs work with table-structured data. CSV (Comma-Separated Values) is a simple, universal format for storing this kind of information."
          },
          {
            type: "heading",
            text: "What is CSV?"
          },
          {
            type: "paragraph",
            text: "CSV files store table data as plain text. Each line is a row, and commas separate the columns. Excel, Google Sheets, and most programs can read and write CSV."
          },
          {
            type: "code",
            title: "Example: CSV File Content",
            code: "# This is what a CSV file looks like:\n# inventory.csv:\n# Item,Price,Stock\n# Nails,45,120\n# Hammer,180,8\n# Paint,95,15\n\nprint('CSV = Comma-Separated Values')\nprint('First line often contains column headers')\nprint('Each line after is a row of data')",
            output: "CSV = Comma-Separated Values\nFirst line often contains column headers\nEach line after is a row of data",
            explanation: "CSV is human-readable and works with all spreadsheet programs"
          },
          {
            type: "heading",
            text: "Reading CSV Files"
          },
          {
            type: "code",
            title: "Example: Using csv Module",
            code: "import csv\n\n# Read CSV file\nwith open('inventory.csv', 'r') as file:\n    reader = csv.reader(file)\n    \n    # First row is headers\n    headers = next(reader)\n    print(f'Columns: {headers}')\n    print()\n    \n    # Remaining rows are data\n    for row in reader:\n        item, price, stock = row\n        print(f'{item}: R{price} ({stock} in stock)')",
            output: "Columns: ['Item', 'Price', 'Stock']\n\nNails: R45 (120 in stock)\nHammer: R180 (8 in stock)\nPaint: R95 (15 in stock)",
            explanation: "csv.reader() parses CSV format. Each row is a list of values."
          },
          {
            type: "heading",
            text: "Using DictReader for Better Access"
          },
          {
            type: "code",
            title: "Example: DictReader",
            code: "import csv\n\nwith open('inventory.csv', 'r') as file:\n    reader = csv.DictReader(file)\n    \n    for row in reader:\n        # Access by column name instead of index\n        print(f\"{row['Item']}: R{row['Price']}\")\n        \n        # Check stock level\n        if int(row['Stock']) < 10:\n            print(f\"  ⚠️ Low stock: {row['Stock']}\")",
            output: "Nails: R45\nHammer: R180\n  ⚠️ Low stock: 8\nPaint: R95",
            explanation: "DictReader treats first row as headers. Access columns by name instead of position."
          },
          {
            type: "heading",
            text: "Writing CSV Files"
          },
          {
            type: "code",
            title: "Example: Creating CSV",
            code: "import csv\n\ndata = [\n    ['Customer', 'Purchase', 'Amount'],\n    ['Thandi', 'Bread', 15],\n    ['Sipho', 'Milk', 25],\n    ['Nomsa', 'Eggs', 40]\n]\n\nwith open('sales.csv', 'w', newline='') as file:\n    writer = csv.writer(file)\n    writer.writerows(data)\n\nprint('CSV file created')",
            output: "CSV file created",
            explanation: "csv.writer() creates CSV files. writerows() writes all rows at once."
          },
          {
            type: "heading",
            text: "Writing with DictWriter"
          },
          {
            type: "code",
            title: "Example: DictWriter",
            code: "import csv\n\ndata = [\n    {'Name': 'Thandi', 'Age': 28, 'City': 'Durban'},\n    {'Name': 'Sipho', 'Age': 31, 'City': 'Joburg'},\n    {'Name': 'Nomsa', 'Age': 25, 'City': 'Cape Town'}\n]\n\nwith open('people.csv', 'w', newline='') as file:\n    fieldnames = ['Name', 'Age', 'City']\n    writer = csv.DictWriter(file, fieldnames=fieldnames)\n    \n    writer.writeheader()  # Write column names\n    writer.writerows(data)  # Write all rows\n\nprint('People CSV created')",
            output: "People CSV created",
            explanation: "DictWriter works with dictionaries. writeheader() creates the first row."
          },
          {
            type: "heading",
            text: "Processing CSV Data"
          },
          {
            type: "code",
            title: "Example: Calculate Totals",
            code: "import csv\n\ntotal = 0\ncount = 0\n\nwith open('sales.csv', 'r') as file:\n    reader = csv.DictReader(file)\n    \n    for row in reader:\n        amount = int(row['Amount'])\n        total += amount\n        count += 1\n\nprint(f'Total sales: R{total}')\nprint(f'Average sale: R{total / count:.2f}')",
            output: "Total sales: R80\nAverage sale: R26.67",
            explanation: "Read CSV, process each row, calculate summary statistics"
          },
          {
            type: "tip",
            text: "Use newline='' when opening CSV files for writing. This prevents extra blank lines on Windows."
          },
          {
            type: "keyPoints",
            points: [
              "CSV stores table data as text with commas separating columns",
              "import csv to use Python's CSV module",
              "csv.reader() reads CSV into lists",
              "csv.DictReader() reads CSV into dictionaries (access by column name)",
              "csv.writer() writes data to CSV",
              "csv.DictWriter() writes dictionaries to CSV",
              "Use newline='' when writing to prevent blank lines",
              "CSV works with Excel, Google Sheets, and all data tools"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What does CSV stand for?",
          options: [
            "Computer Separated Values",
            "Comma-Separated Values",
            "Column Standard Values",
            "Create Save Values"
          ],
          correct: 1,
          explanation: "CSV = Comma-Separated Values, a simple format for table data."
        },
        {
          question: "What's the advantage of DictReader over csv.reader()?",
          options: [
            "It's faster",
            "Access columns by name instead of position",
            "It handles larger files",
            "No advantage"
          ],
          correct: 1,
          explanation: "DictReader lets you use row['ColumnName'] instead of row[0], making code clearer."
        },
        {
          question: "What does the first row of a CSV file usually contain?",
          options: [
            "The most important data",
            "Column headers/names",
            "Total count",
            "File version"
          ],
          correct: 1,
          explanation: "First row typically contains column headers (Item, Price, Stock, etc.)."
        },
        {
          question: "Why use newline='' when writing CSV files?",
          options: [
            "Makes file smaller",
            "Required by Python 3",
            "Prevents extra blank lines on Windows",
            "Speeds up writing"
          ],
          correct: 2,
          explanation: "newline='' prevents Python from adding extra blank lines between rows on Windows."
        },
        {
          question: "Can Excel open CSV files?",
          options: [
            "Yes, CSV is a universal format",
            "No, only Python can read CSV",
            "Only with special converter",
            "Only on Mac"
          ],
          correct: 0,
          explanation: "CSV is a universal format—Excel, Google Sheets, and most programs can read it."
        }
      ]
    }
  },

  36: {
    sections: [
      {
        title: "Exception Handling: Graceful Error Management",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Zanele at the till who has a backup plan when a card payment fails, programs need to handle problems gracefully instead of crashing. Exception handling lets your code respond to errors and continue running."
          },
          {
            type: "heading",
            text: "What Are Exceptions?"
          },
          {
            type: "paragraph",
            text: "Exceptions are errors that occur during program execution. Without handling, they crash the program. With handling, you can catch errors and respond appropriately."
          },
          {
            type: "code",
            title: "Example: Unhandled Exception",
            code: "# This crashes the program:\n# number = int('abc')  # ValueError: can't convert 'abc' to int\n\nprint('This code doesn\\'t run because program crashed')",
            output: "",
            explanation: "Without try/except, errors stop the entire program"
          },
          {
            type: "heading",
            text: "Basic try/except Structure"
          },
          {
            type: "code",
            title: "Example: Handling ValueError",
            code: "try:\n    age = int(input('Enter age: '))  # Simulated: user enters 'twenty'\n    age_text = 'twenty'\n    age = int(age_text)\n    print(f'Age: {age}')\nexcept ValueError:\n    print('Error: Please enter a number')\n    age = 0\n\nprint(f'Program continues. Age set to: {age}')",
            output: "Error: Please enter a number\nProgram continues. Age set to: 0",
            explanation: "try block contains code that might fail. except catches specific errors and handles them."
          },
          {
            type: "heading",
            text: "Handling Multiple Exception Types"
          },
          {
            type: "code",
            title: "Example: Different Error Types",
            code: "def divide_numbers(a, b):\n    try:\n        result = a / b\n        return result\n    except ZeroDivisionError:\n        print('Error: Cannot divide by zero')\n        return None\n    except TypeError:\n        print('Error: Both inputs must be numbers')\n        return None\n\nprint(divide_numbers(10, 2))   # Normal\nprint(divide_numbers(10, 0))   # Zero division\nprint(divide_numbers(10, 'a')) # Type error",
            output: "5.0\nError: Cannot divide by zero\nNone\nError: Both inputs must be numbers\nNone",
            explanation: "Multiple except blocks handle different types of errors differently"
          },
          {
            type: "heading",
            text: "Getting Error Details"
          },
          {
            type: "code",
            title: "Example: Accessing Error Message",
            code: "try:\n    result = 10 / 0\nexcept ZeroDivisionError as error:\n    print(f'Error occurred: {error}')\n    print('Using default value instead')\n    result = 0\n\nprint(f'Result: {result}')",
            output: "Error occurred: division by zero\nUsing default value instead\nResult: 0",
            explanation: "as error captures the exception object, letting you access error details"
          },
          {
            type: "heading",
            text: "The else and finally Clauses"
          },
          {
            type: "code",
            title: "Example: Complete try Structure",
            code: "try:\n    number = int('42')\n    result = 100 / number\nexcept ValueError:\n    print('Invalid number format')\nexcept ZeroDivisionError:\n    print('Cannot divide by zero')\nelse:\n    print(f'Success! Result: {result}')\nfinally:\n    print('Cleanup complete (runs no matter what)')",
            output: "Success! Result: 2.380952380952381\nCleanup complete (runs no matter what)",
            explanation: "else runs if no exception occurred. finally ALWAYS runs, even if there's an error."
          },
          {
            type: "heading",
            text: "Practical File Handling Example"
          },
          {
            type: "code",
            title: "Example: Safe File Reading",
            code: "filename = 'data.txt'\n\ntry:\n    with open(filename, 'r') as file:\n        content = file.read()\n        print(content)\nexcept FileNotFoundError:\n    print(f'Error: {filename} not found')\n    print('Creating file with default content...')\n    with open(filename, 'w') as file:\n        file.write('Default content')\nexcept PermissionError:\n    print(f'Error: No permission to read {filename}')",
            output: "Error: data.txt not found\nCreating file with default content...",
            explanation: "Handle file errors gracefully—create missing files, report permission issues"
          },
          {
            type: "warning",
            text: "Don't catch all exceptions with bare except:. Catch specific exceptions so you can handle each type appropriately."
          },
          {
            type: "keyPoints",
            points: [
              "Exceptions are runtime errors that can be caught and handled",
              "try: block contains code that might raise exceptions",
              "except ExceptionType: catches specific exception types",
              "Multiple except blocks handle different errors differently",
              "else: runs if no exception occurred",
              "finally: always runs, even if exception occurred",
              "Use as variable to access error details",
              "Handle specific exceptions, not all exceptions blindly"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What happens if an exception occurs without try/except?",
          options: [
            "Python ignores it",
            "Program crashes",
            "Error is logged but program continues",
            "Python asks what to do"
          ],
          correct: 1,
          explanation: "Without try/except, unhandled exceptions crash the program."
        },
        {
          question: "Which block catches errors?",
          options: ["try", "except", "catch", "error"],
          correct: 1,
          explanation: "except block catches and handles exceptions raised in the try block."
        },
        {
          question: "When does the else block run?",
          options: [
            "Always",
            "When an exception occurs",
            "When NO exception occurs",
            "Only with finally"
          ],
          correct: 2,
          explanation: "else block runs only if try block completes without raising any exception."
        },
        {
          question: "When does finally block run?",
          options: [
            "Only if exception occurs",
            "Only if no exception occurs",
            "Always, no matter what",
            "Only with else block"
          ],
          correct: 2,
          explanation: "finally ALWAYS runs, whether exception occurred or not—perfect for cleanup."
        },
        {
          question: "What's wrong with except: (bare except)?",
          options: [
            "Nothing, it's good practice",
            "It catches all exceptions, making debugging hard",
            "It's slower",
            "It doesn't work in Python 3"
          ],
          correct: 1,
          explanation: "Bare except catches all exceptions, hiding bugs. Catch specific exceptions instead."
        }
      ]
    }
  },

  37: {
    sections: [
      {
        title: "Specific Exceptions: Targeted Error Handling",
        duration: 12,
        content: [
          {
            type: "paragraph",
            text: "Like Mandla's delivery service responding differently to 'customer not home' vs 'wrong address' vs 'road closed', programs should respond differently to different types of errors. Python has specific exception types for common problems."
          },
          {
            type: "heading",
            text: "Common Exception Types"
          },
          {
            type: "list",
            items: [
              "ValueError: Invalid value (e.g., int('abc'))",
              "TypeError: Wrong type (e.g., 'hello' + 5)",
              "ZeroDivisionError: Division by zero",
              "FileNotFoundError: File doesn't exist",
              "IndexError: List index out of range",
              "KeyError: Dictionary key doesn't exist",
              "AttributeError: Object doesn't have attribute",
              "PermissionError: No permission to access file/resource"
            ]
          },
          {
            type: "code",
            title: "Example: Handling Multiple Specific Exceptions",
            code: "def process_order(customer_id, item_index, inventory):\n    try:\n        # Get customer info\n        customer = customers[customer_id]  # Might raise KeyError\n        \n        # Get item\n        item = inventory[item_index]  # Might raise IndexError\n        \n        # Calculate price\n        price = int(item['price'])  # Might raise ValueError\n        \n        print(f'Order processed: {customer} buys {item[\"name\"]} for R{price}')\n        \n    except KeyError:\n        print(f'Error: Customer ID {customer_id} not found')\n    except IndexError:\n        print(f'Error: Item index {item_index} out of range')\n    except ValueError:\n        print('Error: Invalid price format')\n\n# Example data\ncustomers = {1: 'Thandi', 2: 'Sipho'}\ninventory = [\n    {'name': 'Bread', 'price': '15'},\n    {'name': 'Milk', 'price': '25'}\n]\n\nprocess_order(1, 0, inventory)  # Success\nprocess_order(99, 0, inventory)  # KeyError\nprocess_order(1, 10, inventory)  # IndexError",
            output: "Order processed: Thandi buys Bread for R15\nError: Customer ID 99 not found\nError: Item index 10 out of range",
            explanation: "Each exception type gets appropriate handling with clear error messages"
          },
          {
            type: "heading",
            text: "ValueError: Invalid Conversions"
          },
          {
            type: "code",
            title: "Example: Safe Input Conversion",
            code: "def get_age():\n    while True:\n        try:\n            age_input = input('Enter age: ')  # Simulated\n            age_input = 'twenty'  # For this example\n            age = int(age_input)\n            \n            if age < 0 or age > 150:\n                print('Age must be between 0 and 150')\n                return None\n            \n            return age\n            \n        except ValueError:\n            print(f'Error: \"{age_input}\" is not a valid number')\n            return None\n\nresult = get_age()\nif result:\n    print(f'Valid age: {result}')",
            output: 'Error: "twenty" is not a valid number',
            explanation: "ValueError is raised when conversion fails. Loop until valid input (in real usage)."
          },
          {
            type: "heading",
            text: "FileNotFoundError and PermissionError"
          },
          {
            type: "code",
            title: "Example: Robust File Access",
            code: "def read_config(filename):\n    try:\n        with open(filename, 'r') as file:\n            return file.read()\n    except FileNotFoundError:\n        print(f'Config file {filename} not found. Using defaults.')\n        return 'default_config'\n    except PermissionError:\n        print(f'Permission denied: Cannot read {filename}')\n        return None\n    except Exception as e:\n        print(f'Unexpected error: {e}')\n        return None\n\nconfig = read_config('settings.txt')\nprint(f'Config loaded: {config}')",
            output: "Config file settings.txt not found. Using defaults.\nConfig loaded: default_config",
            explanation: "Handle file-specific errors appropriately—missing files can use defaults, permission errors need different response"
          },
          {
            type: "heading",
            text: "IndexError and KeyError"
          },
          {
            type: "code",
            title: "Example: Safe Data Access",
            code: "prices = {'bread': 15, 'milk': 25}\nitems_list = ['Bread', 'Milk', 'Eggs']\n\n# Safe dictionary access\ndef get_price(item):\n    try:\n        return prices[item.lower()]\n    except KeyError:\n        print(f'{item} not in price list')\n        return None\n\n# Safe list access\ndef get_item(index):\n    try:\n        return items_list[index]\n    except IndexError:\n        print(f'Index {index} out of range (max: {len(items_list) - 1})')\n        return None\n\nprint(get_price('Bread'))  # 15\nprint(get_price('Butter'))  # KeyError handled\nprint(get_item(1))  # Milk\nprint(get_item(10))  # IndexError handled",
            output: "15\nButter not in price list\nNone\nMilk\nIndex 10 out of range (max: 2)\nNone",
            explanation: "Catch KeyError for dictionaries, IndexError for lists/sequences"
          },
          {
            type: "tip",
            text: "Order exception handlers from most specific to most general. Python checks them top-to-bottom."
          },
          {
            type: "keyPoints",
            points: [
              "Different exception types for different error conditions",
              "ValueError: conversion/value errors",
              "TypeError: wrong data type used",
              "FileNotFoundError: file doesn't exist",
              "IndexError: sequence index out of bounds",
              "KeyError: dictionary key not found",
              "Handle specific exceptions before general ones",
              "Use Exception as e for unexpected errors"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "Which exception is raised by int('hello')?",
          options: [
            "TypeError",
            "ValueError",
            "ConversionError",
            "StringError"
          ],
          correct: 1,
          explanation: "ValueError is raised when a value can't be converted to the target type."
        },
        {
          question: "Which exception occurs when accessing list[100] in a 5-item list?",
          options: [
            "ValueError",
            "KeyError",
            "IndexError",
            "RangeError"
          ],
          correct: 2,
          explanation: "IndexError is raised when trying to access an index that doesn't exist."
        },
        {
          question: "Which exception is raised when accessing missing dictionary key?",
          options: [
            "IndexError",
            "KeyError",
            "ValueError",
            "AttributeError"
          ],
          correct: 1,
          explanation: "KeyError is raised when accessing a key that doesn't exist in the dictionary."
        },
        {
          question: "What's raised when opening a file you don't have permission to read?",
          options: [
            "FileNotFoundError",
            "PermissionError",
            "AccessError",
            "IOError"
          ],
          correct: 1,
          explanation: "PermissionError is raised for permission-related access problems."
        },
        {
          question: "Why handle specific exceptions rather than catching all?",
          options: [
            "It's faster",
            "Allows appropriate response to each error type",
            "Required by Python",
            "Catches more errors"
          ],
          correct: 1,
          explanation: "Specific exception handling lets you respond appropriately to each type of error."
        }
      ]
    }
  },

  38: {
    sections: [
      {
        title: "Debugging Strategies: Finding and Fixing Errors",
        duration: 14,
        content: [
          {
            type: "paragraph",
            text: "Like Nonhlanhla tracing her till discrepancy backwards through receipts and transactions, debugging is the systematic process of finding where things went wrong in your code. It's a skill every programmer must master."
          },
          {
            type: "heading",
            text: "Types of Errors"
          },
          {
            type: "list",
            items: [
              "Syntax errors: Code breaks Python's grammar rules (missing colon, unmatched parentheses)",
              "Runtime errors: Code runs but crashes with exception (divide by zero, file not found)",
              "Logic errors: Code runs without crashing but produces wrong results (most difficult to find)"
            ]
          },
          {
            type: "heading",
            text: "Reading Error Messages"
          },
          {
            type: "code",
            title: "Example: Understanding Tracebacks",
            code: "def calculate_average(numbers):\n    total = sum(numbers)\n    return total / len(numbers)\n\ndef main():\n    scores = [85, 90, 78, 92]\n    average = calculate_average(scores)\n    print(f'Average: {average}')\n    \n    # This will cause error:\n    empty = []\n    avg2 = calculate_average(empty)\n\nmain()",
            output: "Average: 86.25\n[Traceback would show]:\nZeroDivisionError: division by zero\n  File \"code.py\", line 3, in calculate_average\n    return total / len(numbers)\n  File \"code.py\", line 12, in main\n    avg2 = calculate_average(empty)",
            explanation: "Traceback shows: 1) Error type, 2) Where error occurred, 3) Call stack (how we got there)"
          },
          {
            type: "heading",
            text: "Debugging Strategy 1: Print Statements"
          },
          {
            type: "paragraph",
            text: "The simplest and most common debugging technique: add print() to see what's happening."
          },
          {
            type: "code",
            title: "Example: Using Print for Debugging",
            code: "def calculate_discount(price, discount_percent):\n    print(f'DEBUG: price={price}, discount={discount_percent}')\n    \n    discount_amount = price * discount_percent\n    print(f'DEBUG: discount_amount={discount_amount}')\n    \n    final_price = price - discount_amount\n    print(f'DEBUG: final_price={final_price}')\n    \n    return final_price\n\nresult = calculate_discount(100, 0.2)\nprint(f'\\nFinal: R{result}')",
            output: "DEBUG: price=100, discount=0.2\nDEBUG: discount_amount=20.0\nDEBUG: final_price=80.0\n\nFinal: R80.0",
            explanation: "Print intermediate values to trace execution and find where logic goes wrong"
          },
          {
            type: "heading",
            text: "Debugging Strategy 2: Divide and Conquer"
          },
          {
            type: "paragraph",
            text: "When code is long, narrow down the problem by testing sections individually."
          },
          {
            type: "code",
            title: "Example: Testing Parts Separately",
            code: "# Instead of testing the whole pipeline at once:\n# result = process(fetch(parse(input_data)))\n\n# Test each step:\ndata = parse(input_data)\nprint(f'After parse: {data}')\n\nfetched = fetch(data)\nprint(f'After fetch: {fetched}')\n\nprocessed = process(fetched)\nprint(f'After process: {processed}')\n\n# Now you know exactly which function causes the problem",
            output: "",
            explanation: "Break complex operations into steps. Test each step's output before proceeding."
          },
          {
            type: "heading",
            text: "Debugging Strategy 3: Rubber Duck Method"
          },
          {
            type: "paragraph",
            text: "Explain your code line-by-line to someone (or something—even a rubber duck). Often, explaining reveals the error."
          },
          {
            type: "heading",
            text: "Debugging Strategy 4: Check Assumptions"
          },
          {
            type: "code",
            title: "Example: Verify Assumptions",
            code: "def process_data(data):\n    # Assumption: data is a list\n    print(f'Type of data: {type(data)}')\n    print(f'Length: {len(data)}')\n    \n    # Assumption: all items are numbers\n    for i, item in enumerate(data):\n        print(f'Item {i}: {item} (type: {type(item)})')\n    \n    # Now process...\n    total = sum(data)\n    return total\n\ntest_data = ['10', '20', '30']  # Oops, strings not numbers!\nresult = process_data(test_data)",
            output: "Type of data: <class 'list'>\nLength: 3\nItem 0: 10 (type: <class 'str'>)\nItem 1: 20 (type: <class 'str'>)\nItem 2: 30 (type: <class 'str'>)\n[TypeError would occur at sum()]",
            explanation: "Verify data types and values match your assumptions"
          },
          {
            type: "heading",
            text: "Debugging Strategy 5: Binary Search"
          },
          {
            type: "paragraph",
            text: "Comment out half the code. If error disappears, problem is in that half. If not, it's in the other half. Repeat until found."
          },
          {
            type: "heading",
            text: "Common Debugging Mistakes"
          },
          {
            type: "list",
            items: [
              "Assuming without verifying—always check your assumptions",
              "Changing too many things at once—change one thing, test, repeat",
              "Not reading error messages carefully—they tell you exactly what's wrong",
              "Giving up too quickly—debugging is normal and expected",
              "Not taking breaks—fresh eyes spot errors faster"
            ]
          },
          {
            type: "tip",
            text: "When stuck: 1) Read error message carefully, 2) Add print statements, 3) Check assumptions, 4) Test small pieces, 5) Take a break."
          },
          {
            type: "keyPoints",
            points: [
              "Three error types: syntax, runtime, logic",
              "Read tracebacks carefully—they show exactly where error occurred",
              "Use print() to trace execution and values",
              "Test code in small pieces (divide and conquer)",
              "Verify assumptions about data types and values",
              "Change one thing at a time when testing fixes",
              "Take breaks—fresh eyes find bugs faster",
              "Debugging is a normal part of programming"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "Which error type produces wrong results but doesn't crash?",
          options: [
            "Syntax error",
            "Runtime error",
            "Logic error",
            "System error"
          ],
          correct: 2,
          explanation: "Logic errors produce incorrect results without crashing—the hardest type to find."
        },
        {
          question: "What's the simplest and most common debugging technique?",
          options: [
            "Using a debugger",
            "Adding print() statements",
            "Rewriting code",
            "Asking someone else"
          ],
          correct: 1,
          explanation: "Print statements are the simplest way to see what's happening in your code."
        },
        {
          question: "What does a traceback show?",
          options: [
            "All variables in the program",
            "Error type and where it occurred",
            "How to fix the error",
            "Program execution time"
          ],
          correct: 1,
          explanation: "Traceback shows the exception type, error location, and call stack."
        },
        {
          question: "What's the 'divide and conquer' debugging strategy?",
          options: [
            "Test code in small sections to isolate the problem",
            "Split the file into multiple files",
            "Divide numbers before comparing",
            "Test with half the data"
          ],
          correct: 0,
          explanation: "Test code piece by piece to narrow down where the problem occurs."
        },
        {
          question: "When debugging, should you change multiple things at once?",
          options: [
            "Yes, saves time",
            "No, change one thing at a time to know what fixed it",
            "Only for small programs",
            "Doesn't matter"
          ],
          correct: 1,
          explanation: "Change one thing at a time so you know which change fixed (or caused) the problem."
        }
      ]
    }
  },

  39: {
    sections: [
      {
        title: "Final Project Part 1: Planning and Structure",
        duration: 15,
        content: [
          {
            type: "paragraph",
            text: "Like Thembi planning her catering business system, building real software requires careful planning before writing code. This lesson guides you through breaking down a complete project into manageable pieces."
          },
          {
            type: "heading",
            text: "The Project: Simple Inventory System"
          },
          {
            type: "paragraph",
            text: "You'll build a command-line inventory manager that can: 1) Add new items, 2) Update quantities, 3) Search for items, 4) Display all items, 5) Save to file, 6) Load from file."
          },
          {
            type: "heading",
            text: "Step 1: Break Down Requirements"
          },
          {
            type: "list",
            items: [
              "Data structure: How do we store items? (Dictionary with item names as keys)",
              "Add item: Function to add item with name, quantity, price",
              "Update: Function to change quantity (stock in/out)",
              "Search: Function to find and display item details",
              "Display all: Function to show entire inventory",
              "Save: Write inventory to CSV file",
              "Load: Read inventory from CSV file",
              "Menu: User interface to access all functions"
            ]
          },
          {
            type: "heading",
            text: "Step 2: Design Data Structures"
          },
          {
            type: "code",
            title: "Example: Planning Data Structure",
            code: "# Inventory will be a dictionary\n# Key: item name (string)\n# Value: dict with 'quantity' and 'price'\n\ninventory = {\n    'Bread': {'quantity': 50, 'price': 15},\n    'Milk': {'quantity': 30, 'price': 25},\n    'Eggs': {'quantity': 20, 'price': 40}\n}\n\n# Demo access\nprint(inventory['Bread']['quantity'])  # 50\nprint(inventory['Milk']['price'])      # 25",
            output: "50\n25",
            explanation: "Choose data structures that make sense for your operations"
          },
          {
            type: "heading",
            text: "Step 3: Write Function Signatures First"
          },
          {
            type: "paragraph",
            text: "Before implementing, define what each function does, its parameters, and return value."
          },
          {
            type: "code",
            title: "Example: Function Signatures",
            code: "# Start with function outlines\n\ndef add_item(inventory, name, quantity, price):\n    \"\"\"Add a new item to inventory\"\"\"\n    pass\n\ndef update_quantity(inventory, name, change):\n    \"\"\"Update item quantity (positive to add, negative to remove)\"\"\"\n    pass\n\ndef search_item(inventory, name):\n    \"\"\"Find and display item details\"\"\"\n    pass\n\ndef display_all(inventory):\n    \"\"\"Show all items in inventory\"\"\"\n    pass\n\ndef save_to_file(inventory, filename):\n    \"\"\"Save inventory to CSV file\"\"\"\n    pass\n\ndef load_from_file(filename):\n    \"\"\"Load inventory from CSV file, return inventory dict\"\"\"\n    pass\n\nprint('Functions planned')",
            output: "Functions planned",
            explanation: "Plan function interfaces before implementation. pass is placeholder."
          },
          {
            type: "heading",
            text: "Step 4: Implement One Function at a Time"
          },
          {
            type: "code",
            title: "Example: Implementing add_item()",
            code: "def add_item(inventory, name, quantity, price):\n    \"\"\"Add a new item to inventory\"\"\"\n    if name in inventory:\n        print(f'{name} already exists. Use update to change quantity.')\n        return False\n    \n    inventory[name] = {'quantity': quantity, 'price': price}\n    print(f'Added: {name} (Qty: {quantity}, Price: R{price})')\n    return True\n\n# Test it\ninventory = {}\nadd_item(inventory, 'Bread', 50, 15)\nadd_item(inventory, 'Milk', 30, 25)\nprint(f'\\nInventory: {inventory}')",
            output: "Added: Bread (Qty: 50, Price: R15)\nAdded: Milk (Qty: 30, Price: R25)\n\nInventory: {'Bread': {'quantity': 50, 'price': 15}, 'Milk': {'quantity': 30, 'price': 25}}",
            explanation: "Implement and test each function before moving to the next"
          },
          {
            type: "heading",
            text: "Step 5: Test Each Function"
          },
          {
            type: "paragraph",
            text: "Write simple tests to verify each function works correctly before integrating."
          },
          {
            type: "code",
            title: "Example: Testing Functions",
            code: "# Test add_item\ninventory = {}\nadd_item(inventory, 'Bread', 50, 15)\nassert 'Bread' in inventory\nassert inventory['Bread']['quantity'] == 50\nprint('✓ add_item works')\n\n# Test duplicate add\nadd_item(inventory, 'Bread', 100, 20)  # Should reject\nassert inventory['Bread']['quantity'] == 50  # Unchanged\nprint('✓ Duplicate rejection works')",
            output: "Added: Bread (Qty: 50, Price: R15)\n✓ add_item works\nBread already exists. Use update to change quantity.\n✓ Duplicate rejection works",
            explanation: "Test edge cases and normal cases before moving on"
          },
          {
            type: "heading",
            text: "Planning Checklist"
          },
          {
            type: "list",
            items: [
              "✓ Requirements clear and complete?",
              "✓ Data structures chosen and justified?",
              "✓ Functions identified with clear responsibilities?",
              "✓ Function signatures defined (parameters, returns)?",
              "✓ Test plan for each function?",
              "✓ Error handling planned?",
              "✓ User interface designed?"
            ]
          },
          {
            type: "keyPoints",
            points: [
              "Plan before coding—saves time in the long run",
              "Break projects into small, manageable functions",
              "Choose appropriate data structures first",
              "Write function signatures before implementation",
              "Implement and test one function at a time",
              "Test edge cases, not just happy path",
              "Good structure makes integration easier",
              "Planning is not wasted time—it's essential"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What should you do before writing code for a project?",
          options: [
            "Start coding immediately",
            "Plan structure, data, and functions",
            "Write tests",
            "Ask for help"
          ],
          correct: 1,
          explanation: "Planning structure, data, and functions before coding saves time and prevents errors."
        },
        {
          question: "What's the benefit of writing function signatures first?",
          options: [
            "Makes code faster",
            "Defines interface before worrying about implementation",
            "Required by Python",
            "Makes code shorter"
          ],
          correct: 1,
          explanation: "Function signatures define what functions do and how they're called, independent of implementation."
        },
        {
          question: "Should you implement all functions before testing any?",
          options: [
            "Yes, test once at the end",
            "No, implement and test one function at a time",
            "Only for large projects",
            "Doesn't matter"
          ],
          correct: 1,
          explanation: "Implement and test incrementally—find bugs early when they're easier to fix."
        },
        {
          question: "What's a good approach for breaking down a project?",
          options: [
            "Write everything in one big function",
            "Identify distinct operations and make each a function",
            "Copy similar code from internet",
            "Start with the hardest part"
          ],
          correct: 1,
          explanation: "Break project into logical functions, each with one clear responsibility."
        },
        {
          question: "What does 'pass' do in a function?",
          options: [
            "Skips the function",
            "Serves as a placeholder for unimplemented code",
            "Returns True",
            "Causes an error"
          ],
          correct: 1,
          explanation: "pass is a placeholder that does nothing—useful when planning function structure."
        }
      ]
    }
  },

  40: {
    sections: [
      {
        title: "Final Project Part 2: Integration and Testing",
        duration: 18,
        content: [
          {
            type: "paragraph",
            text: "Like Thembi testing her complete catering system with a full day's workflow, now we integrate all functions into a working program and verify the entire system functions correctly."
          },
          {
            type: "heading",
            text: "Integrating Functions: The Main Menu"
          },
          {
            type: "code",
            title: "Example: Complete Integration",
            code: "import csv\n\ndef display_menu():\n    print('\\n=== Inventory System ===')\n    print('1. Add item')\n    print('2. Update quantity')\n    print('3. Search item')\n    print('4. Display all')\n    print('5. Save to file')\n    print('6. Load from file')\n    print('7. Exit')\n    return input('Choose option: ')\n\ndef main():\n    inventory = {}  # Start empty\n    filename = 'inventory.csv'\n    \n    # Try to load existing data\n    try:\n        inventory = load_from_file(filename)\n        print(f'Loaded {len(inventory)} items')\n    except FileNotFoundError:\n        print('Starting with empty inventory')\n    \n    # Main loop\n    while True:\n        choice = display_menu()\n        \n        if choice == '1':\n            # Add item code here...\n            pass\n        elif choice == '7':\n            print('Goodbye!')\n            break\n\nprint('Integration example')",
            output: "Integration example",
            explanation: "Main function coordinates all components. Menu loop lets user access all features."
          },
          {
            type: "heading",
            text: "Testing the Complete System"
          },
          {
            type: "paragraph",
            text: "Test the full workflow like a real user would: 1) Add items, 2) Update quantities, 3) Search, 4) Save, 5) Close program, 6) Reopen and verify data persisted."
          },
          {
            type: "code",
            title: "Example: Implementing save_to_file()",
            code: "import csv\n\ndef save_to_file(inventory, filename):\n    \"\"\"Save inventory to CSV file\"\"\"\n    try:\n        with open(filename, 'w', newline='') as file:\n            writer = csv.writer(file)\n            # Write header\n            writer.writerow(['Item', 'Quantity', 'Price'])\n            # Write data\n            for name, data in inventory.items():\n                writer.writerow([name, data['quantity'], data['price']])\n        print(f'Saved {len(inventory)} items to {filename}')\n        return True\n    except Exception as e:\n        print(f'Error saving: {e}')\n        return False\n\n# Test\ninventory = {\n    'Bread': {'quantity': 50, 'price': 15},\n    'Milk': {'quantity': 30, 'price': 25}\n}\nsave_to_file(inventory, 'test_inventory.csv')",
            output: "Saved 2 items to test_inventory.csv",
            explanation: "Save function writes structured data to CSV for persistence"
          },
          {
            type: "code",
            title: "Example: Implementing load_from_file()",
            code: "import csv\n\ndef load_from_file(filename):\n    \"\"\"Load inventory from CSV file\"\"\"\n    inventory = {}\n    \n    with open(filename, 'r') as file:\n        reader = csv.DictReader(file)\n        for row in reader:\n            name = row['Item']\n            inventory[name] = {\n                'quantity': int(row['Quantity']),\n                'price': float(row['Price'])\n            }\n    \n    print(f'Loaded {len(inventory)} items from {filename}')\n    return inventory\n\n# Test\nloaded = load_from_file('test_inventory.csv')\nprint(f'Loaded data: {loaded}')",
            output: "Loaded 2 items from test_inventory.csv\nLoaded data: {'Bread': {'quantity': 50, 'price': 15.0}, 'Milk': {'quantity': 30, 'price': 25.0}}",
            explanation: "Load function reads CSV and reconstructs inventory dictionary"
          },
          {
            type: "heading",
            text: "Testing Strategy"
          },
          {
            type: "list",
            items: [
              "Normal operations: Add, update, search, display—verify expected behavior",
              "Edge cases: Empty inventory, duplicate names, negative quantities",
              "File operations: Save, close program, reopen, verify data persisted",
              "Error handling: Invalid input, missing files, corrupted data",
              "Integration: Test complete workflows, not just individual functions",
              "User experience: Is menu clear? Are error messages helpful?"
            ]
          },
          {
            type: "code",
            title: "Example: Complete display_all() Function",
            code: "def display_all(inventory):\n    \"\"\"Display all items in inventory\"\"\"\n    if not inventory:\n        print('\\nInventory is empty')\n        return\n    \n    print('\\n=== Current Inventory ===')\n    print(f'{\"Item\":<15} {\"Quantity\":>10} {\"Price\":>10}')\n    print('-' * 38)\n    \n    total_value = 0\n    for name, data in sorted(inventory.items()):\n        qty = data['quantity']\n        price = data['price']\n        value = qty * price\n        total_value += value\n        print(f'{name:<15} {qty:>10} R{price:>9.2f}')\n    \n    print('-' * 38)\n    print(f'Total inventory value: R{total_value:.2f}\\n')\n\n# Test\ninventory = {\n    'Bread': {'quantity': 50, 'price': 15},\n    'Milk': {'quantity': 30, 'price': 25},\n    'Eggs': {'quantity': 20, 'price': 40}\n}\ndisplay_all(inventory)",
            output: "\n=== Current Inventory ===\nItem               Quantity      Price\n--------------------------------------\nBread                    50     R15.00\nEggs                     20     R40.00\nMilk                     30     R25.00\n--------------------------------------\nTotal inventory value: R1800.00",
            explanation: "Display function formats output professionally with totals"
          },
          {
            type: "heading",
            text: "Final Integration Checklist"
          },
          {
            type: "list",
            items: [
              "✓ All functions implemented and tested individually?",
              "✓ Functions integrated into main menu loop?",
              "✓ Error handling added for user input?",
              "✓ File save/load works correctly?",
              "✓ Program handles empty inventory gracefully?",
              "✓ User messages clear and helpful?",
              "✓ Complete workflow tested (add → update → save → reload)?",
              "✓ Code organized and readable?",
              "✓ Edge cases tested?"
            ]
          },
          {
            type: "heading",
            text: "What Makes a Complete Project"
          },
          {
            type: "paragraph",
            text: "A complete project isn't just code that works—it's code that: handles errors gracefully, provides clear user feedback, persists data reliably, is tested thoroughly, is maintainable and readable. These skills separate hobby projects from professional software."
          },
          {
            type: "keyPoints",
            points: [
              "Integration combines individual functions into complete system",
              "Main loop provides user interface to all features",
              "Test complete workflows, not just individual functions",
              "Save/load ensures data persists between sessions",
              "Error handling makes programs robust and user-friendly",
              "Clear messages guide users through operations",
              "Testing finds bugs before users do",
              "Complete projects handle normal cases AND edge cases"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What's the purpose of the main menu loop?",
          options: [
            "Make code longer",
            "Provide user interface to access all features",
            "Required by Python",
            "Make program faster"
          ],
          correct: 1,
          explanation: "Main loop provides organized interface for users to access all program features."
        },
        {
          question: "Why test complete workflows, not just individual functions?",
          options: [
            "It's easier",
            "Functions might work alone but fail when integrated",
            "It's faster",
            "Required for grading"
          ],
          correct: 1,
          explanation: "Functions may work individually but have integration issues—test the complete system."
        },
        {
          question: "What should happen when loading a file that doesn't exist?",
          options: [
            "Program crashes",
            "Catch exception, start with empty data or defaults",
            "Create random data",
            "Exit immediately"
          ],
          correct: 1,
          explanation: "Handle FileNotFoundError gracefully—start with empty state or defaults."
        },
        {
          question: "What's an edge case?",
          options: [
            "The main functionality",
            "Unusual or extreme inputs that might cause problems",
            "The last function",
            "Error messages"
          ],
          correct: 1,
          explanation: "Edge cases are unusual inputs (empty data, negative numbers, etc.) that might break code."
        },
        {
          question: "What makes a project 'complete'?",
          options: [
            "Just working code",
            "Code that works, handles errors, persists data, and is tested",
            "Longest possible code",
            "Using every Python feature"
          ],
          correct: 1,
          explanation: "Complete projects work correctly, handle errors gracefully, persist data, and are thoroughly tested."
        }
      ]
    }
  },
  41: {
    duration: "10 min",
    sections: [
      {
        title: "Understanding AI Outputs: Why Machines Sound Confident When They're Wrong",
        duration: 10,
        content: [
          {
            type: "paragraph",
            text: "This is the first non-Python module in Golden Learn. You won't write code here. You'll build AI Literacy—the capacity to use AI tools responsibly and critically."
          },
          {
            type: "heading",
            text: "What AI Systems Actually Do"
          },
          {
            type: "paragraph",
            text: "AI language models (like ChatGPT, Claude, Gemini) generate text by predicting what words are likely to come next, based on patterns they learned from millions of texts. They don't know facts. They don't verify information. They don't say 'I don't know.' They predict probable words."
          },
          {
            type: "paragraph",
            text: "You've already learned how computers work: they follow instructions exactly, without interpretation. They compare values, make logical decisions, and execute code precisely. If you tell a computer 2 + 2, it will always give 4."
          },
          {
            type: "paragraph",
            text: "AI is different. If you ask an AI 'What's the population of Johannesburg?', it will generate a number that sounds plausible based on patterns it's seen before. That number might be correct. It might be completely wrong. The AI doesn't check. It just predicts what fits the pattern."
          },
          {
            type: "note",
            text: "AI systems optimize for sounding right, not being right."
          },
          {
            type: "heading",
            text: "Why This Matters in Your Life"
          },
          {
            type: "paragraph",
            text: "Students submit AI-generated essays with fake citations. Job applicants submit AI-written applications claiming skills they don't have. People follow AI medical advice without verifying it. The AI isn't lying—it's doing exactly what it was trained to do. But the consequences are real."
          },
          {
            type: "warning",
            text: "If you submit AI-generated work without verifying it, you are responsible for the errors. If you follow AI advice without confirming it, you bear the risk."
          },
          {
            type: "paragraph",
            text: "Confidence does not equal correctness. Details do not equal truth. An AI can generate precise-sounding numbers, professional-sounding instructions, and authoritative-sounding explanations—all completely wrong."
          },
          {
            type: "heading",
            text: "Human Responsibility"
          },
          {
            type: "paragraph",
            text: "When you use AI tools, you are responsible for: verifying factual claims independently, checking that instructions are safe and correct, understanding the AI is predicting patterns, not stating facts, evaluating consequences if the output is wrong."
          },
          {
            type: "paragraph",
            text: "AI is a tool, not an authority. You wouldn't trust a calculator that sometimes gave wrong answers. You shouldn't trust an AI that generates plausible-sounding text without verification."
          },
          {
            type: "heading",
            text: "Questions to Always Ask"
          },
          {
            type: "paragraph",
            text: "When you receive AI-generated content, ask yourself:"
          },
          {
            type: "list",
            items: [
              "Is this factually correct, or just well-phrased?",
              "Can I verify this information independently?",
              "What happens if this is wrong?",
              "Am I using this to learn, or to avoid learning?",
              "Would I be comfortable saying 'I wrote this' or 'I know this'?"
            ]
          },
          {
            type: "heading",
            text: "Why You're Ready for This"
          },
          {
            type: "paragraph",
            text: "You've completed 40 levels of Python. You understand how computers follow instructions exactly. You know how conditionals work (if this, then that). You know how logic works (and, or, not). You know how programs compare values and make decisions based on reality, not probability."
          },
          {
            type: "paragraph",
            text: "AI systems make patterns look like thinking. But you know the difference now. Computers execute logic. AI predicts text. One follows rules. The other mimics patterns. They are not the same."
          },
          {
            type: "keyPoints",
            points: [
              "AI predicts words based on patterns, it doesn't know facts",
              "Confidence in tone does not mean correctness in content",
              "You are responsible for verifying AI outputs before using them",
              "AI is a tool for assistance, not an authority for truth",
              "Critical thinking and verification remain human responsibilities",
              "Understanding how AI works protects you from misuse",
              "The patterns AI learned may contain biases and errors",
              "When AI fails, the person who used it bears the consequence"
            ]
          }
        ]
      }
    ],
    quiz: {
      questions: [
        {
          question: "What do AI language models actually do?",
          options: [
            "Know facts and verify information",
            "Predict likely words based on patterns",
            "Search the internet for answers",
            "Think like humans"
          ],
          correct: 1,
          explanation: "AI models predict probable text based on patterns learned from training data—they don't know, verify, or search."
        },
        {
          question: "If an AI generates a precise-sounding number, what should you do?",
          options: [
            "Trust it because it sounds confident",
            "Verify it independently before using it",
            "Assume it's probably right",
            "Use it without checking"
          ],
          correct: 1,
          explanation: "Confidence and precision in AI outputs do not guarantee correctness—always verify factual claims."
        },
        {
          question: "Who is responsible if AI-generated work contains errors?",
          options: [
            "The AI company",
            "The person who submitted it without verifying",
            "No one, it's a machine error",
            "The teacher or employer"
          ],
          correct: 1,
          explanation: "You are responsible for verifying AI outputs before submitting or relying on them."
        },
        {
          question: "What's the key difference between how computers and AI work?",
          options: [
            "Computers are slower",
            "Computers follow exact instructions; AI predicts patterns",
            "AI is always more accurate",
            "Computers can't handle text"
          ],
          correct: 1,
          explanation: "Computers execute logic precisely; AI predicts probable text based on patterns—fundamentally different processes."
        },
        {
          question: "What makes someone AI literate?",
          options: [
            "Using AI for everything",
            "Understanding AI's limitations and verifying its outputs",
            "Never using AI",
            "Trusting AI completely"
          ],
          correct: 1,
          explanation: "AI literacy means understanding how AI works, its limitations, and using it responsibly with verification."
        }
      ]
    }
  },
};
export function addLearningContentToLevels(levels) {
  return levels.map(level => ({
    ...level,
    learningContent: learningContent[level.id] || {
      sections: [{
        title: "Coming Soon",
        duration: 2,
        content: [{
          type: "paragraph",
          text: "Learning content for this level is being prepared."
        }]
      }]
    }
  }));
}
